import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { es } from 'date-fns/locale';
import { format, parseISO } from 'date-fns';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import KeyboardRoundedIcon from '@mui/icons-material/KeyboardRounded';
import TouchAppRoundedIcon from '@mui/icons-material/TouchAppRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  createIndustrialCostProductionReport,
  createIndustrialCostProductionReportIdlePeriod,
  createIndustrialCostProductionReportLine,
  createIndustrialCostProductionReportStop,
  deleteIndustrialCostProductionReportIdlePeriod,
  deleteIndustrialCostProductionReportLine,
  deleteIndustrialCostProductionReportStop,
  getIndustrialCostProductionOperationSettingsConfig,
  getIndustrialCostProductionReportDetail,
  getIndustrialCostProductionReportOperatorContext,
  getIndustrialCostProducts,
  listIndustrialCostProductionReports,
  searchIndustrialCostExistingProductionOrders,
  listIndustrialCostProductionLinesConfig,
  listIndustrialCostProductionMaterialLots,
  reopenIndustrialCostProductionReport,
  resolveIndustrialCostExistingProductionOrder,
  updateIndustrialCostProductionReportOperatorNumber,
  updateIndustrialCostProductionReportIdlePeriod,
  updateIndustrialCostProductionReportLine,
  updateIndustrialCostProductionReportStop,
  upsertIndustrialCostProductionReportQualityResult,
  validateIndustrialCostProductionReport,
  getIndustrialCostLineDefaultsSuggestion,
  acknowledgeIndustrialCostPlantNotice,
  listIndustrialCostPlantNotices,
} from '../services/industrialCostService';

import ProductionOperatorNumericKeyboard from '../components/ProductionOperatorNumericKeyboard';
import ProductionOperatorInlineNumericKeyboard from '../components/ProductionOperatorInlineNumericKeyboard';
import ProductionOperatorInlineTextKeyboard from '../components/ProductionOperatorInlineTextKeyboard';
import { getApplicableProductionReportQualitySlots } from '../utils/productionReportQualitySlots';


import type {
  CreateProductionReportIdlePeriodPayload,
  CreateProductionReportLinePayload,
  CreateProductionReportPayload,
  CreateProductionReportStopPayload,
  ExistingProductionOrderCandidate,
  ExistingProductionOrderLookup,
  PlantNotice,
  Product,
  ProductionLineConfig,
  ProductionMaterialLot,
  ProductionOperationSettings,
  ProductionReport,
  ProductionReportDetail,
  ProductionReportIdlePeriod,
  ProductionReportLine,
  ProductionReportLineMaterialUsageDraft,
  ProductionReportLineMaterialUsagePayload,
  ProductionReportQualityResult,
  ProductionReportQualityResultRow,
  ProductionReportShiftTimeSlot,
  ProductionReportStop,
  ProductionReportStopReason,
  UpdateProductionReportIdlePeriodPayload,
} from '../types/industrialCost';

type ScreenMode = 'list' | 'detail';

const OPERATOR_SELECTED_REPORT_SESSION_KEY =
  'proa-industrial-operator-selected-report-id';

type IndustrialCostProductionReportsOperatorViewProps = {
  permissions?: string[];
};


type NewReportForm = {
  template_id: string;
  report_date: string;
  operator_number: string;
  shift: string;
  production_line_config_id: string;
  line_or_center: string;
  notes: string;
};

type LineForm = {
  product_id: string;
  free_product_ref: string;
  customer_number: string;
  customer_order_number: string;
  manufacturing_order_number: string;
  quantity_value: string;
  actual_quantity_value: string;
  quantity_unit: string;
  speed_value: string;
  start_time: string;
  production_start_time: string;
  end_time: string;
  material_usages: ProductionReportLineMaterialUsageDraft[];
  notes: string;
  extra_fields: Record<string, string>;
};

type OperatorProductionReportFormLayoutSectionKey =
  | 'identity'
  | 'production'
  | 'additional'
  | 'closing';

type ProductionEstimationResult = {
  enabled: boolean;
  estimatedDurationMinutes: number | null;
  estimatedProductionDurationMinutes: number | null;
  estimatedEndTime: string;
  formulaLabel: string;
  missingInputs: string[];
  usesSetupTime: boolean;
};

type LineContinuationStatus = 'none' | 'pending_continuation' | 'continued' | 'finished';

type StopForm = {
  line_id: string;
  start_time: string;
  end_time: string;
  reason_id: string;
  notes: string;
};

type IdlePeriodForm = {
  start_time: string;
  end_time: string;
  notes: string;
};

type TimelineValidationEventSummary = {
  event_type: 'line' | 'stop' | 'idle';
  event_id: string;
  label: string;
  start_time: string | null;
  end_time: string | null;
};

type TimelineValidationGap = {
  start_time: string;
  end_time: string;
  position: 'start_of_shift' | 'between_events' | 'end_of_shift';
  previous_event: TimelineValidationEventSummary | null;
  next_event: TimelineValidationEventSummary | null;
  allowed_actions: string[];
};

type TimelineValidationOverlap = {
  start_time: string;
  end_time: string;
  previous_event: TimelineValidationEventSummary | null;
  next_event: TimelineValidationEventSummary | null;
  allowed_actions: string[];
};

type TimelineValidationIssue = {
  code: 'timeline_validation_failed';
  message: string;
  shift_window: {
    start_time: string;
    end_time: string;
  };
  incomplete_events: TimelineValidationEventSummary[];
  gaps: TimelineValidationGap[];
  overlaps: TimelineValidationOverlap[];
};

const SHIFT_OPTIONS = [
  { value: 'morning', label: 'Mañana' },
  { value: 'afternoon', label: 'Tarde' },
  { value: 'night', label: 'Noche' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'draft', label: 'Borrador' },
  { value: 'validated', label: 'Validado' },
  { value: 'exported', label: 'Exportado' },
];

function getPreviousShiftContext(
  reportDate: string | null | undefined,
  shift: string | null | undefined,
) {
  if (!reportDate || !shift) return null;

  if (shift === 'afternoon') {
    return {
      report_date: reportDate,
      shift: 'morning',
    };
  }

  if (shift === 'night') {
    return {
      report_date: reportDate,
      shift: 'afternoon',
    };
  }

  if (shift === 'morning') {
    const [year, month, day] = reportDate.split('-').map(Number);

    if (!year || !month || !day) return null;

    const previousDate = new Date(year, month - 1, day);
    previousDate.setDate(previousDate.getDate() - 1);

    return {
      report_date: format(previousDate, 'yyyy-MM-dd'),
      shift: 'night',
    };
  }

  return null;
}

function getTodayIsoDate() {
  return format(new Date(), 'yyyy-MM-dd');
}

function getCurrentTimeValue() {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

function normalizeTime(value: string | null | undefined) {
  if (!value) return '';
  return value.slice(0, 5);
}

function getQualityCellKey(
  lineId: string,
  qualityControlId: string,
  timeSlot: string,
) {
  return `${lineId}_${qualityControlId}_${normalizeTime(timeSlot)}`;
}

function formatTime(value: string | null | undefined) {
  const normalized = normalizeTime(value);
  return normalized || '—';
}

function formatPlantNoticeTimestamp(value: string | null | undefined) {
  if (!value) return '—';

  try {
    return format(parseISO(value), 'dd/MM/yyyy HH:mm');
  } catch {
    return value;
  }
}

function getPlantNoticePriorityLabel(value: string | null | undefined) {
  if (value === 'urgent') return 'Urgente';
  if (value === 'important') return 'Importante';
  return 'Normal';
}

function formatProductionQuantity(
  value: number | null | undefined,
) {
  if (value === null || value === undefined) return '—';

  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) return '—';

  return Number.isInteger(numericValue)
    ? String(numericValue)
    : numericValue.toLocaleString('es-ES', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
        useGrouping: false,
      });
}

function addMinutesToTime(value: string, minutesToAdd: number) {
  const normalized = normalizeTime(value);

  if (!normalized) return '';

  const [hour, minute] = normalized.split(':').map(Number);

  if (Number.isNaN(hour) || Number.isNaN(minute)) return '';

  const totalMinutes = (hour * 60 + minute + minutesToAdd + 24 * 60) % (24 * 60);
  const resultHour = Math.floor(totalMinutes / 60);
  const resultMinute = totalMinutes % 60;

  return `${String(resultHour).padStart(2, '0')}:${String(resultMinute).padStart(2, '0')}`;
}

function getShiftEndTimeFromSlots(slots: ProductionReportShiftTimeSlot[]) {
  const activeSlots = slots.filter((slot) => slot.is_active !== false);

  if (activeSlots.length === 0) return '';

  const sortedSlots = [...activeSlots].sort((a, b) => {
    const aOrder = a.display_order ?? 0;
    const bOrder = b.display_order ?? 0;

    if (aOrder !== bOrder) return aOrder - bOrder;

    return normalizeTime(a.time_slot).localeCompare(normalizeTime(b.time_slot));
  });

  const lastSlot = sortedSlots[sortedSlots.length - 1];

  return addMinutesToTime(normalizeTime(lastSlot.time_slot), 30);
}

function getShiftStartTimeFromSlots(slots: ProductionReportShiftTimeSlot[]) {
  const activeSlots = slots.filter((slot) => slot.is_active !== false);

  if (activeSlots.length === 0) return '';

  const sortedSlots = [...activeSlots].sort((a, b) => {
    const aOrder = a.display_order ?? 0;
    const bOrder = b.display_order ?? 0;

    if (aOrder !== bOrder) return aOrder - bOrder;

    return normalizeTime(a.time_slot).localeCompare(normalizeTime(b.time_slot));
  });

  return normalizeTime(sortedSlots[0].time_slot);
}

function timeToMinutes(value: string | null | undefined) {
  const normalized = normalizeTime(value);

  if (!normalized) return null;

  const [hour, minute] = normalized.split(':').map(Number);

  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;

  return hour * 60 + minute;
}

function isTimeGreaterOrEqual(value: string | null | undefined, reference: string | null | undefined) {
  const valueMinutes = timeToMinutes(value);
  const referenceMinutes = timeToMinutes(reference);

  if (valueMinutes === null || referenceMinutes === null) return false;

  return valueMinutes >= referenceMinutes;
}

function isTimeWithinShift(
  value: string | null | undefined,
  shiftStartTime: string | null | undefined,
  shiftEndTime: string | null | undefined,
) {
  const valueMinutes = timeToMinutes(value);
  const shiftStartMinutes = timeToMinutes(shiftStartTime);
  const shiftEndMinutes = timeToMinutes(shiftEndTime);

  if (
    valueMinutes === null ||
    shiftStartMinutes === null ||
    shiftEndMinutes === null
  ) {
    return false;
  }

  if (shiftStartMinutes <= shiftEndMinutes) {
    return valueMinutes >= shiftStartMinutes && valueMinutes <= shiftEndMinutes;
  }

  return valueMinutes >= shiftStartMinutes || valueMinutes <= shiftEndMinutes;
}

function getShiftTimeValidationMessage(
  value: string | null | undefined,
  label: string,
  shiftStartTime: string | null | undefined,
  shiftEndTime: string | null | undefined,
) {
  const normalizedValue = normalizeTime(value);
  const normalizedShiftStartTime = normalizeTime(shiftStartTime);
  const normalizedShiftEndTime = normalizeTime(shiftEndTime);

  if (!normalizedValue || !normalizedShiftStartTime || !normalizedShiftEndTime) {
    return '';
  }

  if (isTimeWithinShift(normalizedValue, normalizedShiftStartTime, normalizedShiftEndTime)) {
    return '';
  }

  return `${label} debe estar dentro del horario del turno (${normalizedShiftStartTime}-${normalizedShiftEndTime}).`;
}

function isEndTimeSameOrAfterStartTimeWithinShift(
  startTime: string | null | undefined,
  endTime: string | null | undefined,
  shiftStartTime: string | null | undefined,
  shiftEndTime: string | null | undefined,
) {
  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(endTime);
  const shiftStartMinutes = timeToMinutes(shiftStartTime);
  const shiftEndMinutes = timeToMinutes(shiftEndTime);

  if (
    startMinutes === null ||
    endMinutes === null ||
    shiftStartMinutes === null ||
    shiftEndMinutes === null
  ) {
    return false;
  }

  const shiftCrossesMidnight = shiftStartMinutes > shiftEndMinutes;

  if (!shiftCrossesMidnight) {
    return endMinutes >= startMinutes;
  }

  const normalizeForNightShift = (minutes: number) =>
    minutes < shiftStartMinutes ? minutes + 24 * 60 : minutes;

  return normalizeForNightShift(endMinutes) >= normalizeForNightShift(startMinutes);
}

function calculateElapsedMinutesFromStart(startTime: string | null | undefined) {
  const startMinutes = timeToMinutes(startTime);
  const nowMinutes = timeToMinutes(getCurrentTimeValue());

  if (startMinutes === null || nowMinutes === null) return null;

  if (nowMinutes >= startMinutes) {
    return nowMinutes - startMinutes;
  }

  return nowMinutes + 24 * 60 - startMinutes;
}

function formatElapsedMinutes(totalMinutes: number | null) {
  if (totalMinutes === null) return '—';

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours <= 0) return `${minutes} min`;

  return `${hours} h ${String(minutes).padStart(2, '0')} min`;
}

function getEffectiveProductionStartTime(startTime: string, productionStartTime: string) {
  return productionStartTime || startTime;
}

function calculateSetupDurationMinutes(startTime: string, productionStartTime: string) {
  const effectiveProductionStartTime = getEffectiveProductionStartTime(
    startTime,
    productionStartTime,
  );

  if (!startTime || !effectiveProductionStartTime) return null;

  return calculateDurationMinutes(startTime, effectiveProductionStartTime);
}

function calculateRealProductionDurationMinutes(
  productionStartTime: string,
  endTime: string,
) {
  if (!productionStartTime || !endTime) return null;

  return calculateDurationMinutes(productionStartTime, endTime);
}

function parsePositiveNumber(value: string | number | null | undefined) {
  if (value === null || value === undefined || value === '') return null;

  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue) || parsedValue <= 0) return null;

  return parsedValue;
}

const PENDING_QUANTITY_EXTRA_FIELD_KEY = '__pending_quantity_value';

function getLinePendingQuantityValue(
  line:
    | Pick<ProductionReportLine, 'quantity_value' | 'extra_fields'>
    | Pick<LineForm, 'quantity_value' | 'extra_fields'>
    | null
    | undefined,
) {
  if (!line) return null;

  const pendingQuantity = parsePositiveNumber(
    line.extra_fields?.[PENDING_QUANTITY_EXTRA_FIELD_KEY] as string | number | null | undefined,
  );

  if (pendingQuantity !== null) {
    return pendingQuantity;
  }

  return parsePositiveNumber(line.quantity_value);
}

function getPendingQuantityAdornmentValue(line: ProductionReportLine | null) {
  const pendingQuantity = getLinePendingQuantityValue(line);

  if (pendingQuantity === null) {
    return '';
  }

  return `/${pendingQuantity}`;
}

function formatEstimatedMinutes(totalMinutes: number | null) {
  if (totalMinutes === null) return '—';

  const roundedMinutes = Math.round(totalMinutes);
  const hours = Math.floor(roundedMinutes / 60);
  const minutes = roundedMinutes % 60;

  if (hours <= 0) return `${minutes} min`;

  return `${hours} h ${String(minutes).padStart(2, '0')} min`;
}

function calculateActiveLineTimingDeviation(
  elapsedMinutes: number | null,
  estimatedDurationMinutes: number | null,
) {
  if (elapsedMinutes === null || estimatedDurationMinutes === null) return null;

  const roundedEstimatedMinutes = Math.round(estimatedDurationMinutes);
  const deltaMinutes = elapsedMinutes - roundedEstimatedMinutes;

  if (deltaMinutes > 0) {
    return {
      label: 'Tiempo extra consumido',
      valueLabel: `+${formatEstimatedMinutes(deltaMinutes)}`,
      isOvertime: true,
    };
  }

  return {
    label: 'Tiempo restante',
    valueLabel: formatEstimatedMinutes(Math.abs(deltaMinutes)),
    isOvertime: false,
  };
}

function calculateLineFormProductionEstimation(
  form: LineForm,
  operationSettings: ProductionOperationSettings | null | undefined,
): ProductionEstimationResult {
  const estimationConfig = operationSettings?.estimation_config;
  const mode =
    estimationConfig?.mode ??
    (estimationConfig?.enable_estimated_duration ? 'quantity_by_speed' : 'none');

  if (!estimationConfig || mode === 'none') {
    return {
      enabled: false,
      estimatedDurationMinutes: null,
      estimatedProductionDurationMinutes: null,
      estimatedEndTime: '',
      formulaLabel: '',
      missingInputs: [],
      usesSetupTime: false,
    };
  }

  const missingInputs: string[] = [];
  const totalQuantity = parsePositiveNumber(form.quantity_value);
  const pendingQuantity = getLinePendingQuantityValue(form);
  const estimationQuantity = pendingQuantity ?? totalQuantity;
  const speed = parsePositiveNumber(form.speed_value);

  if (estimationQuantity === null) {
    missingInputs.push('cantidad');
  }

  if (speed === null) {
    missingInputs.push('velocidad');
  }

  let multiplier = 1;
  let multiplierLabel = '';

  if (mode === 'quantity_by_speed_and_multiplier') {
    const multiplierFieldKey = estimationConfig.multiplier_field_key;
    const multiplierField = operationSettings?.additional_fields_config?.find(
      (field) => field.key === multiplierFieldKey,
    );

    multiplierLabel = multiplierField?.label?.trim() || 'multiplicador';

    if (!multiplierFieldKey) {
      missingInputs.push('campo multiplicador');
    } else {
      const multiplierValue = parsePositiveNumber(form.extra_fields[multiplierFieldKey]);

      if (multiplierValue === null) {
        missingInputs.push(multiplierLabel);
      } else {
        multiplier = multiplierValue;
      }
    }
  }

  const quantityLabel = pendingQuantity !== null ? 'cantidad pendiente' : 'cantidad total OF';

  const formulaLabel =
    mode === 'quantity_by_speed_and_multiplier'
      ? `${quantityLabel} / (velocidad × ${multiplierLabel || 'multiplicador'})`
      : `${quantityLabel} / velocidad`;

  if (missingInputs.length > 0 || estimationQuantity === null || speed === null) {
    return {
      enabled: true,
      estimatedDurationMinutes: null,
      estimatedProductionDurationMinutes: null,
      estimatedEndTime: '',
      formulaLabel,
      missingInputs,
      usesSetupTime: Boolean(estimationConfig.include_setup_time),
    };
  }

  const denominator = speed * multiplier;

  if (denominator <= 0) {
    return {
      enabled: true,
      estimatedDurationMinutes: null,
      estimatedProductionDurationMinutes: null,
      estimatedEndTime: '',
      formulaLabel,
      missingInputs: ['velocidad válida'],
      usesSetupTime: Boolean(estimationConfig.include_setup_time),
    };
  }

  const productionDurationMinutes =
    estimationConfig.speed_time_unit === 'hour'
      ? (estimationQuantity / denominator) * 60
      : estimationQuantity / denominator;

  const setupTimeMinutes =
    estimationConfig.include_setup_time
      ? operationSettings?.production_params?.setup_time_minutes ?? 0
      : 0;

  const estimatedDurationMinutes = productionDurationMinutes + setupTimeMinutes;
  const estimatedProductionStartTime = getEffectiveProductionStartTime(
    form.start_time,
    form.production_start_time,
  );
  const estimatedEndTime = estimatedProductionStartTime
    ? addMinutesToTime(estimatedProductionStartTime, Math.round(productionDurationMinutes))
    : '';

  return {
    enabled: true,
    estimatedDurationMinutes,
    estimatedProductionDurationMinutes: productionDurationMinutes,
    estimatedEndTime,
    formulaLabel,
    missingInputs: estimatedProductionStartTime ? [] : ['hora inicio fabricación'],
    usesSetupTime: Boolean(estimationConfig.include_setup_time),
  };
}

function calculateProductionReportLineEstimation(
  line: ProductionReportLine,
  operationSettings: ProductionOperationSettings | null | undefined,
): ProductionEstimationResult {
  return calculateLineFormProductionEstimation(
    {
      product_id: line.product_id ?? '',
      free_product_ref: line.free_product_ref ?? '',
      customer_number: line.customer_number ?? '',
      customer_order_number: line.customer_order_number ?? '',
      manufacturing_order_number: line.manufacturing_order_number ?? '',
      quantity_value: line.quantity_value === null ? '' : String(line.quantity_value),
      actual_quantity_value:
        line.actual_quantity_value === null ? '' : String(line.actual_quantity_value),
      quantity_unit: line.quantity_unit || 'm',
      speed_value: line.speed_value === null ? '' : String(line.speed_value),
      start_time: normalizeTime(line.start_time),
      production_start_time: normalizeTime(line.production_start_time) || normalizeTime(line.start_time),
      end_time: normalizeTime(line.end_time),
      material_usages: [],
      notes: line.notes ?? '',
      extra_fields: buildLineExtraFieldsFormValue(line.extra_fields),
    },
    operationSettings,
  );
}


function getShiftLabel(value: string | null | undefined) {
  return SHIFT_OPTIONS.find((item) => item.value === value)?.label ?? value ?? '—';
}

function getStatusLabel(value: string | null | undefined) {
  if (value === 'draft') return 'Borrador';
  if (value === 'validated') return 'Validado';
  if (value === 'exported') return 'Exportado';
  return value ?? '—';
}

function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const maybeError = error as {
      response?: { data?: { detail?: string } };
      message?: string;
    };

    if (maybeError.response?.data?.detail) return maybeError.response.data.detail;
    if (maybeError.message) return maybeError.message;
  }

  return 'Se produjo un error inesperado.';
}

function getTimelineValidationIssue(error: unknown): TimelineValidationIssue | null {
  if (typeof error !== 'object' || error === null) return null;

  const maybeError = error as {
    response?: {
      data?: {
        detail?: unknown;
      };
    };
  };

  const detail = maybeError.response?.data?.detail;

  if (!detail || typeof detail !== 'object') return null;

  const maybeIssue = detail as TimelineValidationIssue;

  if (maybeIssue.code !== 'timeline_validation_failed') {
    return null;
  }

  return maybeIssue;
}

function calculateDurationMinutes(startTime: string, endTime: string) {
  if (!startTime || !endTime) return null;

  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);

  if (
    Number.isNaN(startHour) ||
    Number.isNaN(startMinute) ||
    Number.isNaN(endHour) ||
    Number.isNaN(endMinute)
  ) {
    return null;
  }

  const startTotal = startHour * 60 + startMinute;
  let endTotal = endHour * 60 + endMinute;

  if (endTotal < startTotal) endTotal += 24 * 60;

  return endTotal - startTotal;
}

function getMinutesFromShiftStart(
  value: string | null | undefined,
  shiftStartTime: string | null | undefined,
) {
  const minutes = timeToMinutes(value);
  const shiftStartMinutes = timeToMinutes(shiftStartTime);

  if (minutes === null || shiftStartMinutes === null) return 0;

  return minutes < shiftStartMinutes
    ? minutes + 24 * 60 - shiftStartMinutes
    : minutes - shiftStartMinutes;
}

function buildEmptyLineMaterialUsageDraft(
  displayOrder: number,
): ProductionReportLineMaterialUsageDraft {
  return {
    display_order: displayOrder,
    production_run_segment_id: null,
    material_lot_id: null,
    material_id: null,
    material_name: '',
    lot_number: '',
    supplier_name: '',
    quantity_value: '',
    quantity_unit: '',
    notes: '',
    source_type: 'manual',
    copied_from_usage_id: null,
  };
}

function buildLineMaterialUsageDraftsFromLine(
  line: Pick<ProductionReportLine, 'material_usages'>,
): ProductionReportLineMaterialUsageDraft[] {
  return (line.material_usages ?? []).map((usage, index) => ({
    display_order: usage.display_order ?? index,
    production_run_segment_id: usage.production_run_segment_id ?? null,
    material_lot_id: usage.material_lot_id ?? null,
    material_id: usage.material_id ?? null,
    material_name: usage.material_name ?? '',
    lot_number: usage.lot_number ?? '',
    supplier_name: usage.supplier_name ?? '',
    quantity_value:
      usage.quantity_value === null || usage.quantity_value === undefined
        ? ''
        : String(usage.quantity_value),
    quantity_unit: usage.quantity_unit ?? '',
    notes: usage.notes ?? '',
    source_type: usage.source_type ?? 'manual',
    copied_from_usage_id: usage.copied_from_usage_id ?? null,
  }));
}

function buildEmptyLineForm(): LineForm {
  return {
    product_id: '',
    free_product_ref: '',
    customer_number: '',
    customer_order_number: '',
    manufacturing_order_number: '',
    quantity_value: '',
    actual_quantity_value: '',
    quantity_unit: 'm',
    speed_value: '',
    start_time: '',
    production_start_time: '',
    end_time: '',
    material_usages: [],
    notes: '',
    extra_fields: {},
  };
}

function buildEmptyStopForm(): StopForm {
  return {
    line_id: '',
    start_time: '',
    end_time: '',
    reason_id: '',
    notes: '',
  };
}

function buildEmptyIdlePeriodForm(): IdlePeriodForm {
  return {
    start_time: '',
    end_time: '',
    notes: '',
  };
}

function buildEmptyNewReportForm(): NewReportForm {
  return {
    template_id: '',
    report_date: getTodayIsoDate(),
    operator_number: '',
    shift: '',
    production_line_config_id: '',
    line_or_center: '',
    notes: '',
  };
}

function hasMaterialUsagesForLineClose(
  form: Pick<LineForm, 'material_usages'>,
) {
  return form.material_usages.some((usage) => usage.material_name.trim());
}

function buildLineMaterialUsagesPayload(
  materialUsages: ProductionReportLineMaterialUsageDraft[],
): ProductionReportLineMaterialUsagePayload[] {
  return materialUsages
    .filter((usage) => {
      const materialLotId = usage.material_lot_id?.trim() ?? '';
      const materialName = usage.material_name.trim();
      const lotNumber = usage.lot_number.trim();
      const supplierName = usage.supplier_name.trim();
      const quantityValue = usage.quantity_value.trim();
      const quantityUnit = usage.quantity_unit.trim();
      const notes = usage.notes.trim();

      return (
        materialLotId ||
        materialName ||
        lotNumber ||
        supplierName ||
        quantityValue ||
        quantityUnit ||
        notes
      );
    })
    .map((usage, index) => ({
      display_order: usage.display_order ?? index,
      production_run_segment_id: usage.production_run_segment_id || null,
      material_lot_id: usage.material_lot_id || null,
      material_id: usage.material_id || null,
      material_name: usage.material_name.trim(),
      lot_number: usage.lot_number.trim() || null,
      supplier_name: usage.supplier_name.trim() || null,
      quantity_value: usage.quantity_value.trim() ? Number(usage.quantity_value) : null,
      quantity_unit: usage.quantity_unit.trim() || null,
      notes: usage.notes.trim() || null,
      source_type: usage.source_type || 'manual',
      copied_from_usage_id: usage.copied_from_usage_id || null,
    }));
}

function buildLinePayload(
  form: LineForm,
  nextOrder: number,
  additionalFields: ProductionOperationSettings['additional_fields_config'] = [],
): CreateProductionReportLinePayload {
  const duration = calculateDurationMinutes(form.start_time, form.end_time);

  return {
    line_order: nextOrder,
    product_id: form.product_id || null,
    free_product_ref: form.free_product_ref.trim() || null,
    customer_number: form.customer_number.trim() || null,
    customer_order_number: form.customer_order_number.trim() || null,
    manufacturing_order_number: form.manufacturing_order_number.trim() || null,
    quantity_value: form.quantity_value.trim() ? Number(form.quantity_value) : null,
    actual_quantity_value: form.actual_quantity_value.trim()
      ? Number(form.actual_quantity_value)
      : null,
    quantity_unit: form.quantity_unit.trim() || 'm',
    speed_value: form.speed_value.trim() ? Number(form.speed_value) : null,
    start_time: form.start_time || null,
    production_start_time:
      getEffectiveProductionStartTime(form.start_time, form.production_start_time) || null,
    end_time: form.end_time || null,
    duration_minutes: duration,
    notes: form.notes.trim() || null,
    extra_fields: buildLineExtraFieldsPayload(form, additionalFields),
    material_usages: buildLineMaterialUsagesPayload(form.material_usages),
  };
}

function buildLineExtraFieldsFormValue(
  extraFields: Record<string, unknown> | null | undefined,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(extraFields ?? {}).map(([key, value]) => [
      key,
      value === null || value === undefined ? '' : String(value),
    ]),
  );
}

function buildLineExtraFieldsPayload(
  form: LineForm,
  additionalFields: ProductionOperationSettings['additional_fields_config'],
): Record<string, unknown> {
  const nextExtraFields: Record<string, unknown> = { ...form.extra_fields };

  additionalFields
    .filter((field) => field.is_active)
    .forEach((field) => {
      const rawValue = String(form.extra_fields[field.key] ?? '').trim();

      if (!rawValue) {
        delete nextExtraFields[field.key];
        return;
      }

      if (field.field_type === 'number') {
        const numericValue = Number(rawValue);
        nextExtraFields[field.key] = Number.isFinite(numericValue) ? numericValue : rawValue;
        return;
      }

      nextExtraFields[field.key] = rawValue;
    });

  return nextExtraFields;
}

function buildStopPayload(form: StopForm): CreateProductionReportStopPayload {
  const duration = calculateDurationMinutes(form.start_time, form.end_time);

  return {
    line_id: form.line_id || null,
    start_time: form.start_time,
    end_time: form.end_time || null,
    duration_minutes: duration,
    reason_id: form.reason_id || null,
    reason_label: null,
    notes: form.notes.trim() || null,
  };
}

function buildIdlePeriodPayload(
  form: IdlePeriodForm,
  productionLineConfigId: string | null | undefined,
): UpdateProductionReportIdlePeriodPayload {
  const duration = calculateDurationMinutes(form.start_time, form.end_time);

  return {
    production_line_config_id: productionLineConfigId || null,
    start_time: form.start_time,
    end_time: form.end_time,
    duration_minutes: duration,
    idle_type: 'no_planned_order',
    notes: form.notes.trim() || null,
  };
}

export default function IndustrialCostProductionReportsOperatorView({
  permissions,
}: IndustrialCostProductionReportsOperatorViewProps) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const canValidateProductionReports =
    !permissions || permissions.includes('production_reports:validate');

  const [selectedReportId, setSelectedReportId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;

    try {
      return (
        window.sessionStorage.getItem(OPERATOR_SELECTED_REPORT_SESSION_KEY) ||
        null
      );
    } catch {
      return null;
    }
  });

  const [screenMode, setScreenMode] = useState<ScreenMode>(
    () => selectedReportId ? 'detail' : 'list',
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      if (screenMode === 'detail' && selectedReportId) {
        window.sessionStorage.setItem(
          OPERATOR_SELECTED_REPORT_SESSION_KEY,
          selectedReportId,
        );
      } else {
        window.sessionStorage.removeItem(
          OPERATOR_SELECTED_REPORT_SESSION_KEY,
        );
      }
    } catch {
      // El almacenamiento local no debe bloquear el puesto de operario.
    }
  }, [screenMode, selectedReportId]);

  const [plantNoticesDialogOpen, setPlantNoticesDialogOpen] = useState(false);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [operatorNumberDialogOpen, setOperatorNumberDialogOpen] = useState(false);
  const [operatorNumberDraft, setOperatorNumberDraft] = useState('');
  const [touchModeEnabled, setTouchModeEnabled] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;

    const storedValue = window.localStorage.getItem(
      'proa-industrial-operator-touch-mode',
    );

    if (storedValue === '1') return true;
    if (storedValue === '0') return false;

    return (
      window.navigator.maxTouchPoints > 0 ||
      window.matchMedia('(pointer: coarse)').matches
    );
  });

  const [newReportOperatorKeyboardOpen, setNewReportOperatorKeyboardOpen] =
    useState(false);

  const [lineTouchKeyboard, setLineTouchKeyboard] = useState<{
    title: string;
    value: string;

    allowDecimal?: boolean;
    preserveLeadingZeros?: boolean;
    keyboardType?: 'numeric' | 'text';
    field?:
      | 'customer_number'
      | 'free_product_ref'
      | 'quantity_value'
      | 'speed_value'
      | 'actual_quantity_value'
      | 'extra_field';
    section?: 'identity' | 'production' | 'additional' | 'closure';
    extraFieldKey?: string;
    onConfirm: (value: string) => void;
  } | null>(null);


  const [reportWriteLocked, setReportWriteLocked] = useState(false);

  const reportWriteLockRef = useRef(false);
  const [lineDialogOpen, setLineDialogOpen] = useState(false);

  useEffect(() => {
    if (!lineDialogOpen) {
      setLineTouchKeyboard(null);
    }
  }, [lineDialogOpen]);

  const [editingLineId, setEditingLineId] = useState<string | null>(null);

  const [lineToDelete, setLineToDelete] = useState<ProductionReportLine | null>(null);
  const [lineWriteLocked, setLineWriteLocked] = useState(false);
  const lineWriteLockRef = useRef(false);
  const [finishLineDialogOpen, setFinishLineDialogOpen] = useState(false);
  const [finishLineEndTime, setFinishLineEndTime] = useState('');
  const [finishLineStopReasonId, setFinishLineStopReasonId] = useState('');
  const [finishLineForm, setFinishLineForm] = useState<LineForm>(buildEmptyLineForm());
  const [stopDialogOpen, setStopDialogOpen] = useState(false);
  const [editingStopId, setEditingStopId] = useState<string | null>(null);
  const [stopToDelete, setStopToDelete] = useState<ProductionReportStop | null>(null);
  const [stopWriteLocked, setStopWriteLocked] = useState(false);
  const stopWriteLockRef = useRef(false);
  const [idlePeriodDialogOpen, setIdlePeriodDialogOpen] = useState(false);
  const [editingIdlePeriodId, setEditingIdlePeriodId] = useState<string | null>(null);
  const [idlePeriodForm, setIdlePeriodForm] = useState<IdlePeriodForm>(buildEmptyIdlePeriodForm());
  const [idlePeriodToDelete, setIdlePeriodToDelete] = useState<ProductionReportIdlePeriod | null>(null);
  const [idlePeriodWriteLocked, setIdlePeriodWriteLocked] = useState(false);
  const idlePeriodWriteLockRef = useRef(false);
  const [optimisticQualityResults, setOptimisticQualityResults] = useState<
    Record<string, ProductionReportQualityResult>
  >({});
  const [pendingQualityCellKeys, setPendingQualityCellKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const pendingQualityCellKeysRef = useRef(new Set<string>());
  const [nextLineStartAfterFinish, setNextLineStartAfterFinish] = useState<string | null>(null);
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [reopenNotes, setReopenNotes] = useState('');
  const [timelineValidationDialogOpen, setTimelineValidationDialogOpen] = useState(false);
  const [timelineValidationIssue, setTimelineValidationIssue] =
    useState<TimelineValidationIssue | null>(null);
  const [pendingTimelineResolution, setPendingTimelineResolution] = useState<
    | {
        kind: 'gap_create_stop';
        start_time: string;
        end_time: string;
      }
    | {
        kind: 'gap_create_idle_period';
        start_time: string;
        end_time: string;
      }
    | {
        kind: 'gap_extend_previous_end';
        start_time: string;
        end_time: string;
        expected_end_time: string;
      }
    | {
        kind: 'gap_move_next_start';
        start_time: string;
        end_time: string;
        expected_start_time: string;
      }
    | {
        kind: 'overlap_adjust_previous_end';
        start_time: string;
        end_time: string;
        expected_end_time: string;
      }
    | {
        kind: 'overlap_adjust_next_start';
        start_time: string;
        end_time: string;
        expected_start_time: string;
      }
    | null
  >(null);
  const [pendingIncompleteEventResolution, setPendingIncompleteEventResolution] = useState<{
    event_type: 'line' | 'stop' | 'idle';
    event_id: string;
  } | null>(null);
  const [qualityDialogLineId, setQualityDialogLineId] = useState<string | null>(null);
  const [materialUsagesDialogOpen, setMaterialUsagesDialogOpen] = useState(false);
  const [materialUsagesDraft, setMaterialUsagesDraft] = useState<
    ProductionReportLineMaterialUsageDraft[]
  >([]);

  const [clockTick, setClockTick] = useState(0);
  const lastKnownTodayRef = useRef(getTodayIsoDate());

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setClockTick((current) => current + 1);
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const currentToday = getTodayIsoDate();
    const previousToday = lastKnownTodayRef.current;

    if (currentToday === previousToday) return;

    lastKnownTodayRef.current = currentToday;

    setListFilters((current) => {
      if (current.report_date !== previousToday) {
        return current;
      }

      return {
        ...current,
        report_date: currentToday,
      };
    });
  }, [clockTick]);

  const [listFilters, setListFilters] = useState<{
    report_date: string;
    status_filter: 'all' | 'draft' | 'validated' | 'exported';
    production_line_config_id: string;
  }>({
    report_date: getTodayIsoDate(),
    status_filter: 'all',
    production_line_config_id: 'all',
  });

  const [newReportForm, setNewReportForm] = useState<NewReportForm>(buildEmptyNewReportForm());

  const [lineForm, setLineForm] = useState<LineForm>(buildEmptyLineForm());
  const [stopForm, setStopForm] = useState<StopForm>(buildEmptyStopForm());
  const [resolvingExistingOrder, setResolvingExistingOrder] = useState(false);
  const [lineDefaultsSuggested, setLineDefaultsSuggested] = useState(false);
  const [existingOrderSearchText, setExistingOrderSearchText] = useState('');
  const [debouncedExistingOrderSearchText, setDebouncedExistingOrderSearchText] =
    useState('');
  const [resolvedExistingOrderNumber, setResolvedExistingOrderNumber] = useState('');
  const [resolvedExistingOrderSummary, setResolvedExistingOrderSummary] = useState<{
    totalQuantity: number | null;
    pendingQuantity: number | null;
    quantityUnit: string | null;
    hasPreviousProduction: boolean;
  } | null>(null);

  useEffect(() => {
    const normalizedSearchText = existingOrderSearchText.trim();

    if (normalizedSearchText.length < 2) {
      setDebouncedExistingOrderSearchText('');
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setDebouncedExistingOrderSearchText(normalizedSearchText);
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [existingOrderSearchText]);

  const operatorContextQuery = useQuery({
    queryKey: [
      'industrial-cost-production-report-operator-context',
      newReportForm.template_id,
      newReportForm.shift,
    ],
    queryFn: () =>
      getIndustrialCostProductionReportOperatorContext({
        template_id: newReportForm.template_id || null,
        shift: newReportForm.shift || null,
      }),
  });

  const productionReportsQuery = useQuery<ProductionReport[]>({
    queryKey: [
      'industrial-cost-production-reports',
      listFilters.report_date,
      listFilters.status_filter,
    ],
    queryFn: () =>
      listIndustrialCostProductionReports({
        report_date: listFilters.report_date || null,
        status_filter:
          listFilters.status_filter === 'all' ? null : listFilters.status_filter,
      }),
    refetchInterval: screenMode === 'list' ? 30000 : false,
  });

  const productsQuery = useQuery<Product[]>({
    queryKey: ['industrial-cost-products'],
    queryFn: getIndustrialCostProducts,
  });

  const productionLinesConfigQuery = useQuery<ProductionLineConfig[]>({
    queryKey: ['industrial-cost-production-config-lines-active'],
    queryFn: async () => {
      const rows = await listIndustrialCostProductionLinesConfig();
      return rows.filter((line) => line.is_active);
    },
  });

  const operationSettingsQuery = useQuery({
    queryKey: ['industrial-cost-production-config-operation-settings'],
    queryFn: getIndustrialCostProductionOperationSettingsConfig,
  });

  const productionMaterialLotsQuery = useQuery<ProductionMaterialLot[]>({
    queryKey: ['industrial-cost-production-material-lots', materialUsagesDialogOpen],
    queryFn: () =>
      listIndustrialCostProductionMaterialLots({
        limit: 100,
      }),
    enabled: materialUsagesDialogOpen,
    staleTime: 30000,
  });

  const productionMaterialLots = productionMaterialLotsQuery.data ?? [];

  const reportDetailQuery = useQuery<ProductionReportDetail>({
    queryKey: ['industrial-cost-production-report-detail', selectedReportId],
    queryFn: () => getIndustrialCostProductionReportDetail(selectedReportId as string),
    enabled: Boolean(selectedReportId),
    refetchInterval: screenMode === 'detail' && selectedReportId ? 30000 : false,
  });

  const operatorPlantNoticesQuery = useQuery<PlantNotice[]>({
    queryKey: ['industrial-cost-plant-notices', 'operator', selectedReportId],
    queryFn: () =>
      listIndustrialCostPlantNotices({
        status_filter: 'active',
        report_id: selectedReportId,
      }),
    enabled: screenMode === 'detail' && Boolean(selectedReportId),
    refetchInterval:
      screenMode === 'detail' && selectedReportId ? 30000 : false,
    refetchIntervalInBackground: false,
  });

  const acknowledgePlantNoticeMutation = useMutation({
    mutationFn: ({
      noticeId,
      productionLineConfigId,
    }: {
      noticeId: string;
      productionLineConfigId: string;
    }) => {
      if (!selectedReportId) {
        throw new Error('No hay parte seleccionado.');
      }

      return acknowledgeIndustrialCostPlantNotice(noticeId, {
        production_line_config_id: productionLineConfigId,
        report_id: selectedReportId,
      });
    },
    onSuccess: async () => {
      enqueueSnackbar('Lectura del aviso confirmada.', {
        variant: 'success',
      });

      await operatorPlantNoticesQuery.refetch();
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    },
  });

  const existingOrderCandidatesQuery = useQuery<ExistingProductionOrderCandidate[]>({
    queryKey: [
      'industrial-cost-production-existing-orders-search',
      reportDetailQuery.data?.report.production_line_config_id ?? null,
      debouncedExistingOrderSearchText,
    ],
    queryFn: () =>
      searchIndustrialCostExistingProductionOrders({
        search_text: debouncedExistingOrderSearchText,
        production_line_config_id:
          reportDetailQuery.data?.report.production_line_config_id ?? null,
        limit: 20,
      }),
    enabled:
      Boolean(reportDetailQuery.data?.report.production_line_config_id) &&
      reportDetailQuery.data?.report.status === 'draft' &&
      debouncedExistingOrderSearchText.length >= 2,
    staleTime: 30000,
  });

  const filteredExistingOrderCandidates = useMemo(() => {
    const normalizedSearchText = existingOrderSearchText
      .trim()
      .toLowerCase();

    if (normalizedSearchText.length < 2) {
      return [];
    }

    return (existingOrderCandidatesQuery.data ?? [])
      .filter((candidate) =>
        String(candidate.manufacturing_order_number || '')
          .toLowerCase()
          .includes(normalizedSearchText),
      )
      .slice(0, 20);
  }, [
    existingOrderCandidatesQuery.data,
    existingOrderSearchText,
  ]);

  const context = operatorContextQuery.data;
  const templates = context?.templates ?? [];
  const stopReasons = context?.stop_reasons ?? [];
  const qualityControls = context?.quality_controls ?? [];
  const shiftTimeSlots = context?.shift_time_slots ?? [];
  const reports = useMemo(() => {
    const rows = productionReportsQuery.data ?? [];

    if (listFilters.production_line_config_id === 'all') {
      return rows;
    }

    return rows.filter(
      (report) => report.production_line_config_id === listFilters.production_line_config_id,
    );
  }, [listFilters.production_line_config_id, productionReportsQuery.data]);
  const products = productsQuery.data ?? [];
  const productionLinesConfig = productionLinesConfigQuery.data ?? [];

  useEffect(() => {
    if (listFilters.production_line_config_id === 'all') return;
    if (productionLinesConfig.some((line) => line.id === listFilters.production_line_config_id)) return;

    setListFilters((current) => ({
      ...current,
      production_line_config_id: 'all',
    }));
  }, [listFilters.production_line_config_id, productionLinesConfig]);

  const productionReportBoardRows = useMemo(() => {
    const normalizeLineKey = (value: string | null | undefined) =>
      String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');

    const lineById = new Map<string, ProductionLineConfig>();
    const rowByLineId = new Map<
      string,
      {
        line: ProductionLineConfig;
        reportsByShift: Map<string, ProductionReport>;
      }
    >();
    const rowByLineAlias = new Map<
      string,
      {
        line: ProductionLineConfig;
        reportsByShift: Map<string, ProductionReport>;
      }
    >();

    const rows = productionLinesConfig
      .filter((line) =>
        listFilters.production_line_config_id === 'all'
          ? true
          : line.id === listFilters.production_line_config_id,
      )
      .map((line) => {
        const row = {
          line,
          reportsByShift: new Map<string, ProductionReport>(),
        };

        lineById.set(line.id, line);
        rowByLineId.set(line.id, row);

        [
          line.code,
          line.name,
          `${line.code} ${line.name}`,
          `${line.code} · ${line.name}`,
          `${line.name} ${line.code}`,
          `${line.name} · ${line.code}`,
        ].forEach((alias) => {
          const normalizedAlias = normalizeLineKey(alias);

          if (normalizedAlias) {
            rowByLineAlias.set(normalizedAlias, row);
          }
        });

        return row;
      });

    const legacyRowsByLineKey = new Map<
      string,
      {
        line: ProductionLineConfig;
        reportsByShift: Map<string, ProductionReport>;
      }
    >();

    const assignReportToRow = (
      row: {
        line: ProductionLineConfig;
        reportsByShift: Map<string, ProductionReport>;
      },
      report: ProductionReport,
    ) => {
      const currentReportForShift = row.reportsByShift.get(report.shift);

      if (!currentReportForShift) {
        row.reportsByShift.set(report.shift, report);
        return;
      }

      const currentIsDraft = currentReportForShift.status === 'draft';
      const nextIsDraft = report.status === 'draft';

      if (nextIsDraft && !currentIsDraft) {
        row.reportsByShift.set(report.shift, report);
      }
    };

    reports.forEach((report) => {
      const lineId = report.production_line_config_id ?? '';
      const rowById = lineId ? rowByLineId.get(lineId) : null;

      if (rowById) {
        assignReportToRow(rowById, report);
        return;
      }

      const lineAlias = normalizeLineKey(report.line_or_center);
      const rowByAlias = lineAlias ? rowByLineAlias.get(lineAlias) : null;

      if (rowByAlias) {
        assignReportToRow(rowByAlias, report);
        return;
      }

      const legacyLineKey = lineAlias || `legacy-${report.id}`;
      const existingLegacyRow = legacyRowsByLineKey.get(legacyLineKey);

      if (existingLegacyRow) {
        assignReportToRow(existingLegacyRow, report);
        return;
      }

      const legacyRow = {
        line: {
          id: `legacy-${legacyLineKey}`,
          tenant_id: report.tenant_id,
          code: report.line_or_center || 'Sin línea',
          name: report.line_or_center || 'Parte sin línea configurada',
          process_group: 'Sin clasificación',
          production_line_group_id: null,
          production_line_group_code: null,
          production_line_group_name: null,
          description: null,
          is_active: true,
          display_order: 9999,
          created_at: report.created_at,
          updated_at: report.updated_at,
        } satisfies ProductionLineConfig,
        reportsByShift: new Map<string, ProductionReport>(),
      };

      assignReportToRow(legacyRow, report);
      legacyRowsByLineKey.set(legacyLineKey, legacyRow);
    });

    return [...rows, ...Array.from(legacyRowsByLineKey.values())].sort((a, b) => {
      const groupDelta = String(a.line.process_group || '').localeCompare(
        String(b.line.process_group || ''),
      );

      if (groupDelta !== 0) return groupDelta;

      const displayOrderDelta = (a.line.display_order ?? 0) - (b.line.display_order ?? 0);

      if (displayOrderDelta !== 0) return displayOrderDelta;

      return `${a.line.name} ${a.line.code}`.localeCompare(`${b.line.name} ${b.line.code}`);
    });
  }, [listFilters.production_line_config_id, productionLinesConfig, reports]);

  const productionReportBoardGroups = useMemo(() => {
    const groups = new Map<
      string,
      {
        groupLabel: string;
        rows: typeof productionReportBoardRows;
      }
    >();

    productionReportBoardRows.forEach((row) => {
      const groupLabel = row.line.process_group?.trim() || 'Sin clasificación';
      const existingGroup = groups.get(groupLabel);

      if (existingGroup) {
        existingGroup.rows.push(row);
        return;
      }

      groups.set(groupLabel, {
        groupLabel,
        rows: [row],
      });
    });

    return Array.from(groups.values());
  }, [productionReportBoardRows]);

  const operationSettings = operationSettingsQuery.data ?? null;
  const traceabilityConfig = operationSettings?.traceability_config;
  const materialUsageCaptureEnabled =
    traceabilityConfig?.mode !== 'none' &&
    traceabilityConfig?.material_usage_capture_enabled !== false;
  const requireLotOnMaterialUsage =
    traceabilityConfig?.require_lot_on_material_usage !== false;
  const requireQuantityOnMaterialUsage =
    traceabilityConfig?.require_quantity_on_material_usage === true;
  const requireExistingMaterialLot =
    traceabilityConfig?.require_existing_material_lot === true;
  const requireMaterialUsageOnLineClose =
    materialUsageCaptureEnabled &&
    traceabilityConfig?.require_material_usage_on_line_close === true;
  const detail = reportDetailQuery.data ?? null;
  const selectedReport = detail?.report ?? null;
  const isDraft = selectedReport?.status === 'draft';

  const activePlantNotices = operatorPlantNoticesQuery.data ?? [];

  const getSelectedReportPlantNoticeTarget = (notice: PlantNotice) =>
    notice.targets.find(
      (target) =>
        target.production_line_config_id ===
        selectedReport?.production_line_config_id,
    ) ?? null;

  const pendingPlantNotices = activePlantNotices.filter((notice) => {
    const target = getSelectedReportPlantNoticeTarget(notice);

    return !target?.current_report_acknowledged_at;
  });

  const bannerContinuationCandidate = useMemo(() => {
    if (!selectedReport || !isDraft || !detail?.pending_continuation_line) {
      return null;
    }

    const hasAnyRegisteredLine = detail.lines.length > 0;
    const hasAnyRegisteredStop = detail.stops.length > 0;
    const hasAnyRegisteredIdlePeriod = (detail.idle_periods ?? []).length > 0;

    if (hasAnyRegisteredLine || hasAnyRegisteredStop || hasAnyRegisteredIdlePeriod) {
      return null;
    }

    const pendingLine = detail.pending_continuation_line;

    if (pendingLine.continuation_status !== 'pending_continuation') {
      return null;
    }

    if ((pendingLine.remaining_quantity_value ?? 0) <= 0) {
      return null;
    }

    const previousShiftContext = getPreviousShiftContext(
      selectedReport.report_date,
      selectedReport.shift,
    );

    if (!previousShiftContext) {
      return null;
    }

    if (
      pendingLine.source_report_date &&
      pendingLine.source_report_date !== previousShiftContext.report_date
    ) {
      return null;
    }

    if (
      pendingLine.source_shift &&
      pendingLine.source_shift !== previousShiftContext.shift
    ) {
      return null;
    }

    return {
      line_id: pendingLine.id,
      manufacturing_order_number: pendingLine.manufacturing_order_number,
      product_id: pendingLine.product_id,
      product_label: pendingLine.free_product_ref,
      customer_number: pendingLine.customer_number,
      customer_order_number: pendingLine.customer_order_number,
      quantity_value: pendingLine.quantity_value,
      actual_quantity_accumulated: pendingLine.actual_quantity_accumulated,
      remaining_quantity_value: pendingLine.remaining_quantity_value,
      quantity_unit: pendingLine.quantity_unit,
      continuation_status: pendingLine.continuation_status,
      speed_value: pendingLine.speed_value,
      notes: pendingLine.notes,
      extra_fields: pendingLine.extra_fields,
      source_type: 'open_order' as const,
      source_report_id: pendingLine.source_report_id ?? pendingLine.report_id,
      source_report_date: pendingLine.source_report_date ?? previousShiftContext.report_date,
      source_shift: pendingLine.source_shift ?? previousShiftContext.shift,
      source_end_time: pendingLine.source_end_time ?? pendingLine.end_time,
    };
  }, [
    selectedReport,
    isDraft,
    detail,
  ]);

  const productById = useMemo(() => {
    const map = new Map<string, Product>();

    products.forEach((product) => {
      map.set(product.id, product);
    });

    return map;
  }, [products]);

  const getOrderFieldLabel = useMemo(() => {
    return (
      fieldKey: keyof ProductionOperationSettings['order_fields_config'],
      fallback: string,
    ) => operationSettings?.order_fields_config?.[fieldKey]?.label?.trim() || fallback;
  }, [operationSettings]);

  const isOrderFieldVisible = useMemo(() => {
    return (...fieldKeys: (keyof ProductionOperationSettings['order_fields_config'])[]) =>
      fieldKeys.some((fieldKey) => operationSettings?.order_fields_config?.[fieldKey]?.visible ?? true);
  }, [operationSettings]);

  const isOrderFieldRequiredOnClose = useMemo(() => {
    return (...fieldKeys: (keyof ProductionOperationSettings['order_fields_config'])[]) =>
      fieldKeys.some((fieldKey) => operationSettings?.order_fields_config?.[fieldKey]?.required ?? false);
  }, [operationSettings]);

  const visibleAdditionalOrderFields = useMemo(
    () =>
      [...(operationSettings?.additional_fields_config ?? [])]
        .filter((field) => field.is_active && field.visible)
        .sort((a, b) => a.display_order - b.display_order),
    [operationSettings],
  );

  const additionalOrderFieldByKey = useMemo(() => {
    const map = new Map<
      string,
      ProductionOperationSettings['additional_fields_config'][number]
    >();

    visibleAdditionalOrderFields.forEach((field) => {
      map.set(field.key, field);
    });

    return map;
  }, [visibleAdditionalOrderFields]);

  const getDefaultAdditionalFieldSectionKey = useMemo(
    () =>
      (
        field: ProductionOperationSettings['additional_fields_config'][number],
      ): OperatorProductionReportFormLayoutSectionKey =>
        field.section_key ??
        (field.field_type === 'number' ? 'production' : 'identity'),
    [],
  );

  const additionalOrderFieldsByLayoutSection = useMemo(() => {
    const sections: Record<
      OperatorProductionReportFormLayoutSectionKey,
      ProductionOperationSettings['additional_fields_config']
    > = {
      identity: [],
      production: [],
      additional: [],
      closing: [],
    };

    const usedFieldKeys = new Set<string>();
    const validSectionKeys = new Set<OperatorProductionReportFormLayoutSectionKey>([
      'identity',
      'production',
      'additional',
      'closing',
    ]);

    const layoutSections =
      operationSettings?.production_report_form_layout_config?.sections ?? [];

    layoutSections.forEach((section) => {
      const sectionKey = section.key as OperatorProductionReportFormLayoutSectionKey;

      if (!validSectionKeys.has(sectionKey)) {
        return;
      }

      section.field_refs.forEach((fieldRef) => {
        if (fieldRef.source !== 'additional_field') {
          return;
        }

        const field = additionalOrderFieldByKey.get(fieldRef.key);

        if (!field || usedFieldKeys.has(field.key)) {
          return;
        }

        sections[sectionKey].push(field);
        usedFieldKeys.add(field.key);
      });
    });

    visibleAdditionalOrderFields.forEach((field) => {
      if (usedFieldKeys.has(field.key)) {
        return;
      }

      const sectionKey = getDefaultAdditionalFieldSectionKey(field);
      sections[sectionKey].push(field);
      usedFieldKeys.add(field.key);
    });

    return sections;
  }, [
    additionalOrderFieldByKey,
    getDefaultAdditionalFieldSectionKey,
    operationSettings?.production_report_form_layout_config,
    visibleAdditionalOrderFields,
  ]);

  const orderIdentificationAdditionalFields =
    additionalOrderFieldsByLayoutSection.identity;

  const orderProductionAdditionalFields =
    additionalOrderFieldsByLayoutSection.production;

  const orderGenericAdditionalFields =
    additionalOrderFieldsByLayoutSection.additional;

  const getMissingRequiredCloseFields = useMemo(() => {
    return (form: LineForm) => {
      const missing: string[] = [];

      if (
        isOrderFieldRequiredOnClose('product') &&
        !form.product_id &&
        !form.free_product_ref.trim()
      ) {
        missing.push(getOrderFieldLabel('product', 'Producto'));
      }

      if (
        isOrderFieldRequiredOnClose('customer') &&
        !form.customer_number.trim()
      ) {
        missing.push(getOrderFieldLabel('customer', 'Cliente'));
      }

      if (
        isOrderFieldRequiredOnClose('customer_order') &&
        !form.customer_order_number.trim()
      ) {
        missing.push(getOrderFieldLabel('customer_order', 'Pedido cliente'));
      }

      if (
        isOrderFieldRequiredOnClose('manufacturing_order_number') &&
        !form.manufacturing_order_number.trim()
      ) {
        missing.push(getOrderFieldLabel('manufacturing_order_number', 'OF'));
      }

      if (isOrderFieldRequiredOnClose('planned_quantity') && !form.quantity_value.trim()) {
        missing.push(getOrderFieldLabel('planned_quantity', 'Cantidad orden'));
      }

      if (isOrderFieldRequiredOnClose('actual_quantity') && !form.actual_quantity_value.trim()) {
        missing.push(getOrderFieldLabel('actual_quantity', 'Cantidad real'));
      }

      if (
        isOrderFieldRequiredOnClose('speed') &&
        !form.speed_value.trim()
      ) {
        missing.push(getOrderFieldLabel('speed', 'Velocidad'));
      }

      if (
        isOrderFieldRequiredOnClose('observations') &&
        !form.notes.trim()
      ) {
        missing.push(getOrderFieldLabel('observations', 'Observaciones'));
      }

      return missing;
    };
  }, [getOrderFieldLabel, isOrderFieldRequiredOnClose]);

  const sortedLines = useMemo(
    () => [...(detail?.lines ?? [])].sort((a, b) => a.line_order - b.line_order),
    [detail?.lines],
  );

  const activeLine = useMemo(
    () => sortedLines.find((line) => line.start_time && !line.end_time) ?? null,
    [sortedLines],
  );

  const activeLineElapsedMinutes = useMemo(
    () => calculateElapsedMinutesFromStart(activeLine?.start_time),
    [activeLine?.start_time, clockTick],
  );

  const activeLineProductionElapsedMinutes = useMemo(
    () =>
      calculateElapsedMinutesFromStart(
        normalizeTime(activeLine?.production_start_time) || normalizeTime(activeLine?.start_time),
      ),
    [activeLine?.production_start_time, activeLine?.start_time, clockTick],
  );

  const activeLineEstimation = useMemo(
    () =>
      activeLine
        ? calculateProductionReportLineEstimation(activeLine, operationSettings)
        : null,
    [activeLine, operationSettings],
  );

  const activeLineTimingDeviation = useMemo(
    () =>
      activeLineEstimation?.enabled
        ? calculateActiveLineTimingDeviation(
            activeLineProductionElapsedMinutes,
            activeLineEstimation.estimatedProductionDurationMinutes,
          )
        : null,
    [
      activeLineProductionElapsedMinutes,
      activeLineEstimation?.enabled,
      activeLineEstimation?.estimatedProductionDurationMinutes,
    ],
  );

  const closedLines = useMemo(
    () => sortedLines.filter((line) => line.end_time),
    [sortedLines],
  );

  const lastClosedLine = closedLines.length > 0 ? closedLines[closedLines.length - 1] : null;

  const sortedStops = useMemo(
    () => [...(detail?.stops ?? [])].sort((a, b) => {
      const aStart = normalizeTime(a.start_time);
      const bStart = normalizeTime(b.start_time);
      return aStart.localeCompare(bStart);
    }),
    [detail?.stops],
  );

  const activeStop = useMemo(
    () => sortedStops.find((stop) => stop.start_time && !stop.end_time) ?? null,
    [sortedStops],
  );

  const canContinuePendingLine =
    isDraft &&
    !activeLine &&
    !activeStop &&
    Boolean(bannerContinuationCandidate);

  const closedStops = useMemo(
    () => sortedStops.filter((stop) => stop.end_time),
    [sortedStops],
  );

  const qualityResultByLineControlAndTime = useMemo(() => {
    const map = new Map<string, ProductionReportQualityResult>();

    (detail?.quality_results ?? []).forEach((item) => {
      const lineKey = item.line_id ?? 'report';
      map.set(
        `${lineKey}_${item.quality_control_id}_${normalizeTime(item.time_slot)}`,
        item.result,
      );
    });

    Object.entries(optimisticQualityResults).forEach(([cellKey, result]) => {
      map.set(cellKey, result);
    });

    return map;
  }, [detail?.quality_results, optimisticQualityResults]);

  const qualityResultsByLineId = useMemo(() => {
    const map = new Map<string, ProductionReportQualityResultRow[]>();

    (detail?.quality_results ?? []).forEach((item) => {
      if (!item.line_id) return;

      const existing = map.get(item.line_id) ?? [];
      map.set(item.line_id, [...existing, item]);
    });

    return map;
  }, [detail?.quality_results]);

  const selectedShiftTimeSlots = useMemo(
    () =>
      shiftTimeSlots.filter(
        (slot) => slot.shift === (selectedReport?.shift ?? newReportForm.shift),
      ),
    [shiftTimeSlots, selectedReport?.shift, newReportForm.shift],
  );

  const configuredShiftStartTime = useMemo(
    () => getShiftStartTimeFromSlots(selectedShiftTimeSlots),
    [selectedShiftTimeSlots],
  );

  const configuredShiftEndTime = useMemo(
    () => getShiftEndTimeFromSlots(selectedShiftTimeSlots),
    [selectedShiftTimeSlots],
  );

  const activeQualityLineId = activeLine && !activeStop ? activeLine.id : null;
  const canEditQuality = isDraft && Boolean(activeQualityLineId) && !activeStop;

  const getQualityResultForLine = (
    lineId: string,
    qualityControlId: string,
    timeSlot: string,
  ) =>
    qualityResultByLineControlAndTime.get(
      getQualityCellKey(lineId, qualityControlId, timeSlot),
    );

  const isQualityCellPending = (
    lineId: string,
    qualityControlId: string,
    timeSlot: string,
  ) =>
    pendingQualityCellKeys.has(
      getQualityCellKey(lineId, qualityControlId, timeSlot),
    );

  const getApplicableSlotsForLine = (line: ProductionReportLine) =>
    getApplicableProductionReportQualitySlots({
      slots: selectedShiftTimeSlots,
      lineStartTime: line.start_time,
      lineEndTime: line.end_time,
      shiftStartTime: configuredShiftStartTime,
      shiftEndTime: configuredShiftEndTime,
    });

  const nextPendingQualitySlot = useMemo(() => {
    if (!activeQualityLineId || !activeLine || activeStop) return null;

    const applicableSlots = getApplicableSlotsForLine(activeLine);

    for (const slot of applicableSlots) {
      const time = normalizeTime(slot.time_slot);

      const controls = qualityControls.map((control) => ({
        control,
        result: getQualityResultForLine(activeQualityLineId, control.id, time),
      }));

      const hasPendingControls = controls.some((item) => !item.result);

      if (hasPendingControls) {
        return {
          lineId: activeQualityLineId,
          timeSlot: time,
          controls,
          completedInSlot: controls.filter((item) => item.result).length,
          totalInSlot: controls.length,
        };
      }
    }

    return null;
  }, [
    activeQualityLineId,
    activeLine,
    activeStop,
    selectedShiftTimeSlots,
    configuredShiftStartTime,
    configuredShiftEndTime,
    qualityControls,
    qualityResultByLineControlAndTime,
  ]);

  const activeLineQualitySummary = useMemo(() => {
    if (!activeLine) {
      return {
        totalRequired: 0,
        completed: 0,
        conforming: 0,
        nonConforming: 0,
        skipped: 0,
      };
    }

    const applicableSlots = getApplicableSlotsForLine(activeLine);
    const totalRequired = applicableSlots.length * qualityControls.length;
    const results = applicableSlots.flatMap((slot) => {
      const timeSlot = normalizeTime(slot.time_slot);

      return qualityControls
        .map((control) =>
          getQualityResultForLine(activeLine.id, control.id, timeSlot),
        )
        .filter(
          (result): result is ProductionReportQualityResult => Boolean(result),
        );
    });

    return {
      totalRequired,
      completed: results.length,
      conforming: results.filter((result) => result === 'C').length,
      nonConforming: results.filter((result) => result === 'NC').length,
      skipped: results.filter((result) => result === 'NA').length,
    };
  }, [
    activeLine,
    selectedShiftTimeSlots,
    configuredShiftStartTime,
    configuredShiftEndTime,
    qualityControls,
    qualityResultByLineControlAndTime,
  ]);

  const qualityDialogLine = useMemo(
    () => sortedLines.find((line) => line.id === qualityDialogLineId) ?? null,
    [qualityDialogLineId, sortedLines],
  );

  const buildLineQualityRows = (line: ProductionReportLine) => {
    const applicableSlots = getApplicableSlotsForLine(line);

    return applicableSlots.flatMap((slot: ProductionReportShiftTimeSlot) => {
      const timeSlot = normalizeTime(slot.time_slot);

      return qualityControls.map((control) => {
        const existingRow =
          (qualityResultsByLineId.get(line.id) ?? []).find(
            (item) =>
              item.quality_control_id === control.id &&
              normalizeTime(item.time_slot) === timeSlot,
          ) ?? null;

        return {
          key: `${line.id}_${timeSlot}_${control.id}`,
          lineId: line.id,
          timeSlot,
          control,
          resultRow: existingRow,
          result: getQualityResultForLine(line.id, control.id, timeSlot) ?? null,
        };
      });
    });
  };

  const saveQualityResultOptimistically = (
    lineId: string,
    qualityControlId: string,
    timeSlot: string,
    result: ProductionReportQualityResult,
  ) => {
    const normalizedTimeSlot = normalizeTime(timeSlot);
    const cellKey = getQualityCellKey(
      lineId,
      qualityControlId,
      normalizedTimeSlot,
    );

    if (pendingQualityCellKeysRef.current.has(cellKey)) return;

    pendingQualityCellKeysRef.current.add(cellKey);
    setPendingQualityCellKeys(new Set(pendingQualityCellKeysRef.current));
    setOptimisticQualityResults((current) => ({
      ...current,
      [cellKey]: result,
    }));

    upsertQualityMutation.mutate({
      quality_control_id: qualityControlId,
      line_id: lineId,
      time_slot: normalizedTimeSlot,
      result,
      cell_key: cellKey,
    });
  };

  const handleSetQualityResultForLine = (
    lineId: string,
    qualityControlId: string,
    timeSlot: string,
    result: ProductionReportQualityResult,
  ) => {
    if (!isDraft) return;

    saveQualityResultOptimistically(
      lineId,
      qualityControlId,
      timeSlot,
      result,
    );
  };

  const getLineDisplayName = (line: ProductionReportLine) => {
    if (line.product_id) {
      const product = productById.get(line.product_id);

      if (product) return `${product.code} · ${product.name}`;
    }

    if (line.free_product_ref) return line.free_product_ref;
    if (line.manufacturing_order_number) return `OF ${line.manufacturing_order_number}`;

    return 'Producto sin referencia';
  };

  const getStopLineDisplayName = (lineId: string | null | undefined) => {
    if (!lineId) return 'Parte completo';

    const line = sortedLines.find((item) => item.id === lineId);

    if (!line) return 'Orden no encontrada';

    return getLineDisplayName(line);
  };
  

  const currentLineDuration = calculateDurationMinutes(lineForm.start_time, lineForm.end_time);
  const currentLineSetupDuration = calculateSetupDurationMinutes(
    lineForm.start_time,
    lineForm.production_start_time,
  );
  const currentLineProductionDuration = calculateRealProductionDurationMinutes(
    getEffectiveProductionStartTime(lineForm.start_time, lineForm.production_start_time),
    lineForm.end_time,
  );
  const currentLineEstimation = useMemo(
    () => calculateLineFormProductionEstimation(lineForm, operationSettings),
    [lineForm, operationSettings],
  );
  const currentStopDuration = calculateDurationMinutes(stopForm.start_time, stopForm.end_time);

  const sortedIdlePeriods = useMemo(
    () =>
      [...(detail?.idle_periods ?? [])].sort((a, b) => {
        const aStart = getMinutesFromShiftStart(a.start_time, configuredShiftStartTime);
        const bStart = getMinutesFromShiftStart(b.start_time, configuredShiftStartTime);
        return aStart - bStart;
      }),
    [detail?.idle_periods, configuredShiftStartTime],
  );

  const shiftCoverageEvents = useMemo(() => {
    const lineEvents = sortedLines
      .filter((line) => line.start_time)
      .map((line) => ({
        kind: 'line' as const,
        id: line.id,
        start_time: normalizeTime(line.start_time),
        end_time: normalizeTime(line.end_time),
        duration_minutes: line.duration_minutes ?? calculateDurationMinutes(
          normalizeTime(line.start_time),
          normalizeTime(line.end_time),
        ),
        title: getLineDisplayName(line),
        helper: [
          `OF ${line.manufacturing_order_number || '—'}`,
          line.actual_quantity_value !== null &&
          line.actual_quantity_value !== undefined
            ? `Turno: ${formatProductionQuantity(line.actual_quantity_value)} ${
                line.quantity_unit || ''
              }`.trimEnd()
            : null,
          line.actual_quantity_accumulated !== null &&
          line.actual_quantity_accumulated !== undefined &&
          (line.of_quantity_value ?? line.quantity_value) !== null &&
          (line.of_quantity_value ?? line.quantity_value) !== undefined
            ? `OF: ${formatProductionQuantity(
                line.actual_quantity_accumulated,
              )}/${formatProductionQuantity(
                line.of_quantity_value ?? line.quantity_value,
              )} ${line.quantity_unit || ''}`.trimEnd()
            : null,
        ]
          .filter((item): item is string => Boolean(item))
          .join(' · '),
        entity: line,
      }));

    const stopEvents = sortedStops
      .filter((stop) => stop.start_time)
      .map((stop) => ({
        kind: 'stop' as const,
        id: stop.id,
        start_time: normalizeTime(stop.start_time),
        end_time: normalizeTime(stop.end_time),
        duration_minutes: stop.duration_minutes ?? calculateDurationMinutes(
          normalizeTime(stop.start_time),
          normalizeTime(stop.end_time),
        ),
        title: stop.reason_label || 'Parada',
        helper: getStopLineDisplayName(stop.line_id),
        entity: stop,
      }));

    const idleEvents = sortedIdlePeriods
      .filter((idlePeriod) => idlePeriod.start_time)
      .map((idlePeriod) => ({
        kind: 'idle' as const,
        id: idlePeriod.id,
        start_time: normalizeTime(idlePeriod.start_time),
        end_time: normalizeTime(idlePeriod.end_time),
        duration_minutes: idlePeriod.duration_minutes ?? calculateDurationMinutes(
          normalizeTime(idlePeriod.start_time),
          normalizeTime(idlePeriod.end_time),
        ),
        title: 'Sin orden planificada',
        helper: idlePeriod.notes || 'Hueco de turno cubierto para validación',
        entity: idlePeriod,
      }));

    return [...lineEvents, ...stopEvents, ...idleEvents].sort((a, b) => {
      const aStart = getMinutesFromShiftStart(a.start_time, configuredShiftStartTime);
      const bStart = getMinutesFromShiftStart(b.start_time, configuredShiftStartTime);

      if (aStart !== bStart) return aStart - bStart;

      return a.kind.localeCompare(b.kind);
    });
  }, [
    sortedLines,
    sortedStops,
    sortedIdlePeriods,
    configuredShiftStartTime,
    getStopLineDisplayName,
  ]);

  const getProductionStartTimeValidationMessage = (nextProductionStartTime: string) => {
    const normalizedProductionStartTime = normalizeTime(nextProductionStartTime);
    const normalizedStartTime = normalizeTime(lineForm.start_time);
    const normalizedEndTime = normalizeTime(lineForm.end_time);

    if (!normalizedProductionStartTime) {
      return 'Debes informar la hora de inicio de fabricación.';
    }

    if (!normalizedStartTime) {
      return 'Debes informar primero la hora de inicio de la orden.';
    }

    const shiftValidationMessage = getShiftTimeValidationMessage(
      normalizedProductionStartTime,
      'La hora de inicio de fabricación',
      configuredShiftStartTime,
      configuredShiftEndTime,
    );

    if (shiftValidationMessage) {
      return shiftValidationMessage;
    }

    if (
      !isEndTimeSameOrAfterStartTimeWithinShift(
        normalizedStartTime,
        normalizedProductionStartTime,
        configuredShiftStartTime,
        configuredShiftEndTime,
      )
    ) {
      return 'La hora de inicio de fabricación debe ser igual o posterior al inicio de la orden.';
    }

    if (
      normalizedEndTime &&
      !isEndTimeSameOrAfterStartTimeWithinShift(
        normalizedProductionStartTime,
        normalizedEndTime,
        configuredShiftStartTime,
        configuredShiftEndTime,
      )
    ) {
      return 'La hora de inicio de fabricación no puede ser posterior a la hora de fin.';
    }

    return '';
  };

  const applyProductionStartTime = (nextProductionStartTime: string) => {
    const normalizedProductionStartTime = normalizeTime(nextProductionStartTime);
    const validationMessage = getProductionStartTimeValidationMessage(
      normalizedProductionStartTime,
    );

    if (validationMessage) {
      enqueueSnackbar(validationMessage, { variant: 'warning' });
      return;
    }

    setLineForm((current) => ({
      ...current,
      production_start_time: normalizedProductionStartTime,
    }));
  };

  const currentProductionStartTime = getEffectiveProductionStartTime(
    lineForm.start_time,
    lineForm.production_start_time,
  );

  const currentProductionStartTimeValidationMessage = currentProductionStartTime
    ? getProductionStartTimeValidationMessage(currentProductionStartTime)
    : '';

  const refreshReports = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['industrial-cost-production-reports'],
    });
    await queryClient.invalidateQueries({
      queryKey: ['industrial-cost-production-report-operator-context'],
    });
  };

  const refreshDetail = async () => {
    if (!selectedReportId) return;

    await queryClient.invalidateQueries({
      queryKey: ['industrial-cost-production-report-detail', selectedReportId],
      refetchType: 'all',
    });

    await queryClient.invalidateQueries({
      queryKey: ['industrial-cost-production-existing-orders-candidates'],
      refetchType: 'all',
    });
  };

  const acquireStopWriteLock = () => {
    if (stopWriteLockRef.current) return false;

    stopWriteLockRef.current = true;
    setStopWriteLocked(true);
    return true;
  };

  const releaseStopWriteLock = () => {
    stopWriteLockRef.current = false;
    setStopWriteLocked(false);
  };

  const acquireIdlePeriodWriteLock = () => {
    if (idlePeriodWriteLockRef.current) return false;

    idlePeriodWriteLockRef.current = true;
    setIdlePeriodWriteLocked(true);
    return true;
  };

  const releaseIdlePeriodWriteLock = () => {
    idlePeriodWriteLockRef.current = false;
    setIdlePeriodWriteLocked(false);
  };

  const acquireReportWriteLock = () => {
    if (reportWriteLockRef.current) return false;

    reportWriteLockRef.current = true;
    setReportWriteLocked(true);
    return true;
  };

  const releaseReportWriteLock = () => {
    reportWriteLockRef.current = false;
    setReportWriteLocked(false);
  };


  const acquireLineWriteLock = () => {
    if (lineWriteLockRef.current) return false;

    lineWriteLockRef.current = true;
    setLineWriteLocked(true);
    return true;
  };

  const releaseLineWriteLock = () => {
    lineWriteLockRef.current = false;
    setLineWriteLocked(false);
  };

  const createReportMutation = useMutation({
    mutationFn: (payload: CreateProductionReportPayload) => {
      if (!acquireReportWriteLock()) {
        throw new Error('Ya hay una creación de parte en curso.');
      }

      return createIndustrialCostProductionReport(payload);
    },
    onSuccess: async (report) => {
      enqueueSnackbar('Parte creado correctamente.', { variant: 'success' });
      setSelectedReportId(report.id);
      setScreenMode('detail');
      setCreateDialogOpen(false);
      setNewReportForm(buildEmptyNewReportForm());
      await refreshReports();
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
    onSettled: () => {
      releaseReportWriteLock();
    },
  });

  const createLineMutation = useMutation({
    mutationFn: (payload: CreateProductionReportLinePayload) => {
      if (!selectedReportId) throw new Error('No hay parte seleccionado.');
      if (!acquireLineWriteLock()) {
        throw new Error('Ya hay una operación de orden en curso.');
      }

      return createIndustrialCostProductionReportLine(selectedReportId, payload);
    },
    onSuccess: async () => {
      enqueueSnackbar('Línea añadida correctamente.', { variant: 'success' });
      setLineForm(buildEmptyLineForm());
      setEditingLineId(null);
      setLineDialogOpen(false);
      await refreshDetail();
      await refreshReports();
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
    onSettled: () => {
      releaseLineWriteLock();
    },
  });

  const updateLineMutation = useMutation({
    mutationFn: (payload: { lineId: string; data: CreateProductionReportLinePayload }) => {
      if (!selectedReportId) throw new Error('No hay parte seleccionado.');
      if (!acquireLineWriteLock()) {
        throw new Error('Ya hay una operación de orden en curso.');
      }

      return updateIndustrialCostProductionReportLine(
        selectedReportId,
        payload.lineId,
        payload.data,
      );
    },
    onSuccess: async (_data, payload) => {
      enqueueSnackbar('Línea actualizada correctamente.', { variant: 'success' });
      closeFinishLineDialog();
      setLineForm(buildEmptyLineForm());
      setEditingLineId(null);
      setLineDialogOpen(false);
      await refreshDetail();

      if (pendingTimelineResolution || pendingIncompleteEventResolution) {
        setTimelineValidationIssue((current) => {
          if (!current) return current;

          const resolution = pendingTimelineResolution;

          if (
            resolution?.kind === 'gap_extend_previous_end' &&
            payload.data.end_time === resolution.expected_end_time
          ) {
            const remainingGaps = current.gaps.filter(
              (gap) =>
                !(
                  gap.start_time === resolution.start_time && gap.end_time === resolution.end_time
                ),
            );

            const nextIssue: TimelineValidationIssue = {
              ...current,
              gaps: remainingGaps,
            };

            const hasRemainingIssues =
              nextIssue.incomplete_events.length > 0 ||
              nextIssue.gaps.length > 0 ||
              nextIssue.overlaps.length > 0;

            if (!hasRemainingIssues) {
              setTimelineValidationDialogOpen(false);
              return null;
            }

            return nextIssue;
          }

          if (
            resolution?.kind === 'gap_move_next_start' &&
            payload.data.start_time === resolution.expected_start_time
          ) {
            const remainingGaps = current.gaps.filter(
              (gap) =>
                !(
                  gap.start_time === resolution.start_time && gap.end_time === resolution.end_time
                ),
            );

            const nextIssue: TimelineValidationIssue = {
              ...current,
              gaps: remainingGaps,
            };

            const hasRemainingIssues =
              nextIssue.incomplete_events.length > 0 ||
              nextIssue.gaps.length > 0 ||
              nextIssue.overlaps.length > 0;

            if (!hasRemainingIssues) {
              setTimelineValidationDialogOpen(false);
              return null;
            }

            return nextIssue;
          }

          if (
            resolution?.kind === 'overlap_adjust_previous_end' &&
            payload.data.end_time === resolution.expected_end_time
          ) {
            const remainingOverlaps = current.overlaps.filter(
              (overlap) =>
                !(
                  overlap.start_time === resolution.start_time &&
                  overlap.end_time === resolution.end_time
                ),
            );

            const nextIssue: TimelineValidationIssue = {
              ...current,
              overlaps: remainingOverlaps,
            };

            const hasRemainingIssues =
              nextIssue.incomplete_events.length > 0 ||
              nextIssue.gaps.length > 0 ||
              nextIssue.overlaps.length > 0;

            if (!hasRemainingIssues) {
              setTimelineValidationDialogOpen(false);
              return null;
            }

            return nextIssue;
          }

          if (
            resolution?.kind === 'overlap_adjust_next_start' &&
            payload.data.start_time === resolution.expected_start_time
          ) {
            const remainingOverlaps = current.overlaps.filter(
              (overlap) =>
                !(
                  overlap.start_time === resolution.start_time &&
                  overlap.end_time === resolution.end_time
                ),
            );

            const nextIssue: TimelineValidationIssue = {
              ...current,
              overlaps: remainingOverlaps,
            };

            const hasRemainingIssues =
              nextIssue.incomplete_events.length > 0 ||
              nextIssue.gaps.length > 0 ||
              nextIssue.overlaps.length > 0;

            if (!hasRemainingIssues) {
              setTimelineValidationDialogOpen(false);
              return null;
            }

            return nextIssue;
          }

          if (
            pendingIncompleteEventResolution?.event_type === 'line' &&
            pendingIncompleteEventResolution.event_id === payload.lineId &&
            payload.data.end_time
          ) {
            const remainingIncompleteEvents = current.incomplete_events.filter(
              (event) =>
                !(
                  event.event_type === 'line' &&
                  event.event_id === pendingIncompleteEventResolution.event_id
                ),
            );

            const nextIssue: TimelineValidationIssue = {
              ...current,
              incomplete_events: remainingIncompleteEvents,
            };

            const hasRemainingIssues =
              nextIssue.incomplete_events.length > 0 ||
              nextIssue.gaps.length > 0 ||
              nextIssue.overlaps.length > 0;

            if (!hasRemainingIssues) {
              setTimelineValidationDialogOpen(false);
              return null;
            }

            return nextIssue;
          }

          return current;
        });
      }

      setPendingTimelineResolution(null);
      setPendingIncompleteEventResolution(null);

      if (nextLineStartAfterFinish) {
        openCreateLineDialog(nextLineStartAfterFinish);
        setNextLineStartAfterFinish(null);
      }
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
    onSettled: () => {
      releaseLineWriteLock();
    },
  });

  const continuePendingLineMutation = useMutation({
    mutationFn: async () => {
      if (!selectedReportId) throw new Error('No hay parte seleccionado.');
      if (!bannerContinuationCandidate) {
        throw new Error('No hay orden pendiente para continuar.');
      }
      if (!acquireLineWriteLock()) {
        throw new Error('Ya hay una operación de orden en curso.');
      }

      const continuationStartTime =
        normalizeTime(lastClosedLine?.end_time) ||
        normalizeTime(bannerContinuationCandidate.source_end_time) ||
        getCurrentTimeValue() ||
        '';

      const continuedLineForm: LineForm = {
        product_id: bannerContinuationCandidate.product_id || '',
        free_product_ref: bannerContinuationCandidate.product_label || '',
        customer_number: bannerContinuationCandidate.customer_number || '',
        customer_order_number: bannerContinuationCandidate.customer_order_number || '',
        manufacturing_order_number:
          bannerContinuationCandidate.manufacturing_order_number || '',
        quantity_value:
          bannerContinuationCandidate.quantity_value === null ||
          bannerContinuationCandidate.quantity_value === undefined
            ? ''
            : String(bannerContinuationCandidate.quantity_value),
        actual_quantity_value: '',
        quantity_unit: bannerContinuationCandidate.quantity_unit || '',
        speed_value:
          bannerContinuationCandidate.speed_value === null ||
          bannerContinuationCandidate.speed_value === undefined
            ? ''
            : String(bannerContinuationCandidate.speed_value),
        start_time: continuationStartTime,
        production_start_time: continuationStartTime,
        end_time: '',
        material_usages: [],
        notes: bannerContinuationCandidate.notes || '',
        extra_fields: {
          ...Object.fromEntries(
            Object.entries(bannerContinuationCandidate.extra_fields ?? {}).map(([key, value]) => [
              key,
              value === null || value === undefined ? '' : String(value),
            ]),
          ),
          [PENDING_QUANTITY_EXTRA_FIELD_KEY]:
            bannerContinuationCandidate.remaining_quantity_value === null ||
            bannerContinuationCandidate.remaining_quantity_value === undefined
              ? ''
              : String(bannerContinuationCandidate.remaining_quantity_value),
        },
      };

      const nextOrder = (detail?.lines?.length ?? 0) + 1;

      const createPayload: CreateProductionReportLinePayload = {
        ...buildLinePayload(
          continuedLineForm,
          nextOrder,
          operationSettings?.additional_fields_config ?? [],
        ),
        continuation_status: 'none',
        continued_from_line_id: bannerContinuationCandidate.line_id,
      };

      await createIndustrialCostProductionReportLine(selectedReportId, createPayload);
    },
    onSuccess: async () => {
      enqueueSnackbar('Orden continuada correctamente.', { variant: 'success' });
      await refreshDetail();
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
    onSettled: () => {
      releaseLineWriteLock();
    },
  });

  const deleteLineMutation = useMutation({
    mutationFn: (lineId: string) => {
      if (!selectedReportId) throw new Error('No hay parte seleccionado.');
      return deleteIndustrialCostProductionReportLine(selectedReportId, lineId);
    },
    onSuccess: async () => {
      enqueueSnackbar('Orden eliminada correctamente.', { variant: 'success' });
      setLineToDelete(null);
      await refreshDetail();
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
  });

  const createStopMutation = useMutation({
    mutationFn: (payload: CreateProductionReportStopPayload) => {
      if (!selectedReportId) throw new Error('No hay parte seleccionado.');
      return createIndustrialCostProductionReportStop(selectedReportId, payload);
    },
    onSuccess: async (_data, payload) => {
      enqueueSnackbar('Parada registrada correctamente.', { variant: 'success' });
      setStopForm(buildEmptyStopForm());
      setEditingStopId(null);
      setStopDialogOpen(false);
      await refreshDetail();

      if (
        pendingTimelineResolution?.kind === 'gap_create_stop' &&
        payload.start_time === pendingTimelineResolution.start_time &&
        payload.end_time === pendingTimelineResolution.end_time
      ) {
        setTimelineValidationIssue((current) => {
          if (!current) return current;

          const remainingGaps = current.gaps.filter(
            (gap) =>
              !(
                gap.start_time === pendingTimelineResolution.start_time &&
                gap.end_time === pendingTimelineResolution.end_time
              ),
          );

          const nextIssue: TimelineValidationIssue = {
            ...current,
            gaps: remainingGaps,
          };

          const hasRemainingIssues =
            nextIssue.incomplete_events.length > 0 ||
            nextIssue.gaps.length > 0 ||
            nextIssue.overlaps.length > 0;

          if (!hasRemainingIssues) {
            setTimelineValidationDialogOpen(false);
            return null;
          }

          return nextIssue;
        });
      }

      setPendingTimelineResolution(null);
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
    onSettled: () => {
      releaseStopWriteLock();
    },
  });

  const createIdlePeriodMutation = useMutation({
    mutationFn: (payload: CreateProductionReportIdlePeriodPayload) => {
      if (!selectedReportId) throw new Error('No hay parte seleccionado.');
      return createIndustrialCostProductionReportIdlePeriod(selectedReportId, payload);
    },
    onSuccess: async (_data, payload) => {
      enqueueSnackbar('Hueco marcado como sin orden planificada.', { variant: 'success' });
      await refreshDetail();

      if (
        pendingTimelineResolution?.kind === 'gap_create_idle_period' &&
        payload.start_time === pendingTimelineResolution.start_time &&
        payload.end_time === pendingTimelineResolution.end_time
      ) {
        setTimelineValidationIssue((current) => {
          if (!current) return current;

          const remainingGaps = current.gaps.filter(
            (gap) =>
              !(
                gap.start_time === pendingTimelineResolution.start_time &&
                gap.end_time === pendingTimelineResolution.end_time
              ),
          );

          const nextIssue: TimelineValidationIssue = {
            ...current,
            gaps: remainingGaps,
          };

          const hasRemainingIssues =
            nextIssue.incomplete_events.length > 0 ||
            nextIssue.gaps.length > 0 ||
            nextIssue.overlaps.length > 0;

          if (!hasRemainingIssues) {
            setTimelineValidationDialogOpen(false);
            return null;
          }

          return nextIssue;
        });
      }

      setPendingTimelineResolution(null);
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
    onSettled: () => {
      releaseIdlePeriodWriteLock();
    },
  });

  const updateIdlePeriodMutation = useMutation({
    mutationFn: (payload: { idlePeriodId: string; data: UpdateProductionReportIdlePeriodPayload }) => {
      if (!selectedReportId) throw new Error('No hay parte seleccionado.');
      return updateIndustrialCostProductionReportIdlePeriod(
        selectedReportId,
        payload.idlePeriodId,
        payload.data,
      );
    },
    onSuccess: async () => {
      enqueueSnackbar('Periodo sin orden planificada actualizado correctamente.', {
        variant: 'success',
      });
      setIdlePeriodForm(buildEmptyIdlePeriodForm());
      setEditingIdlePeriodId(null);
      setIdlePeriodDialogOpen(false);
      await refreshDetail();
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
    onSettled: () => {
      releaseIdlePeriodWriteLock();
    },
  });

  const deleteIdlePeriodMutation = useMutation({
    mutationFn: (idlePeriodId: string) => {
      if (!selectedReportId) throw new Error('No hay parte seleccionado.');
      return deleteIndustrialCostProductionReportIdlePeriod(selectedReportId, idlePeriodId);
    },
    onSuccess: async () => {
      enqueueSnackbar('Periodo sin orden planificada eliminado correctamente.', {
        variant: 'success',
      });
      setIdlePeriodToDelete(null);
      await refreshDetail();
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
    onSettled: () => {
      releaseIdlePeriodWriteLock();
    },
  });

  const updateStopMutation = useMutation({
    mutationFn: (payload: { stopId: string; data: CreateProductionReportStopPayload }) => {
      if (!selectedReportId) throw new Error('No hay parte seleccionado.');
      return updateIndustrialCostProductionReportStop(
        selectedReportId,
        payload.stopId,
        payload.data,
      );
    },
    onSuccess: async (_data, payload) => {
      enqueueSnackbar('Parada actualizada correctamente.', { variant: 'success' });
      setStopForm(buildEmptyStopForm());
      setEditingStopId(null);
      setStopDialogOpen(false);
      await refreshDetail();

      if (pendingTimelineResolution || pendingIncompleteEventResolution) {
        setTimelineValidationIssue((current) => {
          if (!current) return current;

          const resolution = pendingTimelineResolution;

          if (
            resolution?.kind === 'gap_extend_previous_end' &&
            payload.data.end_time === resolution.expected_end_time
          ) {
            const remainingGaps = current.gaps.filter(
              (gap) =>
                !(
                  gap.start_time === resolution.start_time && gap.end_time === resolution.end_time
                ),
            );

            const nextIssue: TimelineValidationIssue = {
              ...current,
              gaps: remainingGaps,
            };

            const hasRemainingIssues =
              nextIssue.incomplete_events.length > 0 ||
              nextIssue.gaps.length > 0 ||
              nextIssue.overlaps.length > 0;

            if (!hasRemainingIssues) {
              setTimelineValidationDialogOpen(false);
              return null;
            }

            return nextIssue;
          }

          if (
            resolution?.kind === 'gap_move_next_start' &&
            payload.data.start_time === resolution.expected_start_time
          ) {
            const remainingGaps = current.gaps.filter(
              (gap) =>
                !(
                  gap.start_time === resolution.start_time && gap.end_time === resolution.end_time
                ),
            );

            const nextIssue: TimelineValidationIssue = {
              ...current,
              gaps: remainingGaps,
            };

            const hasRemainingIssues =
              nextIssue.incomplete_events.length > 0 ||
              nextIssue.gaps.length > 0 ||
              nextIssue.overlaps.length > 0;

            if (!hasRemainingIssues) {
              setTimelineValidationDialogOpen(false);
              return null;
            }

            return nextIssue;
          }

          if (
            resolution?.kind === 'overlap_adjust_previous_end' &&
            payload.data.end_time === resolution.expected_end_time
          ) {
            const remainingOverlaps = current.overlaps.filter(
              (overlap) =>
                !(
                  overlap.start_time === resolution.start_time &&
                  overlap.end_time === resolution.end_time
                ),
            );

            const nextIssue: TimelineValidationIssue = {
              ...current,
              overlaps: remainingOverlaps,
            };

            const hasRemainingIssues =
              nextIssue.incomplete_events.length > 0 ||
              nextIssue.gaps.length > 0 ||
              nextIssue.overlaps.length > 0;

            if (!hasRemainingIssues) {
              setTimelineValidationDialogOpen(false);
              return null;
            }

            return nextIssue;
          }

          if (
            resolution?.kind === 'overlap_adjust_next_start' &&
            payload.data.start_time === resolution.expected_start_time
          ) {
            const remainingOverlaps = current.overlaps.filter(
              (overlap) =>
                !(
                  overlap.start_time === resolution.start_time &&
                  overlap.end_time === resolution.end_time
                ),
            );

            const nextIssue: TimelineValidationIssue = {
              ...current,
              overlaps: remainingOverlaps,
            };

            const hasRemainingIssues =
              nextIssue.incomplete_events.length > 0 ||
              nextIssue.gaps.length > 0 ||
              nextIssue.overlaps.length > 0;

            if (!hasRemainingIssues) {
              setTimelineValidationDialogOpen(false);
              return null;
            }

            return nextIssue;
          }

          if (
            pendingIncompleteEventResolution?.event_type === 'stop' &&
            pendingIncompleteEventResolution.event_id === payload.stopId &&
            payload.data.end_time
          ) {
            const remainingIncompleteEvents = current.incomplete_events.filter(
              (event) =>
                !(
                  event.event_type === 'stop' &&
                  event.event_id === pendingIncompleteEventResolution.event_id
                ),
            );

            const nextIssue: TimelineValidationIssue = {
              ...current,
              incomplete_events: remainingIncompleteEvents,
            };

            const hasRemainingIssues =
              nextIssue.incomplete_events.length > 0 ||
              nextIssue.gaps.length > 0 ||
              nextIssue.overlaps.length > 0;

            if (!hasRemainingIssues) {
              setTimelineValidationDialogOpen(false);
              return null;
            }

            return nextIssue;
          }

          return current;
        });
      }

      setPendingTimelineResolution(null);
      setPendingIncompleteEventResolution(null);
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
    onSettled: () => {
      releaseStopWriteLock();
    },
  });

  const deleteStopMutation = useMutation({
    mutationFn: (stopId: string) => {
      if (!selectedReportId) throw new Error('No hay parte seleccionado.');
      return deleteIndustrialCostProductionReportStop(selectedReportId, stopId);
    },
    onSuccess: async () => {
      enqueueSnackbar('Parada eliminada correctamente.', { variant: 'success' });
      setStopToDelete(null);
      await refreshDetail();
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
  });

  const upsertQualityMutation = useMutation({
    mutationFn: (payload: {
      quality_control_id: string;
      line_id: string;
      time_slot: string;
      result: ProductionReportQualityResult;
      cell_key: string;
    }) => {
      if (!selectedReportId) throw new Error('No hay parte seleccionado.');
      return upsertIndustrialCostProductionReportQualityResult(selectedReportId, {
        quality_control_id: payload.quality_control_id,
        line_id: payload.line_id,
        time_slot: payload.time_slot,
        result: payload.result,
        notes: null,
      });
    },
    onError: (error, payload) => {
      setOptimisticQualityResults((current) => {
        const next = { ...current };
        delete next[payload.cell_key];
        return next;
      });

      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
    onSettled: async (_data, _error, payload) => {
      try {
        await refreshDetail();
      } finally {
        pendingQualityCellKeysRef.current.delete(payload.cell_key);
        setPendingQualityCellKeys(new Set(pendingQualityCellKeysRef.current));

        setOptimisticQualityResults((current) => {
          const next = { ...current };
          delete next[payload.cell_key];
          return next;
        });
      }
    },
  });

  const handleSaveQualityControlForPendingSlot = (
    qualityControlId: string,
    result: ProductionReportQualityResult,
  ) => {
    if (!nextPendingQualitySlot || !canEditQuality) {
      return;
    }

    saveQualityResultOptimistically(
      nextPendingQualitySlot.lineId,
      qualityControlId,
      nextPendingQualitySlot.timeSlot,
      result,
    );
  };

  const validateReportMutation = useMutation({
    mutationFn: () => {
      if (!canValidateProductionReports) {
        throw new Error('No tienes permisos para validar partes.');
      }

      if (!selectedReportId) throw new Error('No hay parte seleccionado.');
      return validateIndustrialCostProductionReport(selectedReportId);
    },
    onSuccess: async () => {
      enqueueSnackbar('Parte validado correctamente.', { variant: 'success' });
      setTimelineValidationIssue(null);
      setTimelineValidationDialogOpen(false);
      setListFilters((current) => ({
        ...current,
        report_date: selectedReport?.report_date || current.report_date,
      }));
      setScreenMode('list');
      setSelectedReportId(null);
      setLineForm(buildEmptyLineForm());
      setStopForm(buildEmptyStopForm());
      setNewReportForm(buildEmptyNewReportForm());
      await refreshReports();
    },
    onError: (error) => {
      const issue = getTimelineValidationIssue(error);

      if (issue) {
        setTimelineValidationIssue(issue);
        setTimelineValidationDialogOpen(true);
        return;
      }

      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
  });

  const updateOperatorNumberMutation = useMutation({
    mutationFn: () => {
      if (!selectedReportId) {
        throw new Error('No hay parte seleccionado.');
      }

      const operatorNumber = operatorNumberDraft.trim();

      if (!operatorNumber) {
        throw new Error('Debes informar el número de operario.');
      }

      return updateIndustrialCostProductionReportOperatorNumber(
        selectedReportId,
        {
          operator_number: operatorNumber,
        },
      );
    },
    onSuccess: async () => {
      enqueueSnackbar('Número de operario corregido.', {
        variant: 'success',
      });
      setOperatorNumberDialogOpen(false);
      setOperatorNumberDraft('');
      await refreshReports();
      await refreshDetail();
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    },
  });

  const reopenReportMutation = useMutation({
    mutationFn: () => {
      if (!canValidateProductionReports) {
        throw new Error('No tienes permisos para reabrir partes.');
      }

      if (!selectedReportId) throw new Error('No hay parte seleccionado.');
      return reopenIndustrialCostProductionReport(selectedReportId, {
        notes: reopenNotes.trim(),
      });
    },
    onSuccess: async () => {
      enqueueSnackbar('Parte reabierto para corrección.', { variant: 'success' });
      setReopenDialogOpen(false);
      setReopenNotes('');
      await refreshReports();
      await refreshDetail();
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
  });

  const handleOpenReport = (report: ProductionReport) => {
    setQualityDialogLineId(null);
    setPlantNoticesDialogOpen(false);
    setSelectedReportId(report.id);
    setScreenMode('detail');
    setTimelineValidationDialogOpen(false);
    setTimelineValidationIssue(null);
    setLineForm(buildEmptyLineForm());
    setStopForm(buildEmptyStopForm());
  };

  const handleBackToList = () => {
    setQualityDialogLineId(null);
    setPlantNoticesDialogOpen(false);
    setScreenMode('list');
    setSelectedReportId(null);
    setTimelineValidationDialogOpen(false);
    setTimelineValidationIssue(null);
    setLineForm(buildEmptyLineForm());
    setStopForm(buildEmptyStopForm());
  };

  const openCreateReportDialog = (
    defaultProductionLineConfigId?: string | null,
    defaultShift?: string | null,
    defaultReportDate?: string | null,
  ) => {
    const explicitDefaultLine = defaultProductionLineConfigId
      ? productionLinesConfig.find((line) => line.id === defaultProductionLineConfigId)
      : null;

    const filteredDefaultLine =
      !explicitDefaultLine && listFilters.production_line_config_id !== 'all'
        ? productionLinesConfig.find(
            (line) => line.id === listFilters.production_line_config_id,
          )
        : null;

    const onlyAvailableLine =
      !explicitDefaultLine &&
      !filteredDefaultLine &&
      productionLinesConfig.length === 1
        ? productionLinesConfig[0]
        : null;

    const defaultLine = explicitDefaultLine ?? filteredDefaultLine ?? onlyAvailableLine;

    setNewReportForm({
      ...buildEmptyNewReportForm(),
      report_date: defaultReportDate || getTodayIsoDate(),
      shift: defaultShift ?? '',
      production_line_config_id: defaultLine?.id ?? '',
      line_or_center: defaultLine
        ? `${defaultLine.code} · ${defaultLine.name}`
        : '',
    });
    setCreateDialogOpen(true);
  };

  const handleCreateReport = () => {
    const templateId = newReportForm.template_id || templates[0]?.id || '';

    if (!templateId) {
      enqueueSnackbar('No hay plantilla activa para crear partes.', { variant: 'error' });
      return;
    }

    if (!newReportForm.operator_number.trim()) {
      enqueueSnackbar('Debes informar el número de operario.', { variant: 'error' });
      return;
    }

    if (!newReportForm.shift) {
      enqueueSnackbar('Debes seleccionar el turno.', { variant: 'error' });
      return;
    }

    if (!newReportForm.production_line_config_id) {
      enqueueSnackbar('Debes seleccionar una línea o puesto.', { variant: 'error' });
      return;
    }

    const todayIsoDate = getTodayIsoDate();

    if (
      newReportForm.report_date !== todayIsoDate &&
      !window.confirm(
        `Atención: estás creando un parte para ${newReportForm.report_date}, no para hoy (${todayIsoDate}). ¿Quieres continuar?`,
      )
    ) {
      return;
    }

    const selectedLineConfig = productionLinesConfig.find(
      (line) => line.id === newReportForm.production_line_config_id,
    );

    createReportMutation.mutate({
      template_id: templateId,
      report_date: newReportForm.report_date,
      operator_number: newReportForm.operator_number.trim(),
      shift: newReportForm.shift,
      production_line_config_id: newReportForm.production_line_config_id,
      line_or_center: selectedLineConfig?.name ?? null,
      notes: newReportForm.notes.trim() || null,
    });
  };

  const openCreateLineDialog = (initialStartTime?: string | null) => {
    if (activeStop) {
      enqueueSnackbar('Finaliza la parada abierta antes de iniciar una nueva orden.', {
        variant: 'warning',
      });
      return;
    }

    const isFirstLineOfReport = (detail?.lines?.length ?? 0) === 0;

    setEditingLineId(null);
    const nextStartTime =
      initialStartTime ??
      (isFirstLineOfReport
        ? configuredShiftStartTime || getCurrentTimeValue()
        : getCurrentTimeValue());

    setLineForm({
      ...buildEmptyLineForm(),
      start_time: nextStartTime,
      production_start_time: nextStartTime,
    });
    setResolvedExistingOrderNumber('');
    setResolvedExistingOrderSummary(null);
    setLineDefaultsSuggested(false);
    setExistingOrderSearchText('');
    setLineDialogOpen(true);
  };

  const buildLineFormFromLine = (line: ProductionReportLine): LineForm => ({
    product_id: line.product_id ?? '',
    free_product_ref: line.free_product_ref ?? '',
    customer_number: line.customer_number ?? '',
    customer_order_number: line.customer_order_number ?? '',
    manufacturing_order_number: line.manufacturing_order_number ?? '',
    quantity_value: line.quantity_value === null ? '' : String(line.quantity_value),
    actual_quantity_value:
      line.actual_quantity_value === null ? '' : String(line.actual_quantity_value),
    quantity_unit: line.quantity_unit || 'm',
    speed_value: line.speed_value === null ? '' : String(line.speed_value),
    start_time: normalizeTime(line.start_time),
    production_start_time: normalizeTime(line.production_start_time) || normalizeTime(line.start_time),
    end_time: normalizeTime(line.end_time),
    material_usages: buildLineMaterialUsageDraftsFromLine(line),
    notes: line.notes ?? '',
    extra_fields: buildLineExtraFieldsFormValue(line.extra_fields),
  });

  const openEditLineDialog = (line: ProductionReportLine) => {
    setEditingLineId(line.id);
    setLineForm(buildLineFormFromLine(line));
    setLineDialogOpen(true);
  };

  const applyExistingOrderLookupToLineForm = (lookup: ExistingProductionOrderLookup) => {
    if (!lookup.found || !lookup.line) return;

    setLineForm((current) => ({
      ...current,
      product_id: lookup.line?.product_id ?? current.product_id,
      free_product_ref: lookup.line?.free_product_ref ?? current.free_product_ref,
      customer_number: lookup.line?.customer_number ?? current.customer_number,
      customer_order_number:
        lookup.line?.customer_order_number ?? current.customer_order_number,
      manufacturing_order_number:
        lookup.line?.manufacturing_order_number ?? current.manufacturing_order_number,
      quantity_value:
        lookup.line?.quantity_value === null ||
        lookup.line?.quantity_value === undefined
          ? current.quantity_value
          : String(lookup.line.quantity_value),
      quantity_unit: lookup.line?.quantity_unit || current.quantity_unit || 'm',
      speed_value:
        lookup.line?.speed_value === null || lookup.line?.speed_value === undefined
          ? current.speed_value
          : String(lookup.line.speed_value),
      notes: lookup.line?.notes ?? current.notes,
      extra_fields: {
        ...current.extra_fields,
        ...buildLineExtraFieldsFormValue(lookup.line?.extra_fields),
        [PENDING_QUANTITY_EXTRA_FIELD_KEY]:
          lookup.line?.remaining_quantity_value === null ||
          lookup.line?.remaining_quantity_value === undefined
            ? ''
            : String(lookup.line.remaining_quantity_value),
      },
    }));
    setResolvedExistingOrderNumber(lookup.line.manufacturing_order_number ?? '');
    setResolvedExistingOrderSummary({
      totalQuantity:
        lookup.line?.quantity_value === null || lookup.line?.quantity_value === undefined
          ? null
          : Number(lookup.line.quantity_value),
      pendingQuantity:
        lookup.line?.remaining_quantity_value === null ||
        lookup.line?.remaining_quantity_value === undefined
          ? lookup.line?.quantity_value === null || lookup.line?.quantity_value === undefined
            ? null
            : Number(lookup.line.quantity_value)
          : Number(lookup.line.remaining_quantity_value),
      quantityUnit: lookup.line?.quantity_unit ?? null,
      hasPreviousProduction: Number(lookup.line?.actual_quantity_accumulated || 0) > 0,
    });
  };

  const clearResolvedExistingOrderFromLineForm = (
    nextManufacturingOrderNumber: string,
  ) => {
    const emptyLineForm = buildEmptyLineForm();

    setLineForm((current) => ({
      ...current,
      manufacturing_order_number: nextManufacturingOrderNumber,
      product_id: emptyLineForm.product_id,
      free_product_ref: emptyLineForm.free_product_ref,
      customer_number: emptyLineForm.customer_number,
      customer_order_number: emptyLineForm.customer_order_number,
      quantity_value: emptyLineForm.quantity_value,
      quantity_unit: emptyLineForm.quantity_unit,
      speed_value: emptyLineForm.speed_value,
      notes: emptyLineForm.notes,
      extra_fields: {
        ...current.extra_fields,
        [PENDING_QUANTITY_EXTRA_FIELD_KEY]: '',
      },
    }));
    setResolvedExistingOrderNumber('');
    setResolvedExistingOrderSummary(null);
  };

  const handleResolveExistingManufacturingOrder = async (
    manufacturingOrderNumber: string,
    options?: { silent?: boolean },
  ) => {
    if (!isDraft) return;

    const normalizedManufacturingOrderNumber = manufacturingOrderNumber.trim();

    if (!normalizedManufacturingOrderNumber) {
      setResolvedExistingOrderNumber('');
      setResolvedExistingOrderSummary(null);
      setLineDefaultsSuggested(false);
      return;
    }

    setResolvingExistingOrder(true);

    try {
      const lookup = await resolveIndustrialCostExistingProductionOrder({
        manufacturing_order_number: normalizedManufacturingOrderNumber,
        production_line_config_id: selectedReport?.production_line_config_id ?? null,
      });

      if (!lookup.found || !lookup.line) {
        return;
      }

      applyExistingOrderLookupToLineForm(lookup);

      if (!options?.silent) {
        enqueueSnackbar(
          'OF existente detectada. Se han cargado sus datos y la cantidad pendiente actual.',
          { variant: 'info' },
        );
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    } finally {
      setResolvingExistingOrder(false);
    }
  };

  const handleSelectExistingManufacturingOrder = async (
    option: ExistingProductionOrderCandidate | null,
  ) => {
    if (!option) return;

    setLineForm((current) => ({
      ...current,
      manufacturing_order_number: option.manufacturing_order_number,
    }));
    setExistingOrderSearchText(option.manufacturing_order_number);

    if (option.source_type === 'imported_order') {
      setLineForm((current) => ({
        ...current,
        manufacturing_order_number: option.manufacturing_order_number,
        free_product_ref: option.product_label ?? current.free_product_ref,
        customer_number: option.customer_number ?? current.customer_number,
        customer_order_number:
          option.customer_order_number ?? current.customer_order_number,
        quantity_value:
          option.quantity_value === null || option.quantity_value === undefined
            ? current.quantity_value
            : String(option.quantity_value),
        quantity_unit: option.quantity_unit || current.quantity_unit || 'm',
        speed_value:
          option.speed_value === null || option.speed_value === undefined
            ? current.speed_value
            : String(option.speed_value),
        notes: option.notes ?? current.notes,
        extra_fields: {
          ...current.extra_fields,
          ...buildLineExtraFieldsFormValue(option.extra_fields),
          [PENDING_QUANTITY_EXTRA_FIELD_KEY]:
            option.remaining_quantity_value === null ||
            option.remaining_quantity_value === undefined
              ? ''
              : String(option.remaining_quantity_value),
        },
      }));
      setResolvedExistingOrderNumber(option.manufacturing_order_number);
      setResolvedExistingOrderSummary({
        totalQuantity:
          option.quantity_value === null || option.quantity_value === undefined
            ? null
            : Number(option.quantity_value),
        pendingQuantity:
          option.remaining_quantity_value === null ||
          option.remaining_quantity_value === undefined
            ? option.quantity_value === null || option.quantity_value === undefined
              ? null
              : Number(option.quantity_value)
            : Number(option.remaining_quantity_value),
        quantityUnit: option.quantity_unit ?? null,
        hasPreviousProduction: false,
      });

      // Sugerir velocidad y campos adicionales desde historial de la línea
      const productionLineConfigId = selectedReport?.production_line_config_id ?? null;
      const productRef = (option.product_label ?? '').trim();
      const speedEmpty = !option.speed_value;
      if (productionLineConfigId && productRef) {
        try {
          const suggestion = await getIndustrialCostLineDefaultsSuggestion({
            production_line_config_id: productionLineConfigId,
            free_product_ref: productRef,
          });
          if (suggestion.found) {
            setLineForm((current) => ({
              ...current,
              speed_value:
                speedEmpty && suggestion.speed_value !== null && suggestion.speed_value !== undefined
                  ? String(suggestion.speed_value)
                  : current.speed_value,
              extra_fields: (() => {
                const merged = { ...current.extra_fields };
                Object.entries(suggestion.extra_fields).forEach(([key, val]) => {
                  if (!merged[key] || merged[key] === '') {
                    merged[key] = val !== null && val !== undefined ? String(val) : '';
                  }
                });
                return merged;
              })(),
            }));
            setLineDefaultsSuggested(true);
          }
        } catch {
          // Silencioso: la sugerencia es opcional, no bloquea el flujo
        }
      }

      return;
    }

    await handleResolveExistingManufacturingOrder(option.manufacturing_order_number, {
      silent: true,
    });
  };

  const handleSaveLine = () => {
    if (!selectedReportId || !isDraft) return;

    if (isOrderFieldVisible('product') && !lineForm.product_id && !lineForm.free_product_ref.trim()) {
      enqueueSnackbar('Selecciona un producto o informa una referencia libre.', {
        variant: 'error',
      });
      return;
    }

    const startTimeValidationMessage = getShiftTimeValidationMessage(
      lineForm.start_time,
      'La hora de inicio',
      configuredShiftStartTime,
      configuredShiftEndTime,
    );

    if (startTimeValidationMessage) {
      enqueueSnackbar(startTimeValidationMessage, { variant: 'error' });
      return;
    }

    const effectiveProductionStartTime = getEffectiveProductionStartTime(
      lineForm.start_time,
      lineForm.production_start_time,
    );

    const productionStartTimeValidationMessage = getShiftTimeValidationMessage(
      effectiveProductionStartTime,
      'La hora de inicio de fabricación',
      configuredShiftStartTime,
      configuredShiftEndTime,
    );

    if (productionStartTimeValidationMessage) {
      enqueueSnackbar(productionStartTimeValidationMessage, { variant: 'error' });
      return;
    }

    if (
      !isEndTimeSameOrAfterStartTimeWithinShift(
        lineForm.start_time,
        effectiveProductionStartTime,
        configuredShiftStartTime,
        configuredShiftEndTime,
      )
    ) {
      enqueueSnackbar('La hora de inicio de fabricación debe ser igual o posterior al inicio de la orden.', {
        variant: 'error',
      });
      return;
    }

    if (lineForm.end_time) {
      const endTimeValidationMessage = getShiftTimeValidationMessage(
        lineForm.end_time,
        'La hora de fin',
        configuredShiftStartTime,
        configuredShiftEndTime,
      );

      if (endTimeValidationMessage) {
        enqueueSnackbar(endTimeValidationMessage, { variant: 'error' });
        return;
      }

      if (
        !isEndTimeSameOrAfterStartTimeWithinShift(
          lineForm.start_time,
          lineForm.end_time,
          configuredShiftStartTime,
          configuredShiftEndTime,
        )
      ) {
        enqueueSnackbar('La hora de fin debe ser igual o posterior a la hora de inicio.', {
          variant: 'error',
        });
        return;
      }

      if (
        !isEndTimeSameOrAfterStartTimeWithinShift(
          effectiveProductionStartTime,
          lineForm.end_time,
          configuredShiftStartTime,
          configuredShiftEndTime,
        )
      ) {
        enqueueSnackbar('La hora de fin debe ser igual o posterior al inicio de fabricación.', {
          variant: 'error',
        });
        return;
      }

      if (
        requireMaterialUsageOnLineClose &&
        !hasMaterialUsagesForLineClose(lineForm)
      ) {
        enqueueSnackbar(
          'Debes informar al menos un material consumido antes de cerrar la orden.',
          { variant: 'error' },
        );
        return;
      }
    }

    const editingLine = (detail?.lines ?? []).find((line) => line.id === editingLineId);
    const nextOrder = editingLine?.line_order ?? (detail?.lines?.length ?? 0) + 1;
    const payload = buildLinePayload(
      lineForm,
      nextOrder,
      operationSettings?.additional_fields_config ?? [],
    );

    if (editingLineId) {
      updateLineMutation.mutate({
        lineId: editingLineId,
        data: payload,
      });
      return;
    }

    createLineMutation.mutate(payload);
  };

  const buildStopFormFromStop = (stop: ProductionReportStop): StopForm => ({
    line_id: stop.line_id ?? '',
    start_time: normalizeTime(stop.start_time),
    end_time: normalizeTime(stop.end_time),
    reason_id: stop.reason_id ?? '',
    notes: stop.notes ?? '',
  });

  const openStartStopDialog = () => {
    if (!isDraft || activeStop) return;

    setEditingStopId(null);
    setStopForm({
      ...buildEmptyStopForm(),
      line_id: activeLine?.id ?? '',
      start_time: getCurrentTimeValue(),
    });
    setStopDialogOpen(true);
  };

  const openManualStopDialog = () => {
    if (!isDraft || activeStop) return;

    setEditingStopId(null);
    setStopForm({
      ...buildEmptyStopForm(),
      line_id: activeLine?.id ?? '',
      start_time: getCurrentTimeValue(),
    });
    setStopDialogOpen(true);
  };

  const openEditStopDialog = (stop: ProductionReportStop) => {
    setEditingStopId(stop.id);
    setStopForm(buildStopFormFromStop(stop));
    setStopDialogOpen(true);
  };

  const openEditLineDialogWithOverrides = (
    line: ProductionReportLine,
    overrides: Partial<LineForm>,
  ) => {
    setEditingLineId(line.id);
    setLineForm({
      ...buildLineFormFromLine(line),
      ...overrides,
    });
    setLineDialogOpen(true);
  };

  const openEditStopDialogWithOverrides = (
    stop: ProductionReportStop,
    overrides: Partial<StopForm>,
  ) => {
    setEditingStopId(stop.id);
    setStopForm({
      ...buildStopFormFromStop(stop),
      ...overrides,
    });
    setStopDialogOpen(true);
  };

  const getTimelineEventEntity = (event: TimelineValidationEventSummary | null) => {
    if (!event) return null;

    if (event.event_type === 'line') {
      const line = (detail?.lines ?? []).find((item) => item.id === event.event_id);

      if (!line) return null;

      return {
        type: 'line' as const,
        entity: line,
      };
    }

    const stop = (detail?.stops ?? []).find((item) => item.id === event.event_id);

    if (!stop) return null;

    return {
      type: 'stop' as const,
      entity: stop,
    };
  };

  const handleTimelineGapAction = (
    gap: TimelineValidationGap,
    action: string,
  ) => {
    if (action === 'create_stop') {
      setPendingTimelineResolution({
        kind: 'gap_create_stop',
        start_time: gap.start_time,
        end_time: gap.end_time,
      });
      setEditingStopId(null);
      setStopForm({
        ...buildEmptyStopForm(),
        line_id: '',
        start_time: gap.start_time,
        end_time: gap.end_time,
        reason_id: '',
        notes: '',
      });
      setStopDialogOpen(true);
      return;
    }

    if (action === 'create_idle_period') {
      setPendingTimelineResolution({
        kind: 'gap_create_idle_period',
        start_time: gap.start_time,
        end_time: gap.end_time,
      });

      if (!acquireIdlePeriodWriteLock()) return;

      createIdlePeriodMutation.mutate({
        production_line_config_id: selectedReport?.production_line_config_id ?? null,
        start_time: gap.start_time,
        end_time: gap.end_time,
        duration_minutes: calculateDurationMinutes(gap.start_time, gap.end_time),
        idle_type: 'no_planned_order',
        notes: 'Sin orden planificada',
      });
      return;
    }

    if (action === 'extend_previous_end') {
      const previousEvent = getTimelineEventEntity(gap.previous_event);

      if (!previousEvent) {
        enqueueSnackbar('No se ha encontrado la actividad anterior para ajustar.', {
          variant: 'error',
        });
        return;
      }

      setPendingTimelineResolution({
        kind: 'gap_extend_previous_end',
        start_time: gap.start_time,
        end_time: gap.end_time,
        expected_end_time: gap.end_time,
      });

      if (previousEvent.type === 'line') {
        openEditLineDialogWithOverrides(previousEvent.entity, {
          end_time: gap.end_time,
        });
        return;
      }

      openEditStopDialogWithOverrides(previousEvent.entity, {
        end_time: gap.end_time,
      });
      return;
    }

    if (action === 'move_next_start') {
      const nextEvent = getTimelineEventEntity(gap.next_event);

      if (!nextEvent) {
        enqueueSnackbar('No se ha encontrado la actividad siguiente para ajustar.', {
          variant: 'error',
        });
        return;
      }

      setPendingTimelineResolution({
        kind: 'gap_move_next_start',
        start_time: gap.start_time,
        end_time: gap.end_time,
        expected_start_time: gap.start_time,
      });

      if (nextEvent.type === 'line') {
        openEditLineDialogWithOverrides(nextEvent.entity, {
          start_time: gap.start_time,
        });
        return;
      }

      openEditStopDialogWithOverrides(nextEvent.entity, {
        start_time: gap.start_time,
      });
      return;
    }

    if (action === 'edit_manual') {
      const preferredEvent =
        getTimelineEventEntity(gap.next_event) ?? getTimelineEventEntity(gap.previous_event);

      if (!preferredEvent) {
        enqueueSnackbar('No se ha encontrado una actividad para editar manualmente.', {
          variant: 'error',
        });
        return;
      }

      if (preferredEvent.type === 'line') {
        openEditLineDialog(preferredEvent.entity);
        return;
      }

      openEditStopDialog(preferredEvent.entity);
      return;
    }
  };

  const handleTimelineOverlapAction = (
    overlap: TimelineValidationOverlap,
    action: string,
  ) => {
    if (action === 'adjust_previous_end') {
      const previousEvent = getTimelineEventEntity(overlap.previous_event);

      if (!previousEvent) {
        enqueueSnackbar('No se ha encontrado la actividad anterior para ajustar.', {
          variant: 'error',
        });
        return;
      }

      setPendingTimelineResolution({
        kind: 'overlap_adjust_previous_end',
        start_time: overlap.start_time,
        end_time: overlap.end_time,
        expected_end_time: overlap.start_time,
      });

      if (previousEvent.type === 'line') {
        openEditLineDialogWithOverrides(previousEvent.entity, {
          end_time: overlap.start_time,
        });
        return;
      }

      openEditStopDialogWithOverrides(previousEvent.entity, {
        end_time: overlap.start_time,
      });
      return;
    }

    if (action === 'adjust_next_start') {
      const nextEvent = getTimelineEventEntity(overlap.next_event);

      if (!nextEvent) {
        enqueueSnackbar('No se ha encontrado la actividad siguiente para ajustar.', {
          variant: 'error',
        });
        return;
      }

      setPendingTimelineResolution({
        kind: 'overlap_adjust_next_start',
        start_time: overlap.start_time,
        end_time: overlap.end_time,
        expected_start_time: overlap.end_time,
      });

      if (nextEvent.type === 'line') {
        openEditLineDialogWithOverrides(nextEvent.entity, {
          start_time: overlap.end_time,
        });
        return;
      }

      openEditStopDialogWithOverrides(nextEvent.entity, {
        start_time: overlap.end_time,
      });
      return;
    }

    if (action === 'edit_manual') {
      const preferredEvent =
        getTimelineEventEntity(overlap.previous_event) ?? getTimelineEventEntity(overlap.next_event);

      if (!preferredEvent) {
        enqueueSnackbar('No se ha encontrado una actividad para editar manualmente.', {
          variant: 'error',
        });
        return;
      }

      if (preferredEvent.type === 'line') {
        openEditLineDialog(preferredEvent.entity);
        return;
      }

      openEditStopDialog(preferredEvent.entity);
    }
  };

  const handleTimelineIncompleteEventAction = (event: TimelineValidationEventSummary) => {
    const eventEntity = getTimelineEventEntity(event);

    if (!eventEntity) {
      enqueueSnackbar('No se ha encontrado el evento pendiente para completar.', {
        variant: 'error',
      });
      return;
    }

    setPendingIncompleteEventResolution({
      event_type: event.event_type,
      event_id: event.event_id,
    });

    if (eventEntity.type === 'line') {
      openFinishLineDialogForIncompleteEvent(eventEntity.entity);
      return;
    }

    openEditStopDialog(eventEntity.entity);
  };

  const openEditIdlePeriodDialog = (idlePeriod: ProductionReportIdlePeriod) => {
    setEditingIdlePeriodId(idlePeriod.id);
    setIdlePeriodForm({
      start_time: normalizeTime(idlePeriod.start_time),
      end_time: normalizeTime(idlePeriod.end_time),
      notes: idlePeriod.notes ?? '',
    });
    setIdlePeriodDialogOpen(true);
  };

  const handleSaveIdlePeriod = () => {
    if (!selectedReportId || !isDraft || !editingIdlePeriodId) return;

    if (!idlePeriodForm.start_time) {
      enqueueSnackbar('Debes informar la hora de inicio del periodo sin orden planificada.', {
        variant: 'error',
      });
      return;
    }

    if (!idlePeriodForm.end_time) {
      enqueueSnackbar('Debes informar la hora de fin del periodo sin orden planificada.', {
        variant: 'error',
      });
      return;
    }

    const startValidationMessage = getShiftTimeValidationMessage(
      idlePeriodForm.start_time,
      'La hora de inicio del periodo sin orden planificada',
      configuredShiftStartTime,
      configuredShiftEndTime,
    );

    if (startValidationMessage) {
      enqueueSnackbar(startValidationMessage, { variant: 'error' });
      return;
    }

    const endValidationMessage = getShiftTimeValidationMessage(
      idlePeriodForm.end_time,
      'La hora de fin del periodo sin orden planificada',
      configuredShiftStartTime,
      configuredShiftEndTime,
    );

    if (endValidationMessage) {
      enqueueSnackbar(endValidationMessage, { variant: 'error' });
      return;
    }

    if (
      !isEndTimeSameOrAfterStartTimeWithinShift(
        idlePeriodForm.start_time,
        idlePeriodForm.end_time,
        configuredShiftStartTime,
        configuredShiftEndTime,
      )
    ) {
      enqueueSnackbar('La hora de fin debe ser igual o posterior a la hora de inicio.', {
        variant: 'error',
      });
      return;
    }

    if (!acquireIdlePeriodWriteLock()) return;

    updateIdlePeriodMutation.mutate({
      idlePeriodId: editingIdlePeriodId,
      data: buildIdlePeriodPayload(
        idlePeriodForm,
        selectedReport?.production_line_config_id ?? null,
      ),
    });
  };

  const handleSaveStop = () => {
    if (!selectedReportId || !isDraft) return;

    if (!stopForm.start_time) {
      enqueueSnackbar('Debes informar la hora de inicio de la parada.', {
        variant: 'error',
      });
      return;
    }

    const stopStartTimeValidationMessage = getShiftTimeValidationMessage(
      stopForm.start_time,
      'La hora de inicio de la parada',
      configuredShiftStartTime,
      configuredShiftEndTime,
    );

    if (stopStartTimeValidationMessage) {
      enqueueSnackbar(stopStartTimeValidationMessage, { variant: 'error' });
      return;
    }

    if (stopForm.end_time) {
      const stopEndTimeValidationMessage = getShiftTimeValidationMessage(
        stopForm.end_time,
        'La hora de fin de la parada',
        configuredShiftStartTime,
        configuredShiftEndTime,
      );

      if (stopEndTimeValidationMessage) {
        enqueueSnackbar(stopEndTimeValidationMessage, { variant: 'error' });
        return;
      }

      if (!isTimeGreaterOrEqual(stopForm.end_time, stopForm.start_time)) {
        enqueueSnackbar('La hora de fin de la parada debe ser igual o posterior a la de inicio.', {
          variant: 'error',
        });
        return;
      }
    }

    if (!stopForm.reason_id) {
      enqueueSnackbar('Selecciona un motivo de parada.', {
        variant: 'error',
      });
      return;
    }

    const payload = buildStopPayload(stopForm);

    if (!acquireStopWriteLock()) return;

    if (editingStopId) {
      updateStopMutation.mutate({
        stopId: editingStopId,
        data: payload,
      });
      return;
    }

    createStopMutation.mutate(payload);
  };

  const handleFinishActiveStop = () => {
    if (!selectedReportId || !isDraft || !activeStop) return;
    if (!acquireStopWriteLock()) return;

    const endTime = getCurrentTimeValue();
    const form = {
      ...buildStopFormFromStop(activeStop),
      end_time: endTime,
    };

    updateStopMutation.mutate({
      stopId: activeStop.id,
      data: buildStopPayload(form),
    });
  };

  const setLineStartNow = () => {
    const nextStartTime = getCurrentTimeValue();

    setLineForm((current) => ({
      ...current,
      start_time: nextStartTime,
      production_start_time: current.production_start_time || nextStartTime,
    }));
  };

  const setLineEndNow = () => {
    setLineForm((current) => ({
      ...current,
      end_time: getCurrentTimeValue(),
    }));
  };

  const setStopStartNow = () => {
    setStopForm((current) => ({
      ...current,
      start_time: getCurrentTimeValue(),
    }));
  };

  const setStopEndNow = () => {
    setStopForm((current) => ({
      ...current,
      end_time: getCurrentTimeValue(),
    }));
  };

  const closeFinishLineDialog = () => {
    setFinishLineDialogOpen(false);
    setFinishLineEndTime('');
    setFinishLineStopReasonId('');
    setFinishLineForm(buildEmptyLineForm());
    setPendingIncompleteEventResolution(null);
  };

  const openFinishLineDialog = () => {
    if (!isDraft || !activeLine || activeStop) return;

    setFinishLineEndTime(getCurrentTimeValue());
    setFinishLineStopReasonId('');
    setFinishLineForm(buildLineFormFromLine(activeLine));
    setFinishLineDialogOpen(true);
  };

  const openFinishLineDialogForIncompleteEvent = (line: ProductionReportLine) => {
    if (!isDraft || activeStop) return;

    if (!activeLine || activeLine.id !== line.id) {
      enqueueSnackbar('La orden abierta ya no coincide con el estado actual del parte.', {
        variant: 'error',
      });
      return;
    }

    setFinishLineEndTime(getCurrentTimeValue());
    setFinishLineStopReasonId('');
    setFinishLineForm(buildLineFormFromLine(line));
    setFinishLineDialogOpen(true);
  };

  const handleFinishActiveLine = (
    startNext: boolean,
    continuationStatus: LineContinuationStatus,
    endTimeOverride?: string,
  ) => {
    if (!selectedReportId || !isDraft || !activeLine || activeStop) return false;

    const endTime = endTimeOverride || getCurrentTimeValue();

    if (!endTime) {
      enqueueSnackbar('Debes informar la hora de fin de la orden.', { variant: 'error' });
      return false;
    }

    const finishTimeValidationMessage = getShiftTimeValidationMessage(
      endTime,
      'La hora de fin',
      configuredShiftStartTime,
      configuredShiftEndTime,
    );

    if (finishTimeValidationMessage) {
      enqueueSnackbar(finishTimeValidationMessage, { variant: 'error' });
      return false;
    }

    const form = {
      ...finishLineForm,
      end_time: endTime,
    };

    const missingRequiredCloseFields = getMissingRequiredCloseFields(form);

    if (missingRequiredCloseFields.length > 0) {
      enqueueSnackbar(
        `Faltan campos obligatorios al cierre: ${missingRequiredCloseFields.join(', ')}.`,
        { variant: 'error' },
      );
      return false;
    }

    if (
      requireMaterialUsageOnLineClose &&
      !hasMaterialUsagesForLineClose(form)
    ) {
      enqueueSnackbar(
        'Debes informar al menos un material consumido antes de cerrar la orden.',
        { variant: 'error' },
      );
      return false;
    }

    const payload: CreateProductionReportLinePayload = {
      ...buildLinePayload(
        form,
        activeLine.line_order,
        operationSettings?.additional_fields_config ?? [],
      ),
      continuation_status: continuationStatus,
      continued_from_line_id: activeLine.continued_from_line_id ?? null,
    };

    if (startNext) {
      setNextLineStartAfterFinish(endTime);
    } else {
      setNextLineStartAfterFinish(null);
    }

    updateLineMutation.mutate({
      lineId: activeLine.id,
      data: payload,
    });

    return true;
  };

  const handleFinishLineFromDialog = (endTime: string) => {
    if (!endTime) {
      enqueueSnackbar('Debes informar la hora de fin de la orden.', { variant: 'error' });
      return;
    }

    const finishesAtShiftEnd =
      Boolean(configuredShiftEndTime) &&
      normalizeTime(endTime) === normalizeTime(configuredShiftEndTime);

    handleFinishActiveLine(
      false,
      finishesAtShiftEnd ? 'pending_continuation' : 'finished',
      endTime,
    );
  };

  const handleFinishLineAtShiftEnd = () => {
    if (!configuredShiftEndTime) {
      enqueueSnackbar('No hay horario de fin de turno configurado para este parte.', {
        variant: 'error',
      });
      return;
    }

    handleFinishActiveLine(
      false,
      'pending_continuation',
      configuredShiftEndTime,
    );
  };

  const handleFinishLineAndStartStop = () => {
    if (!selectedReportId || !isDraft || !activeLine || activeStop) return;

    if (!finishLineEndTime) {
      enqueueSnackbar('Debes informar la hora de fin de la orden.', { variant: 'error' });
      return;
    }

    if (!finishLineStopReasonId) {
      enqueueSnackbar('Selecciona el motivo de la parada.', { variant: 'error' });
      return;
    }

    const form = {
      ...finishLineForm,
      end_time: finishLineEndTime,
    };

    const missingRequiredCloseFields = getMissingRequiredCloseFields(form);

    if (missingRequiredCloseFields.length > 0) {
      enqueueSnackbar(
        `Faltan campos obligatorios al cierre: ${missingRequiredCloseFields.join(', ')}.`,
        { variant: 'error' },
      );
      return;
    }

    if (
      requireMaterialUsageOnLineClose &&
      !hasMaterialUsagesForLineClose(form)
    ) {
      enqueueSnackbar(
        'Debes informar al menos un material consumido antes de cerrar la orden.',
        { variant: 'error' },
      );
      return;
    }

    const payload = buildLinePayload(
      form,
      activeLine.line_order,
      operationSettings?.additional_fields_config ?? [],
    );

    setNextLineStartAfterFinish(null);

    updateLineMutation.mutate(
      {
        lineId: activeLine.id,
        data: payload,
      },
      {
        onSuccess: () => {
          createStopMutation.mutate(
            buildStopPayload({
              line_id: '',
              start_time: finishLineEndTime,
              end_time: '',
              reason_id: finishLineStopReasonId,
              notes: 'Parada iniciada al finalizar la orden.',
            }),
          );
        },
      },
    );
  };

  const renderCreateReportDialog = () => (
    <Dialog
      open={createDialogOpen}
      onClose={() => {
        if (!reportWriteLocked && !createReportMutation.isPending) {
          setCreateDialogOpen(false);
        }
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Abrir nuevo parte de trabajo</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            select
            size="small"
            label="Plantilla"
            value={newReportForm.template_id || templates[0]?.id || ''}
            onChange={(event) =>
              setNewReportForm((current) => ({
                ...current,
                template_id: event.target.value,
              }))
            }
            fullWidth
          >
            {templates.map((template) => (
              <MenuItem key={template.id} value={template.id}>
                {template.name}
              </MenuItem>
            ))}
          </TextField>

          <Stack spacing={0.75}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <DatePicker
                label="Fecha"
                value={newReportForm.report_date ? parseISO(newReportForm.report_date) : null}
                onChange={(v) =>
                  setNewReportForm((current) => ({
                    ...current,
                    report_date: v ? format(v, 'yyyy-MM-dd') : '',
                  }))
                }
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: {
                      '& .MuiInputBase-input': { color: '#f8fafc' },
                      '& .MuiInputLabel-root': { color: '#94a3b8' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(148,163,184,0.3)' },
                      '& .MuiSvgIcon-root': { color: '#94a3b8' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(148,163,184,0.6)' },
                    },
                  },
                }}
              />
            </LocalizationProvider>

            {newReportForm.report_date && newReportForm.report_date !== getTodayIsoDate() ? (
              <Alert
                severity="error"
                variant="filled"
                sx={{
                  borderRadius: 2.5,
                  fontWeight: 850,
                  py: 1,
                }}
              >
                ATENCIÓN: estás creando un parte para el día {newReportForm.report_date}.
                Hoy es {getTodayIsoDate()}. Comprueba la fecha antes de continuar.
              </Alert>
            ) : null}
          </Stack>

          <TextField
            size="small"
            label="Nº operario"
            value={newReportForm.operator_number}
            onChange={(event) =>
              setNewReportForm((current) => ({
                ...current,
                operator_number: event.target.value,
              }))
            }
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
            InputProps={{
              endAdornment: touchModeEnabled ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Abrir teclado numérico PROA"
                    onPointerDown={(event) => event.preventDefault()}
                    onClick={() => setNewReportOperatorKeyboardOpen(true)}
                    edge="end"
                    sx={{
                      color: '#38bdf8',
                    }}
                  >
                    <KeyboardRoundedIcon />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            fullWidth
          />

          <TextField
            select
            size="small"
            label="Turno"
            value={newReportForm.shift}
            onChange={(event) =>
              setNewReportForm((current) => ({
                ...current,
                shift: event.target.value,
              }))
            }
            fullWidth
          >
            {SHIFT_OPTIONS.map((shift) => (
              <MenuItem key={shift.value} value={shift.value}>
                {shift.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Línea / puesto"
            value={newReportForm.production_line_config_id}
            onChange={(event) => {
              const selectedLine = productionLinesConfig.find(
                (line) => line.id === event.target.value,
              );

              setNewReportForm((current) => ({
                ...current,
                production_line_config_id: event.target.value,
                line_or_center: selectedLine?.name ?? '',
              }));
            }}
            disabled={productionLinesConfigQuery.isLoading}
            fullWidth
          >
            {productionLinesConfig.map((line) => (
              <MenuItem key={line.id} value={line.id}>
                {line.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            label="Notas"
            value={newReportForm.notes}
            onChange={(event) =>
              setNewReportForm((current) => ({
                ...current,
                notes: event.target.value,
              }))
            }
            fullWidth
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setCreateDialogOpen(false)}
          disabled={reportWriteLocked || createReportMutation.isPending}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleCreateReport}
          disabled={
            reportWriteLocked ||
            createReportMutation.isPending ||
            operatorContextQuery.isLoading ||
            productionLinesConfigQuery.isLoading ||
            !newReportForm.production_line_config_id
          }
        >
          {reportWriteLocked ? 'Creando...' : 'Crear y abrir'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const openMaterialUsagesDialog = () => {
    if (!materialUsageCaptureEnabled) {
      enqueueSnackbar('La captura de materiales/lotes está desactivada para esta planta.', {
        variant: 'info',
      });
      return;
    }

    setMaterialUsagesDraft(
      lineForm.material_usages.length > 0
        ? lineForm.material_usages.map((usage) => ({ ...usage }))
        : [buildEmptyLineMaterialUsageDraft(0)],
    );
    setMaterialUsagesDialogOpen(true);
  };

  const closeMaterialUsagesDialog = () => {
    setMaterialUsagesDialogOpen(false);
    setMaterialUsagesDraft([]);
  };

  const handleApplyMaterialUsagesDraft = () => {
    const normalizedUsages = materialUsagesDraft.map((usage, index) => ({
      ...usage,
      display_order: index,
      material_name: usage.material_name.trim(),
      lot_number: usage.lot_number.trim(),
      supplier_name: usage.supplier_name.trim(),
      quantity_value: usage.quantity_value.trim(),
      quantity_unit: usage.quantity_unit.trim(),
      notes: usage.notes.trim(),
    }));

    if (normalizedUsages.some((usage) => !usage.material_name)) {
      enqueueSnackbar('Cada consumo debe incluir material.', {
        variant: 'warning',
      });
      return;
    }

    if (
      requireExistingMaterialLot &&
      normalizedUsages.some((usage) => !usage.material_lot_id)
    ) {
      enqueueSnackbar('Cada material debe seleccionar un lote controlado existente.', {
        variant: 'warning',
      });
      return;
    }

    if (
      requireLotOnMaterialUsage &&
      normalizedUsages.some((usage) => !usage.lot_number)
    ) {
      enqueueSnackbar('Cada material debe incluir lote.', {
        variant: 'warning',
      });
      return;
    }

    if (
      requireQuantityOnMaterialUsage &&
      normalizedUsages.some((usage) => !usage.quantity_value)
    ) {
      enqueueSnackbar('Cada material debe incluir cantidad consumida.', {
        variant: 'warning',
      });
      return;
    }

    if (
      normalizedUsages.some((usage) => {
        if (!usage.quantity_value) return false;

        const quantityValue = Number(usage.quantity_value);
        return !Number.isFinite(quantityValue) || quantityValue < 0;
      })
    ) {
      enqueueSnackbar('La cantidad de material debe ser un número válido y no negativo.', {
        variant: 'warning',
      });
      return;
    }

    setLineForm((current) => ({
      ...current,
      material_usages: normalizedUsages,
    }));

    closeMaterialUsagesDialog();
  };
  
  const renderPlantNoticesDialog = () => (
    <Dialog
      open={plantNoticesDialogOpen}
      onClose={() => setPlantNoticesDialogOpen(false)}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: '#0f172a',
          border: '1px solid rgba(148,163,184,0.18)',
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <NotificationsActiveRoundedIcon sx={{ color: '#f87171' }} />

          <Stack spacing={0.2}>
            <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
              Avisos de línea
            </Typography>

            <Typography sx={{ color: '#94a3b8', fontSize: '0.84rem' }}>
              Los avisos se mantienen visibles hasta que oficina los retire.
            </Typography>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={1.25} sx={{ pt: 1 }}>
          {operatorPlantNoticesQuery.isError ? (
            <Alert
              severity="error"
              variant="outlined"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => operatorPlantNoticesQuery.refetch()}
                >
                  Reintentar
                </Button>
              }
            >
              No se pudieron cargar los avisos de línea.
            </Alert>
          ) : activePlantNotices.length === 0 ? (
            <Alert severity="info" variant="outlined">
              No hay avisos activos para esta línea.
            </Alert>
          ) : (
            activePlantNotices.map((notice) => {
              const target = getSelectedReportPlantNoticeTarget(notice);

              if (!target) return null;

              const acknowledgedAt =
                target.current_report_acknowledged_at ?? null;

              const isAcknowledged = Boolean(acknowledgedAt);

              return (
                <Paper
                  key={notice.id}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: isAcknowledged
                      ? '1px solid rgba(34,197,94,0.28)'
                      : '1px solid rgba(248,113,113,0.42)',
                    bgcolor: isAcknowledged
                      ? 'rgba(20,83,45,0.16)'
                      : 'rgba(127,29,29,0.20)',
                    px: 2,
                    py: 1.75,
                  }}
                >
                  <Stack spacing={1.15}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      spacing={1}
                    >
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        <Chip
                          size="small"
                          label={getPlantNoticePriorityLabel(notice.priority)}
                          variant="outlined"
                          sx={{
                            color:
                              notice.priority === 'important'
                                ? '#fde68a'
                                : '#cbd5e1',
                            borderColor:
                              notice.priority === 'important'
                                ? 'rgba(251,191,36,0.32)'
                                : 'rgba(148,163,184,0.28)',
                            fontWeight: 850,
                          }}
                        />

                        <Chip
                          size="small"
                          label={
                            isAcknowledged
                              ? 'Lectura confirmada'
                              : 'Pendiente de lectura'
                          }
                          variant="outlined"
                          sx={{
                            color: isAcknowledged ? '#bbf7d0' : '#fecaca',
                            borderColor: isAcknowledged
                              ? 'rgba(34,197,94,0.32)'
                              : 'rgba(248,113,113,0.42)',
                            fontWeight: 850,
                          }}
                        />
                      </Stack>

                      <Typography sx={{ color: '#94a3b8', fontSize: '0.78rem' }}>
                        Actualizado {formatPlantNoticeTimestamp(notice.updated_at)}
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                      {notice.title}
                    </Typography>

                    <Typography
                      sx={{
                        color: '#cbd5e1',
                        fontSize: '0.92rem',
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {notice.message}
                    </Typography>

                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                      spacing={1}
                    >
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                        {isAcknowledged
                          ? `Confirmado el ${formatPlantNoticeTimestamp(acknowledgedAt)}`
                          : 'Confirma la lectura para dejar constancia en este turno.'}
                      </Typography>

                      {!isAcknowledged ? (
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          disabled={acknowledgePlantNoticeMutation.isPending}
                          onClick={() =>
                            acknowledgePlantNoticeMutation.mutate({
                              noticeId: notice.id,
                              productionLineConfigId:
                                target.production_line_config_id,
                            })
                          }
                        >
                          {acknowledgePlantNoticeMutation.isPending
                            ? 'Confirmando...'
                            : 'Confirmar lectura'}
                        </Button>
                      ) : null}
                    </Stack>
                  </Stack>
                </Paper>
              );
            })
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setPlantNoticesDialogOpen(false)}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );


  const renderOperatorNumberDialog = () => (
    <Dialog
      open={operatorNumberDialogOpen}
      onClose={() => {
        if (updateOperatorNumberMutation.isPending) return;

        setOperatorNumberDialogOpen(false);
        setOperatorNumberDraft('');
      }}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>Corregir número de operario</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            autoFocus
            size="small"
            label="Nº operario"
            value={operatorNumberDraft}
            onChange={(event) => setOperatorNumberDraft(event.target.value)}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={updateOperatorNumberMutation.isPending}
          onClick={() => {
            setOperatorNumberDialogOpen(false);
            setOperatorNumberDraft('');
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          disabled={
            !operatorNumberDraft.trim() ||
            updateOperatorNumberMutation.isPending
          }
          onClick={() => updateOperatorNumberMutation.mutate()}
        >
          Guardar corrección
        </Button>
      </DialogActions>
    </Dialog>
  );


  const renderReopenDialog = () => (
    <Dialog
      open={reopenDialogOpen}
      onClose={() => setReopenDialogOpen(false)}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Reabrir parte para corrección</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="warning" variant="outlined">
            El parte volverá a estado borrador. Esta acción quedará registrada en la auditoría.
          </Alert>
          <TextField
            label="Motivo de corrección *"
            value={reopenNotes}
            onChange={(event) => setReopenNotes(event.target.value)}
            multiline
            minRows={3}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setReopenDialogOpen(false)}>Cancelar</Button>
        <Button
          variant="contained"
          color="warning"
          disabled={!reopenNotes.trim() || reopenReportMutation.isPending}
          onClick={() => reopenReportMutation.mutate()}
        >
          Reabrir parte
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderMaterialUsagesDialog = () => (
    <Dialog
      open={materialUsagesDialogOpen}
      onClose={closeMaterialUsagesDialog}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>Materiales y lotes utilizados</DialogTitle>

      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          <Alert severity="info" variant="outlined">
            Registra los materiales utilizados en esta OF. En esta primera versión puedes introducir
            los datos manualmente.
          </Alert>

          {materialUsagesDraft.length === 0 ? (
            <Alert severity="warning" variant="outlined">
              No hay materiales registrados.
            </Alert>
          ) : (
            <Stack spacing={1.25}>
              {materialUsagesDraft.map((usage, index) => (
                <Paper
                  key={`material-usage-${index}`}
                  elevation={0}
                  sx={{
                    borderRadius: 2.5,
                    border: '1px solid rgba(148,163,184,0.18)',
                    bgcolor: 'rgba(2,6,23,0.24)',
                    p: 1.5,
                  }}
                >
                  <Stack spacing={1.25}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={1}
                    >
                      <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                        Material {index + 1}
                      </Typography>

                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteOutlineRoundedIcon fontSize="small" />}
                        onClick={() =>
                          setMaterialUsagesDraft((current) =>
                            current.filter((_, currentIndex) => currentIndex !== index),
                          )
                        }
                      >
                        Eliminar
                      </Button>
                    </Stack>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: 'repeat(2, minmax(0, 1fr))',
                        },
                        gap: 1.25,
                      }}
                    >
                      <Autocomplete
                        size="small"
                        options={productionMaterialLots}
                        loading={productionMaterialLotsQuery.isFetching}
                        value={
                          productionMaterialLots.find(
                            (lot) => lot.id === usage.material_lot_id,
                          ) ?? null
                        }
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) =>
                          [
                            option.material_name,
                            option.lot_number,
                            option.supplier_name,
                          ]
                            .filter(Boolean)
                            .join(' · ')
                        }
                        onChange={(_, selectedLot) =>
                          setMaterialUsagesDraft((current) =>
                            current.map((item, currentIndex) => {
                              if (currentIndex !== index) return item;

                              if (!selectedLot) {
                                return {
                                  ...item,
                                  material_lot_id: null,
                                };
                              }

                              return {
                                ...item,
                                material_lot_id: selectedLot.id,
                                material_id: selectedLot.material_id,
                                material_name: selectedLot.material_name,
                                lot_number: selectedLot.lot_number,
                                supplier_name: selectedLot.supplier_name ?? '',
                              };
                            }),
                          )
                        }
                        loadingText="Cargando lotes..."
                        noOptionsText="No hay lotes disponibles"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={
                              requireExistingMaterialLot
                                ? 'Lote controlado *'
                                : 'Lote controlado'
                            }
                            helperText={
                              requireExistingMaterialLot
                                ? 'Obligatorio según la política de trazabilidad de la planta.'
                                : 'Opcional. Al seleccionar un lote se rellenan material y lote.'
                            }
                          />
                        )}
                        sx={{
                          gridColumn: {
                            xs: 'auto',
                            md: '1 / -1',
                          },
                        }}
                      />

                      <TextField
                        size="small"
                        label="Material *"
                        value={usage.material_name}
                        disabled={Boolean(usage.material_lot_id) || requireExistingMaterialLot}
                        onChange={(event) =>
                          setMaterialUsagesDraft((current) =>
                            current.map((item, currentIndex) =>
                              currentIndex === index
                                ? {
                                    ...item,
                                    material_name: event.target.value,
                                  }
                                : item,
                            ),
                          )
                        }
                        fullWidth
                      />

                      <TextField
                        size="small"
                        label={requireLotOnMaterialUsage ? 'Lote *' : 'Lote'}
                        value={usage.lot_number}
                        disabled={Boolean(usage.material_lot_id)}
                        onChange={(event) =>
                          setMaterialUsagesDraft((current) =>
                            current.map((item, currentIndex) =>
                              currentIndex === index
                                ? {
                                    ...item,
                                    lot_number: event.target.value,
                                  }
                                : item,
                            ),
                          )
                        }
                        fullWidth
                      />

                      <TextField
                        size="small"
                        label="Proveedor"
                        value={usage.supplier_name}
                        disabled={Boolean(usage.material_lot_id)}
                        onChange={(event) =>
                          setMaterialUsagesDraft((current) =>
                            current.map((item, currentIndex) =>
                              currentIndex === index
                                ? {
                                    ...item,
                                    supplier_name: event.target.value,
                                  }
                                : item,
                            ),
                          )
                        }
                        fullWidth
                      />

                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(0, 1fr) 120px',
                          gap: 1,
                        }}
                      >
                        <TextField
                          size="small"
                          label="Cantidad"
                          type="number"
                          value={usage.quantity_value}
                          onChange={(event) =>
                            setMaterialUsagesDraft((current) =>
                              current.map((item, currentIndex) =>
                                currentIndex === index
                                  ? {
                                      ...item,
                                      quantity_value: event.target.value,
                                    }
                                  : item,
                              ),
                            )
                          }
                          inputProps={{
                            inputMode: 'decimal',
                            step: 'any',
                          }}
                          fullWidth
                        />

                        <TextField
                          size="small"
                          label="Unidad"
                          value={usage.quantity_unit}
                          onChange={(event) =>
                            setMaterialUsagesDraft((current) =>
                              current.map((item, currentIndex) =>
                                currentIndex === index
                                  ? {
                                      ...item,
                                      quantity_unit: event.target.value,
                                    }
                                  : item,
                              ),
                            )
                          }
                          fullWidth
                        />
                      </Box>
                    </Box>

                    <TextField
                      size="small"
                      label="Observaciones"
                      value={usage.notes}
                      onChange={(event) =>
                        setMaterialUsagesDraft((current) =>
                          current.map((item, currentIndex) =>
                            currentIndex === index
                              ? {
                                  ...item,
                                  notes: event.target.value,
                                }
                              : item,
                          ),
                        )
                      }
                      multiline
                      minRows={2}
                      fullWidth
                    />
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}

          <Button
            variant="outlined"
            startIcon={<AddRoundedIcon />}
            onClick={() =>
              setMaterialUsagesDraft((current) => [
                ...current,
                buildEmptyLineMaterialUsageDraft(current.length),
              ])
            }
          >
            Añadir material
          </Button>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={closeMaterialUsagesDialog}>
          Cancelar
        </Button>

        <Button
          variant="contained"
          onClick={handleApplyMaterialUsagesDraft}
        >
          Aplicar materiales
        </Button>
      </DialogActions>

    </Dialog>
  );

  const openLineNumericKeyboard = (
    field: NonNullable<typeof lineTouchKeyboard>['field'],
    title: string,
    section: 'identity' | 'production' | 'additional' | 'closure',
    options?: {
      extraFieldKey?: string;
      allowDecimal?: boolean;
      preserveLeadingZeros?: boolean;
      keyboardType?: 'numeric' | 'text';
    },
  ) => {
    if (!field) return;

    const value =
      field === 'extra_field'
        ? lineForm.extra_fields[options?.extraFieldKey ?? ''] ?? ''
        : lineForm[field];

    setLineTouchKeyboard({
      title,
      value,
      field,
      section,
      extraFieldKey: options?.extraFieldKey,
      allowDecimal: options?.allowDecimal,
      preserveLeadingZeros: options?.preserveLeadingZeros,
      keyboardType: options?.keyboardType ?? 'numeric',
      onConfirm: (nextValue) => {

        setLineForm((current) => {
          if (field === 'extra_field') {
            if (!options?.extraFieldKey) return current;

            return {
              ...current,
              extra_fields: {
                ...current.extra_fields,
                [options.extraFieldKey]: nextValue,
              },
            };
          }

          return {
            ...current,
            [field]: nextValue,
          };
        });
      },
    });
  };

  const renderLineNumericAdornment = (
    field: NonNullable<typeof lineTouchKeyboard>['field'],
    title: string,
    section: 'identity' | 'production' | 'additional' | 'closure',
    options?: {
      extraFieldKey?: string;
      allowDecimal?: boolean;
      preserveLeadingZeros?: boolean;
      keyboardType?: 'numeric' | 'text';
    },
  ) =>

    touchModeEnabled && isDraft ? (
      <InputAdornment position="end">
        <IconButton
          aria-label={`Abrir teclado PROA para ${title}`}
          onPointerDown={(event) => event.preventDefault()}
          onClick={() =>
            openLineNumericKeyboard(field, title, section, options)
          }
          edge="end"
          sx={{ color: '#38bdf8' }}
        >
          <KeyboardRoundedIcon />
        </IconButton>
      </InputAdornment>
    ) : undefined;

  const renderLineNumericKeyboard = (
    section: 'identity' | 'production' | 'additional' | 'closure',
  ) => {
    const keyboard = lineTouchKeyboard;

    if (
      !touchModeEnabled ||
      !isDraft ||
      !keyboard ||
      keyboard.section !== section ||
      !keyboard.field
    ) {
      return null;
    }

    const value =
      keyboard.field === 'extra_field'
        ? lineForm.extra_fields[keyboard.extraFieldKey ?? ''] ?? ''
        : lineForm[keyboard.field];

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          width: '100%',
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: keyboard.keyboardType === 'text' ? 650 : 330,
            maxWidth: '100%',
            minWidth: 0,
          }}
        >
          {keyboard.keyboardType === 'text' ? (
            <ProductionOperatorInlineTextKeyboard
              title={keyboard.title}
              value={value}
              onChange={(nextValue) => keyboard.onConfirm(nextValue)}
              onClose={() => setLineTouchKeyboard(null)}
            />
          ) : (
            <ProductionOperatorInlineNumericKeyboard
              title={keyboard.title}
              value={value}
              allowDecimal={keyboard.allowDecimal}
              preserveLeadingZeros={keyboard.preserveLeadingZeros}
              onChange={(nextValue) => keyboard.onConfirm(nextValue)}
              onClose={() => setLineTouchKeyboard(null)}
            />
          )}
        </Box>

      </Box>
    );
  };

  const renderLineDialog = () => (
    <Dialog
      open={lineDialogOpen}

      onClose={() => {
        setLineDialogOpen(false);
        setEditingLineId(null);
        setLineForm(buildEmptyLineForm());
        setPendingTimelineResolution(null);
        setPendingIncompleteEventResolution(null);
      }}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle>
        {editingLineId ? 'Editar orden de fabricación' : 'Iniciar orden de fabricación'}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2.25} sx={{ pt: 1 }}>
          <Alert
            severity="info"
            variant="outlined"
            action={
              materialUsageCaptureEnabled ? (
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  onClick={openMaterialUsagesDialog}
                >
                  {lineForm.material_usages.length > 0
                    ? `Materiales y lotes · ${lineForm.material_usages.length}`
                    : 'Añadir materiales y lotes'}
                </Button>
              ) : undefined
            }
          >
            {materialUsageCaptureEnabled
              ? 'Completa la OF y los datos operativos de fabricación.'
              : 'Completa la OF y los datos operativos de fabricación. La captura de materiales/lotes está desactivada para esta planta.'}
          </Alert>

          {resolvedExistingOrderSummary?.pendingQuantity !== null &&
          resolvedExistingOrderSummary?.pendingQuantity !== undefined ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid rgba(251,191,36,0.3)',
                bgcolor: 'rgba(120,53,15,0.15)',
                px: 2,
                py: 1.25,
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  <Stack spacing={0}>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Total OF
                    </Typography>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '0.95rem' }}>
                      {resolvedExistingOrderSummary.totalQuantity ?? '—'}{' '}
                      <Typography component="span" sx={{ color: '#94a3b8', fontSize: '0.82rem', fontWeight: 400 }}>
                        {resolvedExistingOrderSummary.quantityUnit || ''}
                      </Typography>
                    </Typography>
                  </Stack>
                  <Stack spacing={0}>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Fabricado
                    </Typography>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '0.95rem' }}>
                      {resolvedExistingOrderSummary.totalQuantity !== null && resolvedExistingOrderSummary.pendingQuantity !== null
                        ? (resolvedExistingOrderSummary.totalQuantity - resolvedExistingOrderSummary.pendingQuantity)
                        : '—'}{' '}
                      <Typography component="span" sx={{ color: '#94a3b8', fontSize: '0.82rem', fontWeight: 400 }}>
                        {resolvedExistingOrderSummary.quantityUnit || ''}
                      </Typography>
                    </Typography>
                  </Stack>
                  <Stack spacing={0}>
                    <Typography sx={{ color: '#fbbf24', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>
                      Pendiente
                    </Typography>
                    <Typography sx={{ color: '#fbbf24', fontWeight: 900, fontSize: '0.95rem' }}>
                      {resolvedExistingOrderSummary.pendingQuantity}{' '}
                      <Typography component="span" sx={{ color: '#fbbf24', fontSize: '0.82rem', fontWeight: 400, opacity: 0.8 }}>
                        {resolvedExistingOrderSummary.quantityUnit || ''}
                      </Typography>
                    </Typography>
                  </Stack>
                </Stack>
                {resolvedExistingOrderSummary.pendingQuantity === 0 ? (
                  <Chip
                    label="OF completada"
                    size="small"
                    sx={{ bgcolor: 'rgba(34,197,94,0.15)', color: '#86efac', border: '1px solid rgba(34,197,94,0.25)', fontWeight: 700 }}
                  />
                ) : null}
              </Stack>
            </Paper>
          ) : null}

          {lineDefaultsSuggested ? (
            <Alert
              severity="warning"
              variant="outlined"
              onClose={() => setLineDefaultsSuggested(false)}
            >
              Velocidad y campos adicionales completados con el último registro de este producto en esta línea. Revísalos antes de continuar.
            </Alert>
          ) : null}

          <Stack spacing={1.5}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(59,130,246,0.18)',
                bgcolor: 'rgba(15,23,42,0.32)',
                p: 1.5,
              }}
            >
              <Stack spacing={1.35}>
                <Stack spacing={0.25}>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                    Identificación de la orden
                  </Typography>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem' }}>
                    Primero identifica la OF y completa sus datos relacionados.
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, minmax(0, 1fr))',
                      lg: 'repeat(4, minmax(0, 1fr))',
                    },
                    gap: 1,
                  }}
                >
                  {isOrderFieldVisible('manufacturing_order_number') ? (
                    <Autocomplete
                      freeSolo
                      openOnFocus={false}
                      options={filteredExistingOrderCandidates}
                      loading={existingOrderCandidatesQuery.isFetching}
                      loadingText="Buscando OFs..."
                      noOptionsText={
                        existingOrderSearchText.trim().length < 2
                          ? 'Escribe al menos 2 caracteres de la OF.'
                          : 'No se han encontrado OFs.'
                      }
                      inputValue={lineForm.manufacturing_order_number}
                      getOptionLabel={(option) =>
                        typeof option === 'string'
                          ? option
                          : option.manufacturing_order_number
                      }
                      isOptionEqualToValue={(option, value) =>
                        typeof value !== 'string' &&
                        option.line_id === value.line_id
                      }
                      filterOptions={(options) => options}
                      onInputChange={(_event, value, reason) => {
                        if (reason === 'reset') return;

                        const normalizedValue = value.trim();
                        const normalizedResolvedExistingOrderNumber =
                          resolvedExistingOrderNumber.trim();

                        if (
                          normalizedResolvedExistingOrderNumber &&
                          normalizedValue !== normalizedResolvedExistingOrderNumber
                        ) {
                          clearResolvedExistingOrderFromLineForm(value);
                        } else {
                          setLineForm((current) => ({
                            ...current,
                            manufacturing_order_number: value,
                          }));
                        }

                        setExistingOrderSearchText(value);
                      }}
                      onChange={(_event, value) => {
                        if (typeof value === 'string') {
                          setLineForm((current) => ({
                            ...current,
                            manufacturing_order_number: value,
                          }));
                          setExistingOrderSearchText(value);
                          return;
                        }

                        void handleSelectExistingManufacturingOrder(value);
                      }}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Stack spacing={0.2}>
                            <Typography sx={{ color: '#e2e8f0', fontWeight: 800 }}>
                              {option.manufacturing_order_number}
                            </Typography>
                            <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                              {option.product_label || 'Sin producto'} · Pendiente{' '}
                              {option.remaining_quantity_value ?? option.quantity_value ?? '—'}{' '}
                              {option.quantity_unit || ''} ·{' '}
                              {option.source_type === 'imported_order'
                                ? 'Precargada'
                                : 'En progreso'}
                            </Typography>
                          </Stack>
                        </Box>
                      )}

                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          label={getOrderFieldLabel(
                            'manufacturing_order_number',
                            'OF',
                          )}

                          onBlur={(event) => {
                            if (lineTouchKeyboard?.title === 'OF') return;
                            void handleResolveExistingManufacturingOrder(event.target.value);
                          }}
                          InputProps={{

                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {touchModeEnabled && isDraft ? (
                                  <InputAdornment position="end">
                                    <IconButton
                                      aria-label="Abrir teclado PROA para OF"
                                      onPointerDown={(event) => event.preventDefault()}
                                      onClick={() =>
                                        setLineTouchKeyboard({
                                          title: 'OF',
                                          value: lineForm.manufacturing_order_number,
                                          preserveLeadingZeros: true,
                                          onConfirm: (value) => {
                                            const normalizedValue = value.trim();
                                            const normalizedResolvedOrder =
                                              resolvedExistingOrderNumber.trim();

                                            if (
                                              normalizedResolvedOrder &&
                                              normalizedValue !== normalizedResolvedOrder
                                            ) {
                                              clearResolvedExistingOrderFromLineForm(value);
                                            } else {
                                              setLineForm((current) => ({
                                                ...current,
                                                manufacturing_order_number: value,
                                              }));
                                            }

                                            setExistingOrderSearchText(value);

                                          },
                                        })
                                      }
                                      edge="end"
                                      sx={{ color: '#38bdf8' }}
                                    >
                                      <KeyboardRoundedIcon />
                                    </IconButton>
                                  </InputAdornment>
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                          disabled={!isDraft || resolvingExistingOrder}
                          fullWidth
                        />
                      )}

                    />
                  ) : null}

                  {isOrderFieldVisible('customer') ? (
                    <TextField
                      size="small"
                      label={getOrderFieldLabel('customer', 'Cliente')}
                      value={lineForm.customer_number}
                      onChange={(event) =>
                        setLineForm((current) => ({
                          ...current,
                          customer_number: event.target.value,
                        }))
                      }
                      InputProps={{
                        endAdornment: renderLineNumericAdornment(
                          'customer_number',
                          'Cliente',
                          'identity',
                          { preserveLeadingZeros: true },
                        ),
                      }}
                      disabled={!isDraft}
                      fullWidth
                    />
                  ) : null}


                  {isOrderFieldVisible('customer_order') ? (
                    <TextField
                      size="small"
                      label={getOrderFieldLabel('customer_order', 'Pedido cliente')}
                      value={lineForm.customer_order_number}
                      onChange={(event) =>
                        setLineForm((current) => ({
                          ...current,
                          customer_order_number: event.target.value,
                        }))
                      }
                      disabled={!isDraft}
                      fullWidth
                    />
                  ) : null}


                  {isOrderFieldVisible('product') ? (
                    <TextField
                      size="small"
                      label="Referencia libre"
                      value={lineForm.free_product_ref}
                      onChange={(event) =>
                        setLineForm((current) => ({
                          ...current,
                          free_product_ref: event.target.value,
                        }))
                      }
                      InputProps={{
                        endAdornment: renderLineNumericAdornment(
                          'free_product_ref',
                          'Referencia libre',
                          'identity',
                          { preserveLeadingZeros: true },
                        ),
                      }}
                      disabled={!isDraft}
                      fullWidth
                    />
                  ) : null}


                  {orderIdentificationAdditionalFields.map((field) => (
                    <TextField
                      key={field.key}
                      size="small"
                      label={field.label}
                      type="text"
                      value={lineForm.extra_fields[field.key] ?? ''}
                      onChange={(event) =>
                        setLineForm((current) => ({
                          ...current,
                          extra_fields: {
                            ...current.extra_fields,
                            [field.key]: event.target.value,
                          },
                        }))
                      }
                      InputProps={{
                        endAdornment: renderLineNumericAdornment(
                          'extra_field',
                          field.label,
                          'identity',
                          {
                            extraFieldKey: field.key,
                            allowDecimal: field.field_type === 'number',
                            keyboardType:
                              field.field_type === 'number' ? 'numeric' : 'text',
                          },
                        ),
                      }}
                      disabled={!isDraft}
                      fullWidth
                    />

                  ))}

                </Box>

                {renderLineNumericKeyboard('identity')}

                {touchModeEnabled &&
                isDraft &&
                lineTouchKeyboard?.title === 'OF' ? (

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: 'minmax(0, 1fr) 330px',
                      },
                      gap: 1.25,
                      alignItems: 'start',
                    }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: '1px solid rgba(148,163,184,0.2)',
                        bgcolor: 'rgba(15,23,42,0.6)',
                        p: 1.5,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#f8fafc',
                          fontWeight: 900,
                          mb: 1,
                        }}
                      >
                        Órdenes encontradas
                      </Typography>

                      <Stack
                        spacing={0.75}
                        sx={{
                          maxHeight: 295,
                          overflowY: 'auto',
                        }}
                      >
                        {existingOrderSearchText.trim().length < 2 ? (
                          <Typography sx={{ color: '#94a3b8' }}>
                            Introduce al menos dos cifras.
                          </Typography>
                        ) : existingOrderCandidatesQuery.isFetching ||
                          debouncedExistingOrderSearchText !==
                            existingOrderSearchText.trim() ? (
                          <Typography sx={{ color: '#94a3b8' }}>
                            Buscando órdenes...
                          </Typography>
                        ) : existingOrderCandidatesQuery.isError ? (
                          <Typography sx={{ color: '#fca5a5' }}>
                            No se pudo consultar las órdenes.
                          </Typography>
                        ) : filteredExistingOrderCandidates.length === 0 ? (
                          <Typography sx={{ color: '#94a3b8' }}>
                            No hay coincidencias.
                          </Typography>
                        ) : (
                          filteredExistingOrderCandidates.map((option) => (
                            <Button
                              key={option.line_id}
                              variant="outlined"
                              disabled={resolvingExistingOrder}
                              onClick={() => {
                                setLineTouchKeyboard(null);
                                void handleSelectExistingManufacturingOrder(option);
                              }}
                              sx={{
                                justifyContent: 'flex-start',
                                textAlign: 'left',
                                textTransform: 'none',
                                borderRadius: 2,
                                borderColor: 'rgba(56,189,248,0.22)',
                                bgcolor: 'rgba(30,41,59,0.5)',
                                px: 1.25,
                                py: 1,
                              }}
                            >
                              <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                                <Typography
                                  sx={{
                                    color: '#f8fafc',
                                    fontWeight: 900,
                                  }}
                                >
                                  {option.manufacturing_order_number}
                                </Typography>

                                <Typography
                                  sx={{
                                    color: '#94a3b8',
                                    fontSize: '0.8rem',
                                    whiteSpace: 'normal',
                                  }}
                                >
                                  {option.product_label || 'Sin producto'}
                                  {' · '}
                                  Pendiente{' '}
                                  {option.remaining_quantity_value ??
                                    option.quantity_value ??
                                    '—'}{' '}
                                  {option.quantity_unit || ''}
                                </Typography>
                              </Stack>
                            </Button>
                          ))
                        )}
                      </Stack>
                    </Paper>

                    <ProductionOperatorInlineNumericKeyboard
                      title="OF"
                      value={lineForm.manufacturing_order_number}
                      preserveLeadingZeros
                      onChange={(value) => {
                        lineTouchKeyboard?.onConfirm(value);
                      }}
                      onClose={() => {
                        setLineTouchKeyboard(null);

                        const value = lineForm.manufacturing_order_number;

                        if (
                          value.trim() &&
                          value.trim() !== resolvedExistingOrderNumber.trim()
                        ) {
                          void handleResolveExistingManufacturingOrder(value);
                        }
                      }}
                    />
                  </Box>
                ) : null}

                {resolvedExistingOrderSummary ? (

                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 2.5,
                      border: '1px solid rgba(251,191,36,0.22)',
                      bgcolor: 'rgba(120,53,15,0.12)',
                      px: 1.25,
                      py: 0.9,
                    }}
                  >
                    <Typography sx={{ color: '#fbbf24', fontSize: '0.78rem', fontWeight: 800 }}>
                      Total OF: {resolvedExistingOrderSummary.totalQuantity ?? '—'}{' '}
                      {resolvedExistingOrderSummary.quantityUnit || ''} · Pendiente:{' '}
                      {resolvedExistingOrderSummary.pendingQuantity ?? '—'}{' '}
                      {resolvedExistingOrderSummary.quantityUnit || ''}
                    </Typography>
                  </Paper>
                ) : null}
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(16,185,129,0.18)',
                bgcolor: 'rgba(15,23,42,0.32)',
                p: 1.5,
              }}
            >
              <Stack spacing={1.35}>
                <Stack spacing={0.25}>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                    Parámetros de fabricación
                  </Typography>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem' }}>
                    Define la cantidad, la velocidad y los multiplicadores operativos.
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, minmax(0, 1fr))',
                      lg: 'repeat(4, minmax(0, 1fr))',
                    },
                    gap: 1,
                  }}
                >

                  {isOrderFieldVisible('planned_quantity') ? (
                    <TextField
                      size="small"
                      label={getOrderFieldLabel('planned_quantity', 'Cantidad prevista')}
                      type="number"
                      value={lineForm.quantity_value}
                      onChange={(event) =>
                        setLineForm((current) => ({
                          ...current,
                          quantity_value: event.target.value,
                        }))
                      }
                      inputProps={{
                        inputMode: 'decimal',
                        step: 'any',
                      }}
                      InputProps={{
                        endAdornment: renderLineNumericAdornment(
                          'quantity_value',
                          'Cantidad prevista',
                          'production',
                          { allowDecimal: true },
                        ),
                      }}
                      disabled={!isDraft}
                      fullWidth
                    />
                  ) : null}



                  {isOrderFieldVisible('speed') ? (
                    <TextField
                      size="small"
                      label={getOrderFieldLabel('speed', 'Velocidad')}
                      type="number"
                      value={lineForm.speed_value}
                      onChange={(event) =>
                        setLineForm((current) => ({
                          ...current,
                          speed_value: event.target.value,
                        }))
                      }
                      inputProps={{
                        inputMode: 'decimal',
                        step: 'any',
                      }}
                      InputProps={{
                        endAdornment: renderLineNumericAdornment(
                          'speed_value',
                          'Velocidad',
                          'production',
                          { allowDecimal: true },
                        ),
                      }}
                      disabled={!isDraft}
                      fullWidth
                    />
                  ) : null}



                  {orderProductionAdditionalFields.map((field) => (
                    <TextField
                      key={field.key}
                      size="small"
                      label={field.label}
                      type="number"
                      value={lineForm.extra_fields[field.key] ?? ''}
                      onChange={(event) =>
                        setLineForm((current) => ({
                          ...current,
                          extra_fields: {
                            ...current.extra_fields,
                            [field.key]: event.target.value,
                          },
                        }))
                      }
                      inputProps={{
                        inputMode: 'decimal',
                        step: 'any',
                      }}
                      InputProps={{
                        endAdornment: renderLineNumericAdornment(
                          'extra_field',
                          field.label,
                          'production',
                          {
                            extraFieldKey: field.key,
                            allowDecimal: true,
                          },
                        ),
                      }}
                      disabled={!isDraft}
                      fullWidth
                    />
                  ))}
                </Box>

                {renderLineNumericKeyboard('production')}
              </Stack>
            </Paper>

            {orderGenericAdditionalFields.length > 0 ? (

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(148,163,184,0.16)',
                  bgcolor: 'rgba(15,23,42,0.28)',
                  p: 1.5,
                }}
              >
                <Stack spacing={1.35}>
                  <Stack spacing={0.25}>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                      Datos adicionales
                    </Typography>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem' }}>
                      Campos propios de esta operación que complementan la orden.
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, minmax(0, 1fr))',
                        lg: 'repeat(4, minmax(0, 1fr))',
                      },
                      gap: 1,
                    }}
                  >

                    {orderGenericAdditionalFields.map((field) => (
                      <TextField
                        key={field.key}
                        size="small"
                        label={field.label}
                        type={field.field_type === 'number' ? 'number' : 'text'}
                        inputProps={
                          field.field_type === 'number'
                            ? {
                                inputMode: 'decimal',
                                step: 'any',
                              }
                            : undefined
                        }
                        value={lineForm.extra_fields[field.key] ?? ''}
                        onChange={(event) =>
                          setLineForm((current) => ({
                            ...current,
                            extra_fields: {
                              ...current.extra_fields,
                              [field.key]: event.target.value,
                            },
                          }))
                        }
                        InputProps={{
                          endAdornment: renderLineNumericAdornment(
                            'extra_field',
                            field.label,
                            'additional',
                            {
                              extraFieldKey: field.key,
                              allowDecimal: field.field_type === 'number',
                              keyboardType:
                                field.field_type === 'number' ? 'numeric' : 'text',
                            },
                          ),
                        }}

                        disabled={!isDraft}
                        fullWidth
                      />
                    ))}
                  </Box>

                  {renderLineNumericKeyboard('additional')}
                </Stack>
              </Paper>
            ) : null}

            <Paper

              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(148,163,184,0.16)',
                bgcolor: 'rgba(15,23,42,0.28)',
                p: 1.5,
              }}
            >
              <Stack spacing={1.35}>
                <Stack spacing={0.25}>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                    Tiempos de orden
                  </Typography>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem' }}>
                    Separa el tiempo de preparación del inicio real de fabricación.
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      md: 'repeat(3, minmax(0, 1fr))',
                    },
                    gap: 1,
                  }}
                >
                  <Stack direction="row" spacing={1}>
                    <TextField
                      size="small"
                      label="Inicio orden"
                      type="time"
                      value={lineForm.start_time}
                      onChange={(event) => {
                        const nextStartTime = event.target.value;

                        setLineForm((current) => ({
                          ...current,
                          start_time: nextStartTime,
                          production_start_time:
                            !current.production_start_time ||
                            current.production_start_time === current.start_time
                              ? nextStartTime
                              : current.production_start_time,
                        }));
                      }}
                      InputLabelProps={{ shrink: true }}
                      disabled={!isDraft}
                      fullWidth
                    />
                    <IconButton onClick={setLineStartNow} disabled={!isDraft}>
                      <AccessTimeRoundedIcon />
                    </IconButton>
                  </Stack>

                  <TextField
                    size="small"
                    label="Inicio fabricación"
                    type="time"
                    value={currentProductionStartTime}
                    onChange={(event) =>
                      setLineForm((current) => ({
                        ...current,
                        production_start_time: event.target.value,
                      }))
                    }
                    error={Boolean(currentProductionStartTimeValidationMessage)}
                    helperText={currentProductionStartTimeValidationMessage || ' '}
                    InputLabelProps={{ shrink: true }}
                    disabled={!isDraft}
                    fullWidth
                  />

                  <Stack direction="row" spacing={1}>
                    <TextField
                      size="small"
                      label="Hora fin"
                      type="time"
                      value={lineForm.end_time}
                      onChange={(event) =>
                        setLineForm((current) => ({
                          ...current,
                          end_time: event.target.value,
                        }))
                      }
                      InputLabelProps={{ shrink: true }}
                      disabled={!isDraft}
                      fullWidth
                    />
                    <IconButton onClick={setLineEndNow} disabled={!isDraft}>
                      <AccessTimeRoundedIcon />
                    </IconButton>
                  </Stack>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      lg: 'minmax(0, 0.95fr) minmax(0, 1.45fr)',
                    },
                    gap: 1,
                    alignItems: 'stretch',
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 2.5,
                      border: '1px solid rgba(148,163,184,0.12)',
                      bgcolor: 'rgba(2,6,23,0.24)',
                      p: 1,
                    }}
                  >
                    <Stack spacing={0.75}>
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.72rem', fontWeight: 850 }}>
                        Atajos inicio fabricación
                      </Typography>

                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {(() => {
                          const nowTime = getCurrentTimeValue();
                          const nowValidationMessage = getProductionStartTimeValidationMessage(nowTime);
                          const selected = currentProductionStartTime === nowTime;

                          return (
                            <Button
                              size="small"
                              variant={selected ? 'contained' : 'outlined'}
                              disabled={!isDraft || Boolean(nowValidationMessage)}
                              onClick={() => applyProductionStartTime(nowTime)}
                              sx={{
                                minWidth: 72,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 900,
                                px: 1,
                              }}
                            >
                              Ahora
                            </Button>
                          );
                        })()}

                        {[0, 15, 30, 45].map((minutes) => {
                          const optionTime = lineForm.start_time
                            ? addMinutesToTime(lineForm.start_time, minutes)
                            : '';

                          if (!optionTime) return null;

                          const validationMessage = getProductionStartTimeValidationMessage(optionTime);
                          const selected = currentProductionStartTime === optionTime;

                          return (
                            <Button
                              key={minutes}
                              size="small"
                              variant={selected ? 'contained' : 'outlined'}
                              disabled={!isDraft || Boolean(validationMessage)}
                              onClick={() => applyProductionStartTime(optionTime)}
                              sx={{
                                minWidth: 68,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 850,
                                px: 1,
                              }}
                            >
                              {optionTime}
                            </Button>
                          );
                        })}
                      </Stack>
                    </Stack>
                  </Paper>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(3, minmax(0, 1fr))',
                      },
                      gap: 1,
                    }}
                  >
                    {[
                      {
                        label: 'Preparación',
                        value:
                          currentLineSetupDuration === null
                            ? '—'
                            : formatEstimatedMinutes(currentLineSetupDuration),
                      },
                      {
                        label: 'Producción real',
                        value:
                          currentLineProductionDuration === null
                            ? '—'
                            : formatEstimatedMinutes(currentLineProductionDuration),
                      },
                      {
                        label: 'Total ocupado',
                        value:
                          currentLineDuration === null
                            ? '—'
                            : formatEstimatedMinutes(currentLineDuration),
                      },
                    ].map((item) => (
                      <Paper
                        key={item.label}
                        elevation={0}
                        sx={{
                          borderRadius: 2,
                          border: '1px solid rgba(148,163,184,0.12)',
                          bgcolor: 'rgba(2,6,23,0.24)',
                          p: 1,
                        }}
                      >
                        <Typography sx={{ color: '#64748b', fontSize: '0.7rem', fontWeight: 850 }}>
                          {item.label}
                        </Typography>
                        <Typography sx={{ color: '#f8fafc', fontWeight: 950 }}>
                          {item.value}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Box>
              </Stack>
            </Paper>
          </Stack>

          {isOrderFieldVisible('observations') ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(148,163,184,0.16)',
                bgcolor: 'rgba(15,23,42,0.32)',
                p: 1.5,
              }}
            >
              <Stack spacing={1}>
                <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                  Observaciones
                </Typography>

                <TextField
                  size="small"
                  label={getOrderFieldLabel('observations', 'Observaciones')}
                  value={lineForm.notes}
                  onChange={(event) =>
                    setLineForm((current) => ({
                      ...current,
                      notes: event.target.value,
                    }))
                  }
                  disabled={!isDraft}
                  multiline
                  minRows={2}
                  fullWidth
                />
              </Stack>
            </Paper>
          ) : null}

          {editingLineId &&
          (detail?.lines ?? []).some((line) => line.id === editingLineId && Boolean(line.end_time)) &&
          (isOrderFieldVisible('actual_quantity') || isOrderFieldRequiredOnClose('actual_quantity')) ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: isOrderFieldRequiredOnClose('actual_quantity')
                  ? '1px solid rgba(239,68,68,0.28)'
                  : '1px solid rgba(148,163,184,0.18)',
                bgcolor: isOrderFieldRequiredOnClose('actual_quantity')
                  ? 'rgba(127,29,29,0.12)'
                  : 'rgba(15,23,42,0.32)',
                p: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                  {isOrderFieldRequiredOnClose('actual_quantity')
                    ? 'Campos obligatorios al cierre'
                    : 'Datos de cierre'}
                </Typography>

                <TextField
                  size="small"
                  type="number"
                  inputProps={{
                    inputMode: 'decimal',
                    step: 'any',
                  }}
                  label={getOrderFieldLabel('actual_quantity', 'Cantidad real')}
                  value={lineForm.actual_quantity_value}
                  onChange={(event) =>
                    setLineForm((current) => ({
                      ...current,
                      actual_quantity_value: event.target.value,
                    }))
                  }
                  InputProps={{
                    endAdornment:
                      getLinePendingQuantityValue(lineForm) !== null ? (
                        <InputAdornment position="end">
                          <Typography
                            sx={{
                              color: '#94a3b8',
                              fontSize: '0.82rem',
                              fontWeight: 700,
                            }}
                          >
                            /{getLinePendingQuantityValue(lineForm)}
                          </Typography>
                        </InputAdornment>
                      ) : undefined,
                  }}
                  fullWidth
                />
              </Stack>
            </Paper>
          ) : null}

          {currentLineEstimation.enabled ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: '1px solid rgba(59,130,246,0.22)',
                bgcolor: 'rgba(15,23,42,0.32)',
                p: 1.5,
              }}
            >
              <Stack spacing={1}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1}
                  justifyContent="space-between"
                >
                  <Stack spacing={0.3}>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                      Estimación teórica
                    </Typography>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.86rem' }}>
                      Fórmula: {currentLineEstimation.formulaLabel}
                      {currentLineEstimation.usesSetupTime ? ' + preparación' : ''}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      label={`Duración teórica: ${formatEstimatedMinutes(
                        currentLineEstimation.estimatedDurationMinutes,
                      )}`}
                      sx={{
                        bgcolor: 'rgba(14,165,233,0.14)',
                        color: '#bae6fd',
                        fontWeight: 800,
                      }}
                    />
                    {operationSettings?.estimation_config?.enable_estimated_end_time ? (
                      <Chip
                        size="small"
                        label={`Hora teórica fin: ${
                          currentLineEstimation.estimatedEndTime || '—'
                        }`}
                        sx={{
                          bgcolor: 'rgba(34,197,94,0.13)',
                          color: '#bbf7d0',
                          fontWeight: 800,
                        }}
                      />
                    ) : null}
                  </Stack>
                </Stack>

                {currentLineEstimation.missingInputs.length > 0 ? (
                  <Alert severity="warning" variant="outlined">
                    Para calcular la estimación falta informar:{' '}
                    {currentLineEstimation.missingInputs.join(', ')}.
                  </Alert>
                ) : null}
              </Stack>
            </Paper>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => {
            setLineDialogOpen(false);
            setEditingLineId(null);
            setLineForm(buildEmptyLineForm());
            setPendingTimelineResolution(null);
            setPendingIncompleteEventResolution(null);
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveLine}
          disabled={
            !isDraft ||
            lineWriteLocked ||
            createLineMutation.isPending ||
            updateLineMutation.isPending
          }
        >
          {lineWriteLocked
            ? 'Guardando...'
            : editingLineId
              ? 'Guardar cambios'
              : 'Iniciar orden'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderDeleteLineDialog = () => (
    <Dialog
      open={Boolean(lineToDelete)}
      onClose={() => {
        if (!deleteLineMutation.isPending) setLineToDelete(null);
      }}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>Eliminar orden del parte</DialogTitle>

      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          <Alert severity="warning" variant="outlined">
            Esta acción eliminará la orden del parte. No debe usarse para corregir una orden que simplemente necesita editarse.
          </Alert>

          {lineToDelete && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(148,163,184,0.18)',
                bgcolor: 'rgba(2,6,23,0.28)',
                px: 2,
                py: 1.5,
              }}
            >
              <Stack spacing={0.35}>
                <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                  {getLineDisplayName(lineToDelete)}
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                  OF {lineToDelete.manufacturing_order_number || '—'} · {lineToDelete.quantity_value ?? '—'} {lineToDelete.quantity_unit} · {formatTime(lineToDelete.start_time)} - {formatTime(lineToDelete.end_time)}
                </Typography>
              </Stack>
            </Paper>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => setLineToDelete(null)}
          disabled={deleteLineMutation.isPending}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={!lineToDelete || deleteLineMutation.isPending}
          onClick={() => {
            if (!lineToDelete) return;
            deleteLineMutation.mutate(lineToDelete.id);
          }}
        >
          Eliminar orden
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderFinishLineDialog = () => (
    <Dialog
      open={finishLineDialogOpen}
      onClose={() => {
        if (
          !lineWriteLocked &&
          !updateLineMutation.isPending &&
          !createStopMutation.isPending
        ) {
          closeFinishLineDialog();
        }
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Finalizar orden</DialogTitle>

      <DialogContent>
        <Stack spacing={2.25} sx={{ pt: 1 }}>
          <Alert severity="info" variant="outlined">
            Completa los datos pendientes antes de cerrar la orden. Primero se muestran los obligatorios al cierre y después otros datos vacíos que también puedes aprovechar para completar.
          </Alert>

          {activeLine && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(148,163,184,0.18)',
                bgcolor: 'rgba(2,6,23,0.28)',
                px: 2,
                py: 1.5,
              }}
            >
              <Stack spacing={0.35}>
                <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                  {getLineDisplayName(activeLine)}
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                  OF {activeLine.manufacturing_order_number || '—'} · Inicio {formatTime(activeLine.start_time)}
                </Typography>
              </Stack>
            </Paper>
          )}

          <TextField
            size="small"
            type="time"
            label="Hora fin"
            value={finishLineEndTime}
            onChange={(event) => setFinishLineEndTime(event.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          {getShiftTimeValidationMessage(
            finishLineEndTime,
            'La hora de fin',
            configuredShiftStartTime,
            configuredShiftEndTime,
          ) ? (
            <Alert
              severity="warning"
              variant="outlined"
              action={
                configuredShiftEndTime ? (
                  <Button size="small" onClick={() => setFinishLineEndTime(configuredShiftEndTime)}>
                    Llevar a fin de turno
                  </Button>
                ) : undefined
              }
            >
              {getShiftTimeValidationMessage(
                finishLineEndTime,
                'La hora de fin',
                configuredShiftStartTime,
                configuredShiftEndTime,
              )}
            </Alert>
          ) : null}

          {(operationSettings?.order_fields_config?.manufacturing_order_number?.required &&
            !finishLineForm.manufacturing_order_number.trim()) ||
          operationSettings?.order_fields_config?.actual_quantity?.required ||
          (operationSettings?.order_fields_config?.observations?.required &&
            !finishLineForm.notes.trim()) ||
          (operationSettings?.order_fields_config?.customer?.required &&
            !finishLineForm.customer_number.trim()) ||
          (operationSettings?.order_fields_config?.customer_order?.required &&
            !finishLineForm.customer_order_number.trim()) ||
          (operationSettings?.order_fields_config?.speed?.required &&
            !finishLineForm.speed_value.trim()) ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(239,68,68,0.28)',
                bgcolor: 'rgba(127,29,29,0.12)',
                p: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                  Campos obligatorios al cierre
                </Typography>

                {operationSettings?.order_fields_config?.manufacturing_order_number?.required &&
                  !finishLineForm.manufacturing_order_number.trim() && (
                    <TextField
                      size="small"
                      label={getOrderFieldLabel('manufacturing_order_number', 'OF')}
                      value={finishLineForm.manufacturing_order_number}
                      onChange={(event) =>
                        setFinishLineForm((current) => ({
                          ...current,
                          manufacturing_order_number: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                  )}

                {operationSettings?.order_fields_config?.customer?.required &&
                  !finishLineForm.customer_number.trim() && (
                    <TextField
                      size="small"
                      label={getOrderFieldLabel('customer', 'Cliente')}
                      value={finishLineForm.customer_number}
                      onChange={(event) =>
                        setFinishLineForm((current) => ({
                          ...current,
                          customer_number: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                  )}

                {operationSettings?.order_fields_config?.customer_order?.required &&
                  !finishLineForm.customer_order_number.trim() && (
                    <TextField
                      size="small"
                      label={getOrderFieldLabel('customer_order', 'Pedido cliente')}
                      value={finishLineForm.customer_order_number}
                      onChange={(event) =>
                        setFinishLineForm((current) => ({
                          ...current,
                          customer_order_number: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                  )}

                {operationSettings?.order_fields_config?.actual_quantity?.required && (
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{
                      inputMode: 'decimal',
                      step: 'any',
                    }}
                    label={getOrderFieldLabel('actual_quantity', 'Cantidad real')}
                    value={finishLineForm.actual_quantity_value}
                    onChange={(event) =>
                      setFinishLineForm((current) => ({
                        ...current,
                        actual_quantity_value: event.target.value,
                      }))
                    }
                    InputProps={{
                      endAdornment: getPendingQuantityAdornmentValue(activeLine) ? (
                        <InputAdornment position="end">
                          <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem', fontWeight: 700 }}>
                            {getPendingQuantityAdornmentValue(activeLine)}
                          </Typography>
                        </InputAdornment>
                      ) : undefined,
                    }}
                    fullWidth
                  />
                )}

                {operationSettings?.order_fields_config?.speed?.required &&
                  !finishLineForm.speed_value.trim() && (
                    <TextField
                      size="small"
                      type="number"
                      inputProps={{
                        inputMode: 'decimal',
                        step: 'any',
                      }}
                      label={getOrderFieldLabel('speed', 'Velocidad')}
                      value={finishLineForm.speed_value}
                      onChange={(event) =>
                        setFinishLineForm((current) => ({
                          ...current,
                          speed_value: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                  )}

                {operationSettings?.order_fields_config?.observations?.required &&
                  !finishLineForm.notes.trim() && (
                    <TextField
                      size="small"
                      label={getOrderFieldLabel('observations', 'Observaciones')}
                      value={finishLineForm.notes}
                      onChange={(event) =>
                        setFinishLineForm((current) => ({
                          ...current,
                          notes: event.target.value,
                        }))
                      }
                      multiline
                      minRows={2}
                      fullWidth
                    />
                  )}
              </Stack>
            </Paper>
          ) : null}

          {((operationSettings?.order_fields_config?.manufacturing_order_number?.visible &&
            !operationSettings?.order_fields_config?.manufacturing_order_number?.required &&
            !finishLineForm.manufacturing_order_number.trim()) ||
          (operationSettings?.order_fields_config?.customer?.visible &&
            !operationSettings?.order_fields_config?.customer?.required &&
            !finishLineForm.customer_number.trim()) ||
          (operationSettings?.order_fields_config?.customer_order?.visible &&
            !operationSettings?.order_fields_config?.customer_order?.required &&
            !finishLineForm.customer_order_number.trim()) ||
          (operationSettings?.order_fields_config?.actual_quantity?.visible &&
            !operationSettings?.order_fields_config?.actual_quantity?.required &&
            !finishLineForm.actual_quantity_value.trim()) ||
          (operationSettings?.order_fields_config?.speed?.visible &&
            !operationSettings?.order_fields_config?.speed?.required &&
            !finishLineForm.speed_value.trim()) ||
          (operationSettings?.order_fields_config?.observations?.visible &&
            !operationSettings?.order_fields_config?.observations?.required &&
            !finishLineForm.notes.trim())) ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(148,163,184,0.18)',
                bgcolor: 'rgba(15,23,42,0.32)',
                p: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                  Datos adicionales de cierre
                </Typography>

                {operationSettings?.order_fields_config?.manufacturing_order_number?.visible &&
                  !operationSettings?.order_fields_config?.manufacturing_order_number?.required &&
                  !finishLineForm.manufacturing_order_number.trim() && (
                    <TextField
                      size="small"
                      label={getOrderFieldLabel('manufacturing_order_number', 'OF')}
                      value={finishLineForm.manufacturing_order_number}
                      onChange={(event) =>
                        setFinishLineForm((current) => ({
                          ...current,
                          manufacturing_order_number: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                  )}

                {operationSettings?.order_fields_config?.customer?.visible &&
                  !operationSettings?.order_fields_config?.customer?.required &&
                  !finishLineForm.customer_number.trim() && (
                    <TextField
                      size="small"
                      label={getOrderFieldLabel('customer', 'Cliente')}
                      value={finishLineForm.customer_number}
                      onChange={(event) =>
                        setFinishLineForm((current) => ({
                          ...current,
                          customer_number: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                  )}

                {operationSettings?.order_fields_config?.customer_order?.visible &&
                  !operationSettings?.order_fields_config?.customer_order?.required &&
                  !finishLineForm.customer_order_number.trim() && (
                    <TextField
                      size="small"
                      label={getOrderFieldLabel('customer_order', 'Pedido cliente')}
                      value={finishLineForm.customer_order_number}
                      onChange={(event) =>
                        setFinishLineForm((current) => ({
                          ...current,
                          customer_order_number: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                  )}

                {operationSettings?.order_fields_config?.actual_quantity?.visible &&
                  !operationSettings?.order_fields_config?.actual_quantity?.required && (
                    <TextField
                      size="small"
                      type="number"
                      label={getOrderFieldLabel('actual_quantity', 'Cantidad real')}
                      value={finishLineForm.actual_quantity_value}
                      onChange={(event) =>
                        setFinishLineForm((current) => ({
                          ...current,
                          actual_quantity_value: event.target.value,
                        }))
                      }
                      InputProps={{
                        endAdornment: getPendingQuantityAdornmentValue(activeLine) ? (
                          <InputAdornment position="end">
                            <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem', fontWeight: 700 }}>
                              {getPendingQuantityAdornmentValue(activeLine)}
                            </Typography>
                          </InputAdornment>
                        ) : undefined,
                      }}
                      fullWidth
                    />
                  )}

                {operationSettings?.order_fields_config?.speed?.visible &&
                  !operationSettings?.order_fields_config?.speed?.required &&
                  !finishLineForm.speed_value.trim() && (
                    <TextField
                      size="small"
                      type="number"
                      label={getOrderFieldLabel('speed', 'Velocidad')}
                      value={finishLineForm.speed_value}
                      onChange={(event) =>
                        setFinishLineForm((current) => ({
                          ...current,
                          speed_value: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                  )}

                {operationSettings?.order_fields_config?.observations?.visible &&
                  !operationSettings?.order_fields_config?.observations?.required &&
                  !finishLineForm.notes.trim() && (
                    <TextField
                      size="small"
                      label={getOrderFieldLabel('observations', 'Observaciones')}
                      value={finishLineForm.notes}
                      onChange={(event) =>
                        setFinishLineForm((current) => ({
                          ...current,
                          notes: event.target.value,
                        }))
                      }
                      multiline
                      minRows={2}
                      fullWidth
                    />
                  )}
              </Stack>
            </Paper>
          ) : null}

          <Stack spacing={1}>
            <Button
              variant="contained"
              disabled={
                !finishLineEndTime ||
                lineWriteLocked ||
                updateLineMutation.isPending
              }
              onClick={() => handleFinishLineFromDialog(finishLineEndTime)}
            >
              Finalizar con esta hora
            </Button>

            <Button
              variant="outlined"
              disabled={
                !configuredShiftEndTime ||
                lineWriteLocked ||
                updateLineMutation.isPending
              }
              onClick={handleFinishLineAtShiftEnd}
            >
              Finalizar por fin de turno
              {configuredShiftEndTime ? ` (${configuredShiftEndTime})` : ''}
            </Button>
          </Stack>

          <Divider sx={{ borderColor: 'rgba(148,163,184,0.18)' }} />

          <Stack spacing={1.25}>
            <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
              Finalizar e iniciar parada
            </Typography>

            <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
              Usa esta opción cuando la línea queda parada por mantenimiento, limpieza, pruebas u
              otro motivo.
            </Typography>

            <TextField
              select
              size="small"
              label="Motivo de parada"
              value={finishLineStopReasonId}
              onChange={(event) => setFinishLineStopReasonId(event.target.value)}
              fullWidth
            >
              <MenuItem value="">Seleccionar motivo</MenuItem>
              {stopReasons.map((reason: ProductionReportStopReason) => (
                <MenuItem key={reason.id} value={reason.id}>
                  {reason.label}
                </MenuItem>
              ))}
            </TextField>

            <Button
              variant="outlined"
              color="warning"
              disabled={
                !finishLineEndTime ||
                !finishLineStopReasonId ||
                lineWriteLocked ||
                updateLineMutation.isPending ||
                createStopMutation.isPending
              }
              onClick={handleFinishLineAndStartStop}
            >
              Finalizar orden e iniciar parada
            </Button>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={closeFinishLineDialog}
          disabled={
            lineWriteLocked ||
            updateLineMutation.isPending ||
            createStopMutation.isPending
          }
        >
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderTimelineValidationDialog = () => (
    <Dialog
      open={timelineValidationDialogOpen}
      onClose={() => {
        if (idlePeriodWriteLocked || createIdlePeriodMutation.isPending) return;
        setTimelineValidationDialogOpen(false);
      }}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>Completar registro horario</DialogTitle>

      <DialogContent>
        <Stack spacing={2.25} sx={{ pt: 1 }}>
          <Alert severity="warning" variant="outlined">
            {timelineValidationIssue?.message ||
              'No se puede validar el parte hasta que el horario del turno quede correctamente registrado.'}
          </Alert>

          {timelineValidationIssue?.shift_window ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(148,163,184,0.18)',
                bgcolor: 'rgba(2,6,23,0.28)',
                px: 2,
                py: 1.5,
              }}
            >
              <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                Ventana del turno
              </Typography>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                {timelineValidationIssue.shift_window.start_time} -{' '}
                {timelineValidationIssue.shift_window.end_time}
              </Typography>
            </Paper>
          ) : null}

          {(timelineValidationIssue?.incomplete_events ?? []).length > 0 ? (
            <Stack spacing={1.25}>
              <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                Eventos sin cerrar
              </Typography>

              {timelineValidationIssue?.incomplete_events.map((event) => (
                <Paper
                  key={`${event.event_type}-${event.event_id}`}
                  elevation={0}
                  sx={{
                    borderRadius: 2.5,
                    border: '1px solid rgba(248,113,113,0.22)',
                    bgcolor: 'rgba(127,29,29,0.14)',
                    px: 2,
                    py: 1.35,
                  }}
                >
                  <Stack spacing={0.75}>
                    <Stack spacing={0.35}>
                      <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                        {event.label}
                      </Typography>
                      <Typography sx={{ color: '#fecaca', fontSize: '0.9rem' }}>
                        Inicio {event.start_time || '—'} · Fin {event.end_time || '—'}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleTimelineIncompleteEventAction(event)}
                      >
                        Completar cierre
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : null}

          {(timelineValidationIssue?.gaps ?? []).length > 0 ? (
            <Stack spacing={1.25}>
              <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                Huecos sin registrar
              </Typography>

              {timelineValidationIssue?.gaps.map((gap, index) => {
                const isSavingIdlePeriodForThisGap =
                  idlePeriodWriteLocked &&
                  pendingTimelineResolution?.kind === 'gap_create_idle_period' &&
                  pendingTimelineResolution.start_time === gap.start_time &&
                  pendingTimelineResolution.end_time === gap.end_time;

                return (
                  <Paper
                    key={`${gap.start_time}-${gap.end_time}-${index}`}
                    elevation={0}
                    sx={{
                      borderRadius: 2.5,
                      border: '1px solid rgba(245,158,11,0.22)',
                      bgcolor: 'rgba(120,53,15,0.16)',
                      px: 2,
                      py: 1.35,
                      opacity: isSavingIdlePeriodForThisGap ? 0.62 : 1,
                      pointerEvents: isSavingIdlePeriodForThisGap ? 'none' : 'auto',
                      transition: 'opacity 120ms ease',
                    }}
                  >
                  <Stack spacing={0.75}>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                      {gap.start_time} - {gap.end_time}
                    </Typography>

                    <Typography sx={{ color: '#fde68a', fontSize: '0.9rem' }}>
                      {gap.position === 'start_of_shift'
                        ? 'Hueco al inicio del turno.'
                        : gap.position === 'end_of_shift'
                          ? 'Hueco al final del turno.'
                          : 'Hueco entre actividades.'}
                    </Typography>

                    <Typography sx={{ color: '#cbd5e1', fontSize: '0.88rem' }}>
                      {gap.previous_event
                        ? `Anterior: ${gap.previous_event.label} (${gap.previous_event.start_time || '—'} - ${gap.previous_event.end_time || '—'})`
                        : 'Anterior: —'}
                    </Typography>

                    <Typography sx={{ color: '#cbd5e1', fontSize: '0.88rem' }}>
                      {gap.next_event
                        ? `Siguiente: ${gap.next_event.label} (${gap.next_event.start_time || '—'} - ${gap.next_event.end_time || '—'})`
                        : 'Siguiente: —'}
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {gap.allowed_actions.map((action) => (
                        <Button
                          key={action}
                          size="small"
                          variant="outlined"
                          disabled={isSavingIdlePeriodForThisGap}
                          onClick={() => handleTimelineGapAction(gap, action)}
                        >
                          {action === 'create_idle_period' && isSavingIdlePeriodForThisGap
                            ? 'Guardando...'
                            : action === 'create_stop'
                              ? 'Registrar como parada'
                              : action === 'create_idle_period'
                                ? 'Sin orden planificada'
                                : action === 'extend_previous_end'
                                  ? 'Extender actividad anterior'
                                  : action === 'move_next_start'
                                    ? 'Cambiar hora de inicio siguiente'
                                    : 'Editar manualmente'}
                        </Button>
                      ))}
                    </Stack>
                  </Stack>
                  </Paper>
                );
              })}
            </Stack>
          ) : null}

          {(timelineValidationIssue?.overlaps ?? []).length > 0 ? (
            <Stack spacing={1.25}>
              <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                Solapes a revisar
              </Typography>

              {timelineValidationIssue?.overlaps.map((overlap, index) => (
                <Paper
                  key={`${overlap.start_time}-${overlap.end_time}-${index}`}
                  elevation={0}
                  sx={{
                    borderRadius: 2.5,
                    border: '1px solid rgba(244,114,182,0.22)',
                    bgcolor: 'rgba(131,24,67,0.14)',
                    px: 2,
                    py: 1.35,
                  }}
                >
                  <Stack spacing={0.75}>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                      {overlap.start_time} - {overlap.end_time}
                    </Typography>

                    <Typography sx={{ color: '#fbcfe8', fontSize: '0.9rem' }}>
                      {overlap.previous_event
                        ? `Anterior: ${overlap.previous_event.label} (${overlap.previous_event.start_time || '—'} - ${overlap.previous_event.end_time || '—'})`
                        : 'Anterior: —'}
                    </Typography>

                    <Typography sx={{ color: '#fbcfe8', fontSize: '0.9rem' }}>
                      {overlap.next_event
                        ? `Siguiente: ${overlap.next_event.label} (${overlap.next_event.start_time || '—'} - ${overlap.next_event.end_time || '—'})`
                        : 'Siguiente: —'}
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {overlap.allowed_actions.map((action) => (
                        <Button
                          key={action}
                          size="small"
                          variant="outlined"
                          onClick={() => handleTimelineOverlapAction(overlap, action)}
                        >
                          {action === 'adjust_previous_end'
                            ? 'Ajustar fin del anterior'
                            : action === 'adjust_next_start'
                              ? 'Ajustar inicio del siguiente'
                              : 'Editar manualmente'}
                        </Button>
                      ))}
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          disabled={idlePeriodWriteLocked || createIdlePeriodMutation.isPending}
          onClick={() => setTimelineValidationDialogOpen(false)}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderIdlePeriodDialog = () => (
    <Dialog
      open={idlePeriodDialogOpen}
      onClose={() => {
        if (idlePeriodWriteLocked || updateIdlePeriodMutation.isPending) return;
        setIdlePeriodDialogOpen(false);
        setEditingIdlePeriodId(null);
        setIdlePeriodForm(buildEmptyIdlePeriodForm());
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Editar periodo sin orden planificada</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info" variant="outlined">
            Ajusta el tramo marcado como tiempo sin orden planificada. Este evento forma parte de
            la cobertura temporal del turno.
          </Alert>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              size="small"
              type="time"
              label="Hora inicio"
              value={idlePeriodForm.start_time}
              onChange={(event) =>
                setIdlePeriodForm((current) => ({
                  ...current,
                  start_time: event.target.value,
                }))
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              size="small"
              type="time"
              label="Hora fin"
              value={idlePeriodForm.end_time}
              onChange={(event) =>
                setIdlePeriodForm((current) => ({
                  ...current,
                  end_time: event.target.value,
                }))
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>

          {getShiftTimeValidationMessage(
            idlePeriodForm.start_time,
            'La hora de inicio del periodo sin orden planificada',
            configuredShiftStartTime,
            configuredShiftEndTime,
          ) ? (
            <Alert severity="warning" variant="outlined">
              {getShiftTimeValidationMessage(
                idlePeriodForm.start_time,
                'La hora de inicio del periodo sin orden planificada',
                configuredShiftStartTime,
                configuredShiftEndTime,
              )}
            </Alert>
          ) : null}

          {getShiftTimeValidationMessage(
            idlePeriodForm.end_time,
            'La hora de fin del periodo sin orden planificada',
            configuredShiftStartTime,
            configuredShiftEndTime,
          ) ? (
            <Alert severity="warning" variant="outlined">
              {getShiftTimeValidationMessage(
                idlePeriodForm.end_time,
                'La hora de fin del periodo sin orden planificada',
                configuredShiftStartTime,
                configuredShiftEndTime,
              )}
            </Alert>
          ) : null}

          <TextField
            size="small"
            label="Observaciones"
            value={idlePeriodForm.notes}
            onChange={(event) =>
              setIdlePeriodForm((current) => ({
                ...current,
                notes: event.target.value,
              }))
            }
            multiline
            minRows={2}
            fullWidth
          />

          <Chip
            label={
              calculateDurationMinutes(idlePeriodForm.start_time, idlePeriodForm.end_time) === null
                ? 'Duración: —'
                : `Duración: ${calculateDurationMinutes(
                    idlePeriodForm.start_time,
                    idlePeriodForm.end_time,
                  )} min`
            }
            variant="outlined"
            sx={{
              alignSelf: 'flex-start',
              color: '#cbd5e1',
              borderColor: 'rgba(148,163,184,0.24)',
            }}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => {
            setIdlePeriodDialogOpen(false);
            setEditingIdlePeriodId(null);
            setIdlePeriodForm(buildEmptyIdlePeriodForm());
          }}
          disabled={updateIdlePeriodMutation.isPending}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveIdlePeriod}
          disabled={
            !isDraft ||
            !editingIdlePeriodId ||
            idlePeriodWriteLocked ||
            updateIdlePeriodMutation.isPending
          }
        >
          {idlePeriodWriteLocked ? 'Guardando...' : 'Guardar cambios'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderDeleteIdlePeriodDialog = () => (
    <Dialog
      open={Boolean(idlePeriodToDelete)}
      onClose={() => {
        if (!idlePeriodWriteLocked && !deleteIdlePeriodMutation.isPending) {
          setIdlePeriodToDelete(null);
        }
      }}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>Eliminar periodo sin orden planificada</DialogTitle>

      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          <Alert severity="warning" variant="outlined">
            Esta acción eliminará este tramo de la cobertura del turno. Si era necesario para cubrir
            un hueco, el parte puede volver a mostrar una incidencia de cobertura.
          </Alert>

          {idlePeriodToDelete ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(148,163,184,0.18)',
                bgcolor: 'rgba(2,6,23,0.28)',
                px: 2,
                py: 1.5,
              }}
            >
              <Stack spacing={0.35}>
                <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                  Sin orden planificada
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                  {formatTime(idlePeriodToDelete.start_time)} - {formatTime(idlePeriodToDelete.end_time)}
                  {idlePeriodToDelete.notes ? ` · ${idlePeriodToDelete.notes}` : ''}
                </Typography>
              </Stack>
            </Paper>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => setIdlePeriodToDelete(null)}
          disabled={idlePeriodWriteLocked || deleteIdlePeriodMutation.isPending}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={
            !idlePeriodToDelete ||
            idlePeriodWriteLocked ||
            deleteIdlePeriodMutation.isPending
          }
          onClick={() => {
            if (!idlePeriodToDelete || !acquireIdlePeriodWriteLock()) return;
            deleteIdlePeriodMutation.mutate(idlePeriodToDelete.id);
          }}
        >
          Eliminar periodo
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderQualityDialog = () => {
    const line = qualityDialogLine;

    if (!line) {
      return (
        <Dialog
          open={Boolean(qualityDialogLineId)}
          onClose={() => setQualityDialogLineId(null)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Controles de calidad</DialogTitle>
          <DialogContent>
            <Alert severity="warning" variant="outlined">
              No se ha encontrado la orden seleccionada.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setQualityDialogLineId(null)}>Cerrar</Button>
          </DialogActions>
        </Dialog>
      );
    }

    const qualityRows = buildLineQualityRows(line);

    return (
      <Dialog
        open={Boolean(qualityDialogLineId)}
        onClose={() => setQualityDialogLineId(null)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          Controles de calidad · {getLineDisplayName(line)}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
              {formatTime(line.start_time)} - {formatTime(line.end_time)} · OF{' '}
              {line.manufacturing_order_number || '—'} · {line.quantity_value ?? '—'}{' '}
              {line.quantity_unit}
            </Typography>

            {qualityRows.length === 0 ? (
              <Alert severity="info" variant="outlined">
                Esta orden no tiene controles previstos según su horario.
              </Alert>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Box
                  sx={{
                    minWidth: Math.max(760, 180 + qualityControls.length * 260),
                    display: 'grid',
                    gridTemplateColumns: `120px repeat(${qualityControls.length}, minmax(220px, 1fr))`,
                    gap: 0.75,
                    alignItems: 'stretch',
                  }}
                >
                  <Box
                    sx={{
                      px: 1,
                      py: 0.75,
                      color: '#94a3b8',
                      fontSize: '0.78rem',
                      fontWeight: 850,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}
                  >
                    Hora
                  </Box>

                  {qualityControls.map((control) => (
                    <Box
                      key={control.id}
                      sx={{
                        px: 1,
                        py: 0.75,
                        color: '#94a3b8',
                        fontSize: '0.78rem',
                        fontWeight: 850,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      {control.label}
                    </Box>
                  ))}

                  {getApplicableSlotsForLine(line).map((slot: ProductionReportShiftTimeSlot) => {
                    const timeSlot = normalizeTime(slot.time_slot);

                    return (
                      <React.Fragment key={`${line.id}_${timeSlot}`}>
                        <Box
                          sx={{
                            borderRadius: 2,
                            border: '1px solid rgba(148,163,184,0.12)',
                            bgcolor: 'rgba(15,23,42,0.36)',
                            px: 1,
                            py: 1,
                            color: '#f8fafc',
                            fontWeight: 900,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {timeSlot}
                        </Box>

                        {qualityControls.map((control) => {
                          const result =
                            getQualityResultForLine(line.id, control.id, timeSlot) ?? null;
                          const qualityCellPending = isQualityCellPending(
                            line.id,
                            control.id,
                            timeSlot,
                          );

                          return (
                            <Box
                              key={`${line.id}_${timeSlot}_${control.id}`}
                              sx={{
                                borderRadius: 2,
                                border: '1px solid rgba(34,211,238,0.14)',
                                bgcolor: 'rgba(8,47,73,0.22)',
                                px: 1,
                                py: 1,
                              }}
                            >
                              <Stack spacing={0.75}>
                                {result ? (
                                  <Typography sx={{ color: '#94a3b8', fontSize: '0.78rem' }}>
                                    Marcado como {result === 'NA' ? 'Saltado' : result}
                                  </Typography>
                                ) : (
                                  <Typography sx={{ color: '#64748b', fontSize: '0.78rem' }}>
                                    Pendiente
                                  </Typography>
                                )}

                                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                  <Button
                                    size="small"
                                    variant={result === 'C' ? 'contained' : 'outlined'}
                                    color="success"
                                    disabled={!isDraft || qualityCellPending}
                                    onClick={() =>
                                      handleSetQualityResultForLine(
                                        line.id,
                                        control.id,
                                        timeSlot,
                                        'C',
                                      )
                                    }
                                    sx={{ minWidth: 48 }}
                                  >
                                    C
                                  </Button>

                                  <Button
                                    size="small"
                                    variant={result === 'NC' ? 'contained' : 'outlined'}
                                    color="error"
                                    disabled={!isDraft || qualityCellPending}
                                    onClick={() =>
                                      handleSetQualityResultForLine(
                                        line.id,
                                        control.id,
                                        timeSlot,
                                        'NC',
                                      )
                                    }
                                    sx={{ minWidth: 48 }}
                                  >
                                    NC
                                  </Button>

                                  <Button
                                    size="small"
                                    variant={result === 'NA' ? 'contained' : 'outlined'}
                                    disabled={!isDraft || qualityCellPending}
                                    onClick={() =>
                                      handleSetQualityResultForLine(
                                        line.id,
                                        control.id,
                                        timeSlot,
                                        'NA',
                                      )
                                    }
                                    sx={{ minWidth: 72 }}
                                  >
                                    Saltar
                                  </Button>
                                </Stack>
                              </Stack>
                            </Box>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setQualityDialogLineId(null)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderStopDialog = () => (
    <Dialog
      open={stopDialogOpen}
      onClose={() => {
        if (createStopMutation.isPending || updateStopMutation.isPending) return;
        setStopDialogOpen(false);
        setEditingStopId(null);
        setStopForm(buildEmptyStopForm());
        setPendingTimelineResolution(null);
        setPendingIncompleteEventResolution(null);
      }}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        {editingStopId ? 'Editar parada' : 'Registrar parada'}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2.25} sx={{ pt: 1 }}>
          <Alert severity="info" variant="outlined">
            Registra el motivo, la hora y si la parada afecta a una orden concreta o al parte completo.
          </Alert>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 1.5,
            }}
          >
            <TextField
              select
              size="small"
              label="Orden asociada"
              value={stopForm.line_id}
              onChange={(event) =>
                setStopForm((current) => ({
                  ...current,
                  line_id: event.target.value,
                }))
              }
              disabled={!isDraft}
            >
              <MenuItem value="">Parte completo</MenuItem>
              {sortedLines.map((line) => (
                <MenuItem key={line.id} value={line.id}>
                  {getLineDisplayName(line)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Motivo"
              value={stopForm.reason_id}
              onChange={(event) =>
                setStopForm((current) => ({
                  ...current,
                  reason_id: event.target.value,
                }))
              }
              disabled={!isDraft}
            >
              <MenuItem value="">Seleccionar motivo</MenuItem>
              {stopReasons.map((reason: ProductionReportStopReason) => (
                <MenuItem key={reason.id} value={reason.id}>
                  {reason.label}
                </MenuItem>
              ))}
            </TextField>

            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                type="time"
                label="Inicio"
                value={stopForm.start_time}
                onChange={(event) =>
                  setStopForm((current) => ({
                    ...current,
                    start_time: event.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                disabled={!isDraft}
                fullWidth
              />
              <IconButton onClick={setStopStartNow} disabled={!isDraft}>
                <AccessTimeRoundedIcon />
              </IconButton>
            </Stack>

            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                type="time"
                label="Fin"
                value={stopForm.end_time}
                onChange={(event) =>
                  setStopForm((current) => ({
                    ...current,
                    end_time: event.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                disabled={!isDraft}
                fullWidth
              />
              <IconButton onClick={setStopEndNow} disabled={!isDraft}>
                <AccessTimeRoundedIcon />
              </IconButton>
            </Stack>

            <TextField
              size="small"
              label="Duración"
              value={currentStopDuration === null ? '—' : `${currentStopDuration} min`}
              InputProps={{ readOnly: true }}
            />

            <TextField
              size="small"
              label="Observación"
              value={stopForm.notes}
              onChange={(event) =>
                setStopForm((current) => ({
                  ...current,
                  notes: event.target.value,
                }))
              }
              disabled={!isDraft}
            />
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => {
            setStopDialogOpen(false);
            setEditingStopId(null);
            setStopForm(buildEmptyStopForm());
            setPendingTimelineResolution(null);
            setPendingIncompleteEventResolution(null);
          }}
          disabled={stopWriteLocked || createStopMutation.isPending || updateStopMutation.isPending}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveStop}
          disabled={
            !isDraft ||
            stopWriteLocked ||
            createStopMutation.isPending ||
            updateStopMutation.isPending
          }
        >
          {stopWriteLocked
            ? 'Guardando...'
            : editingStopId
              ? 'Guardar cambios'
              : 'Registrar parada'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderDeleteStopDialog = () => (
    <Dialog
      open={Boolean(stopToDelete)}
      onClose={() => {
        if (!deleteStopMutation.isPending) setStopToDelete(null);
      }}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>Eliminar parada</DialogTitle>

      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          <Alert severity="warning" variant="outlined">
            Esta acción eliminará la parada del parte. No debe usarse para corregir una parada que simplemente necesita editarse.
          </Alert>

          {stopToDelete && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(148,163,184,0.18)',
                bgcolor: 'rgba(2,6,23,0.28)',
                px: 2,
                py: 1.5,
              }}
            >
              <Stack spacing={0.35}>
                <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                  {stopToDelete.reason_label || 'Parada'}
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                  {formatTime(stopToDelete.start_time)} - {formatTime(stopToDelete.end_time)} · {getStopLineDisplayName(stopToDelete.line_id)}
                </Typography>
              </Stack>
            </Paper>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => setStopToDelete(null)}
          disabled={deleteStopMutation.isPending}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={!stopToDelete || deleteStopMutation.isPending}
          onClick={() => {
            if (!stopToDelete) return;
            deleteStopMutation.mutate(stopToDelete.id);
          }}
        >
          Eliminar parada
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderListScreen = () => (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: 'rgba(15,23,42,0.72)',
          px: { xs: 2.5, md: 3 },
          py: { xs: 2.5, md: 3 },
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          spacing={2}
        >
          <Stack spacing={0.75}>
            <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '1.35rem' }}>
              Puesto operario
            </Typography>
            <Typography sx={{ color: '#94a3b8' }}>
              Abre, continúa o revisa partes de producción por fecha, turno y línea.
            </Typography>
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Button
              variant={touchModeEnabled ? 'contained' : 'outlined'}
              size="large"
              startIcon={<TouchAppRoundedIcon />}
              onClick={() =>
                setTouchModeEnabled((current) => {
                  const nextValue = !current;

                  window.localStorage.setItem(
                    'proa-industrial-operator-touch-mode',
                    nextValue ? '1' : '0',
                  );

                  return nextValue;
                })
              }
              sx={
                touchModeEnabled
                  ? {
                      bgcolor: '#38bdf8',
                      color: '#020617',
                      fontWeight: 900,
                      '&:hover': {
                        bgcolor: '#7dd3fc',
                      },
                    }
                  : undefined
              }
            >
              Modo táctil
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<RefreshRoundedIcon />}
              onClick={() => void refreshReports()}
              disabled={productionReportsQuery.isFetching}
            >
              Actualizar
            </Button>

            <Button
              variant="contained"
              size="large"
              startIcon={<AddRoundedIcon />}
              onClick={() => openCreateReportDialog()}
            >
              Abrir nuevo parte de trabajo
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: 'rgba(15,23,42,0.66)',
          px: { xs: 2.5, md: 3 },
          py: { xs: 2.5, md: 3 },
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
            spacing={1.5}
          >
            <Stack spacing={0.5}>
              <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                Partes de producción
              </Typography>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.93rem' }}>
                Listado operativo de partes creados para fábrica.
              </Typography>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                <DatePicker
                  label="Fecha"
                  value={listFilters.report_date ? parseISO(listFilters.report_date) : null}
                  onChange={(v) =>
                    setListFilters((current) => ({
                      ...current,
                      report_date: v ? format(v, 'yyyy-MM-dd') : '',
                    }))
                  }
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { minWidth: 170,
                        '& .MuiInputBase-input': { color: '#f8fafc' },
                        '& .MuiInputLabel-root': { color: '#94a3b8' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(148,163,184,0.3)' },
                        '& .MuiSvgIcon-root': { color: '#94a3b8' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(148,163,184,0.6)' },
                      },
                    },
                  }}
                />
              </LocalizationProvider>

              <TextField
                select
                size="small"
                label="Estado"
                value={listFilters.status_filter}
                onChange={(event) =>
                  setListFilters((current) => ({
                    ...current,
                    status_filter: event.target.value as typeof current.status_filter,
                  }))
                }
                sx={{ minWidth: 160 }}
              >
                {STATUS_OPTIONS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label="Línea"
                value={listFilters.production_line_config_id}
                onChange={(event) =>
                  setListFilters((current) => ({
                    ...current,
                    production_line_config_id: event.target.value,
                  }))
                }
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="all">Todas las líneas</MenuItem>
                {productionLinesConfig.map((line) => (
                  <MenuItem key={line.id} value={line.id}>
                    {line.name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Stack>

          {listFilters.report_date && listFilters.report_date !== getTodayIsoDate() ? (
            <Alert
              severity="error"
              variant="filled"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() =>
                    setListFilters((current) => ({
                      ...current,
                      report_date: getTodayIsoDate(),
                    }))
                  }
                >
                  Ir a hoy
                </Button>
              }
              sx={{
                borderRadius: 2.5,
                fontWeight: 850,
                alignItems: 'center',
              }}
            >
              ATENCIÓN: estás consultando los partes del día {listFilters.report_date}.
              Hoy es {getTodayIsoDate()}. Comprueba la fecha antes de crear un parte nuevo.
            </Alert>
          ) : null}

          <Divider sx={{ borderColor: 'rgba(148,163,184,0.18)' }} />

          <Divider sx={{ borderColor: 'rgba(148,163,184,0.18)' }} />

          {productionReportsQuery.isLoading || productionLinesConfigQuery.isLoading ? (
            <Alert severity="info" variant="outlined">
              Cargando partes...
            </Alert>
          ) : productionReportBoardGroups.length === 0 ? (
            <Alert severity="info" variant="outlined">
              No hay líneas disponibles para los filtros seleccionados.
            </Alert>
          ) : (
            <Stack spacing={2}>
              {productionReportBoardGroups.map((group) => (
                <Paper
                  key={group.groupLabel}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(56,189,248,0.24)',
                    bgcolor: 'rgba(2,6,23,0.28)',
                    overflow: 'hidden',
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      px: 1.75,
                      py: 1.15,
                      borderTop: '3px solid rgba(56,189,248,0.88)',
                      borderBottom: '1px solid rgba(148,163,184,0.14)',
                      bgcolor: 'rgba(15,23,42,0.62)',
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography
                        sx={{
                          color: '#38bdf8',
                          fontWeight: 950,
                          fontSize: '0.88rem',
                          letterSpacing: '0.05em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {group.groupLabel}
                      </Typography>
                      <Chip
                        size="small"
                        label={`${group.rows.length} línea(s)`}
                        variant="outlined"
                        sx={{
                          height: 22,
                          color: '#cbd5e1',
                          borderColor: 'rgba(148,163,184,0.24)',
                        }}
                      />
                    </Stack>

                    <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem' }}>
                      Partes del {listFilters.report_date}
                    </Typography>
                  </Stack>

                  <Box sx={{ overflowX: 'auto' }}>
                    <Box
                      sx={{
                        minWidth: 980,
                        display: 'grid',
                        gridTemplateColumns: '210px repeat(3, minmax(230px, 1fr))',
                      }}
                    >
                      <Box
                        sx={{
                          px: 1.5,
                          py: 1,
                          borderBottom: '1px solid rgba(148,163,184,0.14)',
                          borderRight: '1px solid rgba(148,163,184,0.14)',
                          bgcolor: 'rgba(15,23,42,0.40)',
                        }}
                      >
                        <Typography sx={{ color: '#94a3b8', fontSize: '0.78rem', fontWeight: 850 }}>
                          Línea
                        </Typography>
                      </Box>

                      {SHIFT_OPTIONS.map((shift) => (
                        <Box
                          key={`${group.groupLabel}-header-${shift.value}`}
                          sx={{
                            px: 1.5,
                            py: 1,
                            borderBottom: '1px solid rgba(148,163,184,0.14)',
                            borderRight: '1px solid rgba(148,163,184,0.08)',
                            bgcolor: 'rgba(15,23,42,0.40)',
                          }}
                        >
                          <Typography sx={{ color: '#e2e8f0', fontSize: '0.82rem', fontWeight: 900 }}>
                            {shift.label}
                          </Typography>
                        </Box>
                      ))}

                      {group.rows.map((row) => (
                        <React.Fragment key={row.line.id}>
                          <Box
                            sx={{
                              px: 1.75,
                              py: 1.5,
                              borderRight: '1px solid rgba(148,163,184,0.14)',
                              borderBottom: '1px solid rgba(148,163,184,0.10)',
                              bgcolor: 'rgba(15,23,42,0.34)',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Typography
                              sx={{
                                color: '#f8fafc',
                                fontWeight: 950,
                                fontSize: '1.02rem',
                              }}
                            >
                              {row.line.name || row.line.code}
                            </Typography>
                          </Box>

                          {SHIFT_OPTIONS.map((shift) => {
                            const report = row.reportsByShift.get(shift.value);

                            return (
                              <Box
                                key={`${row.line.id}-${shift.value}`}
                                sx={{
                                  px: 1.5,
                                  py: 1.25,
                                  minHeight: 112,
                                  borderRight: '1px solid rgba(148,163,184,0.08)',
                                  borderBottom: '1px solid rgba(148,163,184,0.10)',
                                  bgcolor: !report
                                    ? 'rgba(15,23,42,0.08)'
                                    : report.status === 'validated'
                                      ? 'rgba(34,197,94,0.10)'
                                      : report.status === 'draft'
                                        ? 'rgba(245,158,11,0.10)'
                                        : 'rgba(15,23,42,0.18)',
                                  boxShadow: !report
                                    ? 'none'
                                    : report.status === 'validated'
                                      ? 'inset 0 0 0 1px rgba(34,197,94,0.18)'
                                      : report.status === 'draft'
                                        ? 'inset 0 0 0 1px rgba(245,158,11,0.22)'
                                        : 'none',
                                  transition: 'background-color 0.18s ease, box-shadow 0.18s ease',
                                }}
                              >
                                {report ? (
                                  <Stack spacing={0.85}>
                                    <Stack direction="row" spacing={0.85} alignItems="center" flexWrap="wrap">
                                      <Chip
                                        size="small"
                                        label={getStatusLabel(report.status)}
                                        color={report.status === 'draft' ? 'warning' : 'success'}
                                        variant="outlined"
                                        sx={{
                                          height: 24,
                                          fontWeight: 850,
                                        }}
                                      />
                                      <Typography sx={{ color: '#cbd5e1', fontSize: '0.84rem', fontWeight: 700 }}>
                                        Operario {report.operator_number}
                                      </Typography>
                                    </Stack>

                                    <Stack direction="row" spacing={0.75} flexWrap="wrap">
                                      <Button
                                        size="medium"
                                        variant="outlined"
                                        onClick={() => handleOpenReport(report)}
                                        sx={{ minWidth: 86 }}
                                      >
                                        Abrir
                                      </Button>

                                      {canValidateProductionReports && report.status === 'validated' ? (
                                        <Button
                                          size="medium"
                                          variant="outlined"
                                          color="warning"
                                          startIcon={<EditRoundedIcon />}
                                          onClick={() => handleOpenReport(report)}
                                          sx={{ minWidth: 96 }}
                                        >
                                          Revisar
                                        </Button>
                                      ) : null}
                                    </Stack>
                                  </Stack>
                                ) : (
                                  <Stack spacing={0.85}>
                                    <Chip
                                      size="small"
                                      label="Sin parte"
                                      variant="outlined"
                                      sx={{
                                        alignSelf: 'flex-start',
                                        height: 24,
                                        color: '#94a3b8',
                                        borderColor: 'rgba(100,116,139,0.42)',
                                        fontWeight: 750,
                                      }}
                                    />

                                    <Button
                                      size="medium"
                                      variant="outlined"
                                      startIcon={<AddRoundedIcon />}
                                      onClick={() =>
                                        openCreateReportDialog(
                                          row.line.id,
                                          shift.value,
                                          listFilters.report_date,
                                        )
                                      }
                                      sx={{
                                        alignSelf: 'flex-start',
                                        minWidth: 86,
                                      }}
                                    >
                                      Crear
                                    </Button>
                                  </Stack>
                                )}
                              </Box>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>
    </Stack>
  );

  const renderDetailScreen = () => {
    if (!selectedReport) {
      return (
        <Stack spacing={3}>
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={handleBackToList}
            sx={{ alignSelf: 'flex-start' }}
          >
            Volver al listado
          </Button>

          {reportDetailQuery.isError ? (
            <Stack spacing={1.5}>
              <Alert severity="error" variant="outlined">
                No se pudo recuperar el parte de trabajo.
                Comprueba la conexión o vuelve al listado.
              </Alert>

              <Button
                variant="outlined"
                onClick={() => void reportDetailQuery.refetch()}
                disabled={reportDetailQuery.isFetching}
                sx={{ alignSelf: 'flex-start' }}
              >
                Reintentar
              </Button>
            </Stack>
          ) : (
            <Alert severity="info" variant="outlined">
              Recuperando parte de trabajo...
            </Alert>
          )}
        </Stack>
      );
    }

    return (
      <Stack spacing={3}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(15,23,42,0.72)',
            px: { xs: 2.5, md: 3 },
            py: { xs: 2.5, md: 3 },
          }}
        >
          <Stack spacing={2}>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
            >
              <Button
                startIcon={<ArrowBackRoundedIcon />}
                onClick={handleBackToList}
              >
                Volver al listado
              </Button>

              <Button
                variant={touchModeEnabled ? 'contained' : 'outlined'}
                size="small"
                startIcon={<TouchAppRoundedIcon />}
                onClick={() =>
                  setTouchModeEnabled((current) => {
                    const nextValue = !current;

                    window.localStorage.setItem(
                      'proa-industrial-operator-touch-mode',
                      nextValue ? '1' : '0',
                    );

                    return nextValue;
                  })
                }
                sx={
                  touchModeEnabled
                    ? {
                        bgcolor: '#38bdf8',
                        color: '#020617',
                        fontWeight: 900,
                        '&:hover': {
                          bgcolor: '#7dd3fc',
                        },
                      }
                    : undefined
                }
              >
                Modo táctil
              </Button>
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  lg: 'minmax(0, 1fr) minmax(360px, 460px) minmax(0, 1fr)',
                },
                gap: 2,
                alignItems: 'center',
              }}
            >

              <Stack spacing={0.75}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography sx={{ color: '#f8fafc', fontWeight: 900, fontSize: '1.25rem' }}>
                    Parte de trabajo
                  </Typography>
                  <Chip
                    label={getStatusLabel(selectedReport.status)}
                    color={isDraft ? 'warning' : 'success'}
                    variant="outlined"
                  />
                </Stack>

                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <Typography sx={{ color: '#94a3b8' }}>
                    {selectedReport.report_date} · {getShiftLabel(selectedReport.shift)} · {selectedReport.line_or_center} · Operario {selectedReport.operator_number}
                  </Typography>

                  {isDraft && (
                    <IconButton
                      size="small"
                      aria-label="Corregir número de operario"
                      title="Corregir número de operario"
                      onClick={() => {
                        setOperatorNumberDraft(selectedReport.operator_number);
                        setOperatorNumberDialogOpen(true);
                      }}
                      sx={{
                        color: '#67e8f9',
                        width: 24,
                        height: 24,
                        p: 0,
                        flexShrink: 0,
                      }}
                    >
                      <EditRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Stack>
              </Stack>

              {operatorPlantNoticesQuery.isError ? (
                <Paper
                  elevation={0}
                  sx={{
                    minHeight: 68,
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: 2.5,
                    border: '1px solid rgba(248,113,113,0.38)',
                    bgcolor: 'rgba(127,29,29,0.18)',
                    px: 1.35,
                    py: 0.9,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ width: '100%' }}
                  >
                    <Typography
                      sx={{
                        color: '#fecaca',
                        fontSize: '0.82rem',
                        fontWeight: 850,
                      }}
                    >
                      No se pudieron cargar los avisos.
                    </Typography>

                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => operatorPlantNoticesQuery.refetch()}
                    >
                      Reintentar
                    </Button>
                  </Stack>
                </Paper>
              ) : pendingPlantNotices.length > 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    minHeight: 68,
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: 2.5,
                    border: '1px solid rgba(248,113,113,0.46)',
                    bgcolor: 'rgba(127,29,29,0.22)',
                    px: 1.35,
                    py: 0.9,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1.25}
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ width: '100%' }}
                  >
                    <Stack
                      direction="row"
                      spacing={0.9}
                      alignItems="center"
                      sx={{ minWidth: 0 }}
                    >
                      <NotificationsActiveRoundedIcon
                        sx={{
                          color: '#f87171',
                          fontSize: 22,
                          flexShrink: 0,
                        }}
                      />

                      <Stack spacing={0.05} sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            color: '#fecaca',
                            fontSize: '0.8rem',
                            fontWeight: 900,
                          }}
                        >
                          Avisos de fábrica pendientes
                        </Typography>

                        <Typography
                          sx={{
                            color: '#f8fafc',
                            fontSize: '0.86rem',
                            fontWeight: 850,
                          }}
                        >
                          {pendingPlantNotices.length} aviso
                          {pendingPlantNotices.length === 1 ? '' : 's'} pendiente
                          {pendingPlantNotices.length === 1 ? '' : 's'} de lectura
                        </Typography>
                      </Stack>
                    </Stack>

                    <Button
                      size="small"
                      variant="contained"
                      color="error"
                      onClick={() => setPlantNoticesDialogOpen(true)}
                      sx={{
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Ver avisos
                    </Button>
                  </Stack>
                </Paper>
              ) : activePlantNotices.length > 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    minHeight: 68,
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: 2.5,
                    border: '1px solid rgba(34,197,94,0.28)',
                    bgcolor: 'rgba(20,83,45,0.14)',
                    px: 1.35,
                    py: 0.9,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1.25}
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ width: '100%' }}
                  >
                    <Stack
                      direction="row"
                      spacing={0.9}
                      alignItems="center"
                      sx={{ minWidth: 0 }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{
                          color: '#4ade80',
                          fontSize: 22,
                          flexShrink: 0,
                        }}
                      />

                      <Stack spacing={0.05}>
                        <Typography
                          sx={{
                            color: '#bbf7d0',
                            fontSize: '0.8rem',
                            fontWeight: 900,
                          }}
                        >
                          Avisos de fábrica
                        </Typography>

                        <Typography
                          sx={{
                            color: '#e2e8f0',
                            fontSize: '0.84rem',
                            fontWeight: 800,
                          }}
                        >
                          {activePlantNotices.length} aviso
                          {activePlantNotices.length === 1 ? '' : 's'} activo
                          {activePlantNotices.length === 1 ? '' : 's'} · lectura confirmada
                        </Typography>
                      </Stack>
                    </Stack>

                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      onClick={() => setPlantNoticesDialogOpen(true)}
                      sx={{
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Ver avisos
                    </Button>
                  </Stack>
                </Paper>
              ) : (
                <Box />
              )}

              <Stack
                direction={{ xs: 'column', sm: 'row', lg: 'column' }}
                spacing={1}
                sx={{
                  justifySelf: { lg: 'end' },
                }}
              >
                {canValidateProductionReports && selectedReport.status === 'validated' && (
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<EditRoundedIcon />}
                    onClick={() => setReopenDialogOpen(true)}
                  >
                    Reabrir para corrección
                  </Button>
                )}

                {canValidateProductionReports && (
                  <Button
                    variant="contained"
                    startIcon={<CheckCircleRoundedIcon />}
                    disabled={!isDraft || validateReportMutation.isPending}
                    onClick={() => validateReportMutation.mutate()}
                  >
                    Validar parte
                  </Button>
                )}
              </Stack>
            </Box>

            {!isDraft && (
              <Alert severity="info" variant="outlined">
                Este parte está validado. Para modificarlo, usa “Reabrir para corrección” e indica el motivo.
              </Alert>
            )}

            {isDraft && selectedReport.validated_at && (
              <Alert severity="warning" variant="outlined">
                Este parte fue validado previamente y está reabierto para corrección.
              </Alert>
            )}
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(15,23,42,0.66)',
            px: { xs: 2.5, md: 3 },
            py: { xs: 2.5, md: 3 },
          }}
        >
          <Stack spacing={2.25}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              spacing={1.5}
            >
              <Stack spacing={0.5}>
                <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                  Producción / órdenes
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.93rem' }}>
                  Gestiona la orden activa y el histórico de órdenes fabricadas en este parte.
                </Typography>
              </Stack>

              <Typography sx={{ color: '#94a3b8', fontSize: '0.93rem' }}>
                {sortedLines.length} orden(es) registradas
              </Typography>
            </Stack>

            {activeLine ? (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(34,211,238,0.32)',
                  bgcolor: 'rgba(8,47,73,0.28)',
                  px: 2,
                  py: 2,
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Stack spacing={0.65}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Chip
                        size="small"
                        label={activeStop ? 'Orden activa · línea parada' : 'En producción ahora'}
                        color={activeStop ? 'warning' : 'info'}
                      />
                      <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                        {getLineDisplayName(activeLine)}
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: '#cbd5e1', fontSize: '0.94rem' }}>
                      OF {activeLine.manufacturing_order_number || '—'} · Inicio {formatTime(activeLine.start_time)} · Total OF {activeLine.quantity_value ?? '—'} {activeLine.quantity_unit} · Pendiente {getLinePendingQuantityValue(activeLine) ?? '—'} {activeLine.quantity_unit}
                    </Typography>

                    {(activeLine.customer_order_number || activeLine.lot_number || activeLine.supplier) && (
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.88rem' }}>
                        Pedido {activeLine.customer_order_number || '—'} · Lote {activeLine.lot_number || '—'} · Proveedor {activeLine.supplier || '—'}
                      </Typography>
                    )}
                    <Paper
                      elevation={0}
                      sx={{
                        mt: 1.25,
                        borderRadius: 3,
                        border: '1px solid rgba(148,163,184,0.16)',
                        bgcolor: 'rgba(2,6,23,0.28)',
                        px: 1.5,
                        py: 1.25,
                      }}
                    >
                      {activeStop ? (
                        <Stack spacing={0.5}>
                          <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                            Calidad pausada
                          </Typography>
                          <Typography sx={{ color: '#94a3b8', fontSize: '0.88rem' }}>
                            La línea está parada. Finaliza la parada para continuar con controles de calidad.
                          </Typography>
                        </Stack>
                      ) : nextPendingQualitySlot ? (
                        (() => {
                          const pendingSlot = nextPendingQualitySlot;

                          console.log(
                            '[quality-debug]',
                            {
                              canEditQuality,
                              activeStop: Boolean(activeStop),
                              isDraft,
                              pendingQualityCellKeys: Array.from(pendingQualityCellKeys),
                              controls: pendingSlot.controls.map(({ control }) => ({
                                id: control.id,
                                label: control.label,
                                cellKey: getQualityCellKey(
                                  pendingSlot.lineId,
                                  control.id,
                                  pendingSlot.timeSlot,
                                ),
                                isPending: isQualityCellPending(
                                  pendingSlot.lineId,
                                  control.id,
                                  pendingSlot.timeSlot,
                                ),
                              })),
                            },
                          );

                          return (
                            <Stack spacing={1.25}>
                              <Stack spacing={0.35}>
                                <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                                  Controles pendientes · {pendingSlot.timeSlot}
                                </Typography>

                                <Typography sx={{ color: '#94a3b8', fontSize: '0.84rem' }}>
                                  Horario {pendingSlot.completedInSlot}/{pendingSlot.totalInSlot} resuelto · Total orden {activeLineQualitySummary.completed}/{activeLineQualitySummary.totalRequired} · C {activeLineQualitySummary.conforming} · NC {activeLineQualitySummary.nonConforming} · Saltados {activeLineQualitySummary.skipped}
                                </Typography>
                              </Stack>

                              <Stack spacing={0.75}>
                                {pendingSlot.controls.map(({ control, result }) => (
                                  <Paper
                                    key={control.id}
                                    elevation={0}
                                    sx={{
                                      borderRadius: 2,
                                      border: result
                                        ? '1px solid rgba(34,197,94,0.26)'
                                        : '1px solid rgba(148,163,184,0.14)',
                                      bgcolor: result
                                        ? 'rgba(22,101,52,0.12)'
                                        : 'rgba(15,23,42,0.34)',
                                      px: 1.25,
                                      py: 1,
                                    }}
                                  >
                                    <Stack
                                      direction={{ xs: 'column', sm: 'row' }}
                                      spacing={1}
                                      justifyContent="space-between"
                                      alignItems={{ xs: 'stretch', sm: 'center' }}
                                    >
                                      <Typography sx={{ color: '#cbd5e1', fontSize: '0.9rem', fontWeight: 750 }}>
                                        {control.label}
                                      </Typography>

                                      <Stack direction="row" spacing={0.75}>
                                        <Button
                                          size="small"
                                          variant={result === 'C' ? 'contained' : 'outlined'}
                                          color="success"
                                          disabled={
                                            !canEditQuality ||
                                            isQualityCellPending(
                                              pendingSlot.lineId,
                                              control.id,
                                              pendingSlot.timeSlot,
                                            )
                                          }
                                          onClick={() => handleSaveQualityControlForPendingSlot(control.id, 'C')}
                                          sx={{ minWidth: 68 }}
                                        >
                                          C
                                        </Button>

                                        <Button
                                          size="small"
                                          variant={result === 'NC' ? 'contained' : 'outlined'}
                                          color="error"
                                          disabled={
                                            !canEditQuality ||
                                            isQualityCellPending(
                                              pendingSlot.lineId,
                                              control.id,
                                              pendingSlot.timeSlot,
                                            )
                                          }
                                          onClick={() => handleSaveQualityControlForPendingSlot(control.id, 'NC')}
                                          sx={{ minWidth: 68 }}
                                        >
                                          NC
                                        </Button>

                                        <Button
                                          size="small"
                                          variant={result === 'NA' ? 'contained' : 'outlined'}
                                          disabled={
                                            !canEditQuality ||
                                            isQualityCellPending(
                                              pendingSlot.lineId,
                                              control.id,
                                              pendingSlot.timeSlot,
                                            )
                                          }
                                          onClick={() => handleSaveQualityControlForPendingSlot(control.id, 'NA')}
                                          sx={{ minWidth: 86 }}
                                        >
                                          Saltar
                                        </Button>
                                      </Stack>
                                    </Stack>
                                  </Paper>
                                ))}
                              </Stack>
                            </Stack>
                          );
                        })()
                      ) : (
                        <Stack spacing={0.5}>
                          <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                            Calidad completa para esta orden
                          </Typography>
                          <Typography sx={{ color: '#94a3b8', fontSize: '0.88rem' }}>
                            Total orden {activeLineQualitySummary.completed}/{activeLineQualitySummary.totalRequired} · C {activeLineQualitySummary.conforming} · NC {activeLineQualitySummary.nonConforming} · Saltados {activeLineQualitySummary.skipped}
                          </Typography>
                        </Stack>
                      )}
                    </Paper>
                  </Stack>

                  <Stack
                    spacing={1}
                    alignItems={{ xs: 'stretch', sm: 'flex-end' }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                    >
                      <Button
                        variant="contained"
                        startIcon={<StopRoundedIcon />}
                        disabled={
                          !isDraft ||
                          lineWriteLocked ||
                          updateLineMutation.isPending ||
                          Boolean(activeStop)
                        }
                        onClick={openFinishLineDialog}
                      >
                        Finalizar orden
                      </Button>

                      <IconButton
                        size="small"
                        disabled={!isDraft}
                        onClick={() => openEditLineDialog(activeLine)}
                        title="Editar orden activa"
                      >
                        <EditRoundedIcon fontSize="small" />
                      </IconButton>
                    </Stack>

                    <Stack
                      spacing={1.35}
                      sx={{
                        minWidth: { xs: '100%', sm: 520 },
                        width: { xs: '100%', sm: 520 },
                        maxWidth: '100%',
                      }}
                    >
                      {activeLineEstimation?.enabled ? (
                        <Paper
                          elevation={0}
                          sx={{
                            borderRadius: 3,
                            border: activeLineTimingDeviation?.isOvertime
                              ? '1px solid rgba(248,113,113,0.28)'
                              : '1px solid rgba(59,130,246,0.22)',
                            bgcolor: 'rgba(15,23,42,0.34)',
                            px: 2.25,
                            py: 1.6,
                            width: '100%',
                          }}
                        >
                          <Stack spacing={1.15}>
                            <Typography
                              sx={{
                                color: '#93c5fd',
                                fontSize: '0.82rem',
                                fontWeight: 850,
                              }}
                            >
                              Plan de orden
                            </Typography>

                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                  xs: '1fr',
                                  sm: 'minmax(120px, 0.75fr) minmax(260px, 1.25fr)',
                                },
                                gap: 1.5,
                                alignItems: 'stretch',
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                }}
                              >
                                <Typography
                                  sx={{
                                    color: '#cbd5e1',
                                    fontSize: '0.78rem',
                                    fontWeight: 800,
                                    lineHeight: 1.1,
                                  }}
                                >
                                  Fin previsto
                                </Typography>
                                <Typography
                                  sx={{
                                    color: '#f8fafc',
                                    fontSize: '2rem',
                                    fontWeight: 950,
                                    lineHeight: 1.05,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {operationSettings?.estimation_config?.enable_estimated_end_time
                                    ? activeLineEstimation.estimatedEndTime || '—'
                                    : '—'}
                                </Typography>
                              </Box>

                              <Paper
                                elevation={0}
                                sx={{
                                  borderRadius: 2.25,
                                  border: activeLineTimingDeviation?.isOvertime
                                    ? '1px solid rgba(248,113,113,0.34)'
                                    : '1px solid rgba(34,197,94,0.28)',
                                  bgcolor: activeLineTimingDeviation?.isOvertime
                                    ? 'rgba(127,29,29,0.2)'
                                    : 'rgba(20,83,45,0.18)',
                                  px: 1.35,
                                  py: 1.05,
                                  minWidth: 0,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  textAlign: { xs: 'left', sm: 'right' },
                                }}
                              >
                                <Typography
                                  sx={{
                                    color: activeLineTimingDeviation?.isOvertime
                                      ? '#fecaca'
                                      : '#bbf7d0',
                                    fontSize: '0.78rem',
                                    fontWeight: 900,
                                    lineHeight: 1.1,
                                  }}
                                >
                                  {activeLineTimingDeviation
                                    ? activeLineTimingDeviation.label
                                    : 'Tiempo restante'}
                                </Typography>
                                <Typography
                                  sx={{
                                    color: activeLineTimingDeviation?.isOvertime
                                      ? '#fca5a5'
                                      : '#f8fafc',
                                    fontSize: '2rem',
                                    fontWeight: 950,
                                    lineHeight: 1.05,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {activeLineTimingDeviation
                                    ? activeLineTimingDeviation.valueLabel
                                    : '—'}
                                </Typography>
                              </Paper>
                            </Box>

                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={{ xs: 0.45, sm: 1.35 }}
                              justifyContent="space-between"
                              sx={{
                                borderRadius: 2,
                                border: '1px solid rgba(148,163,184,0.12)',
                                bgcolor: 'rgba(2,6,23,0.18)',
                                px: 1.25,
                                py: 0.85,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: '#94a3b8',
                                  fontSize: '0.82rem',
                                  fontWeight: 800,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Tiempo en orden{' '}
                                <Box component="span" sx={{ color: '#f8fafc', fontWeight: 950 }}>
                                  {formatElapsedMinutes(activeLineElapsedMinutes)}
                                </Box>
                              </Typography>

                              <Typography
                                sx={{
                                  color: '#94a3b8',
                                  fontSize: '0.82rem',
                                  fontWeight: 800,
                                  textAlign: { xs: 'left', sm: 'right' },
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Duración teórica{' '}
                                <Box component="span" sx={{ color: '#f8fafc', fontWeight: 950 }}>
                                  {formatEstimatedMinutes(activeLineEstimation.estimatedProductionDurationMinutes)}
                                </Box>
                              </Typography>
                            </Stack>

                            {activeLineEstimation.missingInputs.length > 0 ? (
                              <Typography
                                sx={{
                                  color: '#fbbf24',
                                  fontSize: '0.72rem',
                                  lineHeight: 1.2,
                                }}
                              >
                                Faltan: {activeLineEstimation.missingInputs.join(', ')}
                              </Typography>
                            ) : null}
                          </Stack>
                        </Paper>
                      ) : (
                        <Paper
                          elevation={0}
                          sx={{
                            borderRadius: 3,
                            border: '1px solid rgba(34,211,238,0.24)',
                            bgcolor: 'rgba(8,47,73,0.22)',
                            px: 2,
                            py: 1.25,
                          }}
                        >
                          <Typography sx={{ color: '#94a3b8', fontSize: '0.78rem', fontWeight: 700 }}>
                            Tiempo en orden
                          </Typography>
                          <Typography sx={{ color: '#f8fafc', fontSize: '1.55rem', fontWeight: 950, lineHeight: 1.15 }}>
                            {formatElapsedMinutes(activeLineElapsedMinutes)}
                          </Typography>
                          <Typography sx={{ color: '#64748b', fontSize: '0.78rem' }}>
                            desde {formatTime(activeLine.start_time)}
                          </Typography>
                        </Paper>
                      )}
                    </Stack>
                  </Stack>
                </Stack>
              </Paper>
            ) : (
              <Stack spacing={1.5}>
                {canContinuePendingLine && bannerContinuationCandidate ? (
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: '1px solid rgba(245,158,11,0.28)',
                      bgcolor: 'rgba(120,53,15,0.18)',
                      px: 2,
                      py: 2,
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      justifyContent="space-between"
                      spacing={1.5}
                    >
                      <Stack spacing={0.45}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Chip size="small" color="warning" label="Orden pendiente de continuación" />
                          <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                            {bannerContinuationCandidate.product_label ||
                              bannerContinuationCandidate.customer_order_number ||
                              bannerContinuationCandidate.manufacturing_order_number ||
                              'Orden pendiente'}
                          </Typography>
                        </Stack>

                        <Typography sx={{ color: '#fbbf24', fontSize: '0.9rem', fontWeight: 700 }}>
                          OF {bannerContinuationCandidate.manufacturing_order_number || '—'}
                        </Typography>

                        <Typography sx={{ color: '#cbd5e1', fontSize: '0.9rem' }}>
                          Cantidad objetivo OF {bannerContinuationCandidate.quantity_value ?? '—'}{' '}
                          {bannerContinuationCandidate.quantity_unit}
                          {' · '}
                          Fabricado acumulado {bannerContinuationCandidate.actual_quantity_accumulated ?? '—'}{' '}
                          {bannerContinuationCandidate.quantity_unit}
                          {' · '}
                          Pendiente {bannerContinuationCandidate.remaining_quantity_value ?? '—'}{' '}
                          {bannerContinuationCandidate.quantity_unit}
                        </Typography>

                        <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                          Último cierre {formatTime(bannerContinuationCandidate.source_end_time)} · Esta orden se
                          cerró por fin de turno y puede retomarse ahora.
                        </Typography>
                      </Stack>

                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                      >
                        <Button
                          variant="contained"
                          color="warning"
                          onClick={() => continuePendingLineMutation.mutate()}
                          disabled={continuePendingLineMutation.isPending}
                        >
                          Continuar orden
                        </Button>

                        <Button
                          variant="outlined"
                          startIcon={<PlayArrowRoundedIcon />}
                          onClick={() =>
                            openCreateLineDialog(
                              lastClosedLine?.end_time
                                ? normalizeTime(lastClosedLine.end_time)
                                : null,
                            )
                          }
                          disabled={!isDraft || Boolean(activeStop) || continuePendingLineMutation.isPending}
                        >
                          Iniciar orden nueva
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                ) : null}

                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px dashed rgba(148,163,184,0.26)',
                    bgcolor: 'rgba(2,6,23,0.22)',
                    px: 2,
                    py: 2,
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    spacing={1.5}
                  >
                    <Stack spacing={0.35}>
                      <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                        {activeStop ? 'Línea parada' : 'No hay orden activa'}
                      </Typography>
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                        {activeStop
                          ? 'Finaliza la parada abierta antes de iniciar una nueva orden.'
                          : canContinuePendingLine
                            ? 'Puedes continuar la orden pendiente o iniciar una orden nueva.'
                            : 'Inicia una orden cuando comience la fabricación.'}
                      </Typography>
                    </Stack>

                    {!canContinuePendingLine && (
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<PlayArrowRoundedIcon />}
                        onClick={() =>
                          openCreateLineDialog(
                            lastClosedLine?.end_time ? normalizeTime(lastClosedLine.end_time) : null,
                          )
                        }
                        disabled={!isDraft || Boolean(activeStop) || continuePendingLineMutation.isPending}
                        sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}
                      >
                        Iniciar orden
                      </Button>
                    )}
                  </Stack>
                </Paper>
              </Stack>
            )}
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(56,189,248,0.18)',
            bgcolor: 'rgba(15,23,42,0.58)',
            px: { xs: 2.5, md: 3 },
            py: { xs: 2.25, md: 2.5 },
          }}
        >
          <Stack spacing={1.75}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              spacing={1}
            >
              <Stack spacing={0.35}>
                <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                  Cobertura del turno
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                  Reconstrucción temporal del parte: órdenes, paradas y periodos sin orden planificada.
                </Typography>
              </Stack>

              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  label={`${sortedLines.length} orden${sortedLines.length === 1 ? '' : 'es'}`}
                  variant="outlined"
                  sx={{
                    color: '#bbf7d0',
                    borderColor: 'rgba(34,197,94,0.24)',
                    bgcolor: 'rgba(20,83,45,0.12)',
                    fontWeight: 800,
                  }}
                />
                <Chip
                  size="small"
                  label={`${sortedStops.length} parada${sortedStops.length === 1 ? '' : 's'}`}
                  variant="outlined"
                  sx={{
                    color: '#fecaca',
                    borderColor: 'rgba(248,113,113,0.24)',
                    bgcolor: 'rgba(127,29,29,0.12)',
                    fontWeight: 800,
                  }}
                />
                <Chip
                  size="small"
                  label={`${sortedIdlePeriods.length} sin orden`}
                  variant="outlined"
                  sx={{
                    color: '#cbd5e1',
                    borderColor: 'rgba(148,163,184,0.24)',
                    bgcolor: 'rgba(30,41,59,0.20)',
                    fontWeight: 800,
                  }}
                />
              </Stack>
            </Stack>

            {shiftCoverageEvents.length === 0 ? (
              <Alert severity="info" variant="outlined">
                Todavía no hay eventos registrados para reconstruir el turno.
              </Alert>
            ) : (
              <Stack spacing={0.8}>
                {shiftCoverageEvents.map((event) => {
                  const isLine = event.kind === 'line';
                  const isStop = event.kind === 'stop';
                  const isIdle = event.kind === 'idle';

                  return (
                    <Paper
                      key={`${event.kind}-${event.id}`}
                      elevation={0}
                      sx={{
                        borderRadius: 2.5,
                        border: isLine
                          ? '1px solid rgba(34,197,94,0.22)'
                          : isStop
                            ? '1px solid rgba(248,113,113,0.24)'
                            : '1px dashed rgba(148,163,184,0.30)',
                        bgcolor: isLine
                          ? 'rgba(20,83,45,0.12)'
                          : isStop
                            ? 'rgba(127,29,29,0.12)'
                            : 'rgba(30,41,59,0.18)',
                        px: 1.5,
                        py: 1,
                      }}
                    >
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        spacing={1}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                      >
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <Box
                            sx={{
                              width: 9,
                              height: 9,
                              borderRadius: '50%',
                              bgcolor: isLine
                                ? '#4ade80'
                                : isStop
                                  ? '#f87171'
                                  : '#94a3b8',
                              mt: 0.65,
                              flexShrink: 0,
                            }}
                          />

                          <Stack spacing={0.18}>
                            <Typography
                              sx={{
                                color: isIdle ? '#cbd5e1' : '#f8fafc',
                                fontWeight: 900,
                                fontSize: '0.9rem',
                              }}
                            >
                              {event.title}
                            </Typography>
                            <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem' }}>
                              {event.start_time || '—'} – {event.end_time || 'Abierto'} · {event.helper}
                            </Typography>
                          </Stack>
                        </Stack>

                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                          <Chip
                            size="small"
                            label={
                              event.duration_minutes === null || event.duration_minutes === undefined
                                ? 'En producción'
                                : `${event.duration_minutes} min`
                            }
                            sx={{
                              bgcolor: isLine
                                ? 'rgba(34,197,94,0.14)'
                                : isStop
                                  ? 'rgba(248,113,113,0.14)'
                                  : 'rgba(148,163,184,0.12)',
                              color: isLine
                                ? '#86efac'
                                : isStop
                                  ? '#fecaca'
                                  : '#cbd5e1',
                              border: isLine
                                ? '1px solid rgba(34,197,94,0.22)'
                                : isStop
                                  ? '1px solid rgba(248,113,113,0.22)'
                                  : '1px solid rgba(148,163,184,0.20)',
                              fontWeight: 850,
                            }}
                          />

                          {event.kind === 'line' ? (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={buildLineQualityRows(event.entity).length === 0}
                              onClick={() => setQualityDialogLineId(event.entity.id)}
                              sx={{
                                minWidth: 92,
                                px: 1.25,
                                color: '#67e8f9',
                                borderColor: 'rgba(34,211,238,0.35)',
                              }}
                            >
                              Calidad
                            </Button>
                          ) : null}

                          <IconButton
                            size="small"
                            disabled={!isDraft}
                            onClick={() => {
                              if (event.kind === 'line') {
                                openEditLineDialog(event.entity);
                                return;
                              }

                              if (event.kind === 'stop') {
                                openEditStopDialog(event.entity);
                                return;
                              }

                              openEditIdlePeriodDialog(event.entity);
                            }}
                            title={
                              event.kind === 'line'
                                ? 'Editar orden'
                                : event.kind === 'stop'
                                  ? 'Editar parada'
                                  : 'Editar periodo sin orden planificada'
                            }
                            sx={{
                              width: 40,
                              height: 40,
                              color: '#cbd5e1',
                              border: '1px solid rgba(148,163,184,0.18)',
                            }}
                          >
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>

                          <IconButton
                            size="small"
                            disabled={
                              !isDraft ||
                              deleteLineMutation.isPending ||
                              deleteStopMutation.isPending ||
                              deleteIdlePeriodMutation.isPending
                            }
                            onClick={() => {
                              if (event.kind === 'line') {
                                setLineToDelete(event.entity);
                                return;
                              }

                              if (event.kind === 'stop') {
                                setStopToDelete(event.entity);
                                return;
                              }

                              setIdlePeriodToDelete(event.entity);
                            }}
                            title={
                              event.kind === 'line'
                                ? 'Eliminar orden'
                                : event.kind === 'stop'
                                  ? 'Eliminar parada'
                                  : 'Eliminar periodo sin orden planificada'
                            }
                            sx={{
                              width: 40,
                              height: 40,
                              color: '#cbd5e1',
                              border: '1px solid rgba(148,163,184,0.18)',
                            }}
                          >
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(15,23,42,0.66)',
            px: { xs: 2.5, md: 3 },
            py: { xs: 2.5, md: 3 },
          }}
        >
          <Stack spacing={2.25}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              spacing={1.5}
            >
              <Stack spacing={0.5}>
                <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                  Paradas / averías
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.93rem' }}>
                  Registra paradas reales de la línea, asociadas a la orden activa cuando corresponda.
                </Typography>
              </Stack>

              <Typography sx={{ color: '#94a3b8', fontSize: '0.93rem' }}>
                {closedStops.length} parada(s) finalizadas
              </Typography>
            </Stack>

            {activeStop ? (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(251,191,36,0.34)',
                  bgcolor: 'rgba(120,53,15,0.18)',
                  px: 1.5,
                  py: 1.25,
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'stretch', md: 'center' }}
                  spacing={2}
                >
                  <Stack spacing={0.65}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Chip size="small" label="Parada en curso" color="warning" />
                      <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                        {activeStop.reason_label || 'Parada'}
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: '#cbd5e1', fontSize: '0.94rem' }}>
                      Inicio {formatTime(activeStop.start_time)} · {getStopLineDisplayName(activeStop.line_id)}
                    </Typography>

                    {activeStop.notes && (
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.88rem' }}>
                        {activeStop.notes}
                      </Typography>
                    )}
                  </Stack>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                  >
                    <Button
                      variant="contained"
                      startIcon={<StopRoundedIcon />}
                      disabled={!isDraft || stopWriteLocked || updateStopMutation.isPending}
                      onClick={handleFinishActiveStop}
                    >
                      Finalizar parada
                    </Button>

                    <Button
                      variant="outlined"
                      disabled={!isDraft}
                      onClick={() => openEditStopDialog(activeStop)}
                    >
                      Editar datos
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px dashed rgba(148,163,184,0.22)',
                  bgcolor: 'rgba(2,6,23,0.18)',
                  px: 1.5,
                  py: 1.25,
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  spacing={1.5}
                >
                  <Stack spacing={0.35}>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                      No hay parada abierta
                    </Typography>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                      Puedes iniciar una parada ahora o añadir una parada manual ya ocurrida.
                    </Typography>
                  </Stack>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button
                      variant="contained"
                      startIcon={<PlayArrowRoundedIcon />}
                      onClick={openStartStopDialog}
                      disabled={!isDraft}
                    >
                      Iniciar parada
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<AddRoundedIcon />}
                      onClick={openManualStopDialog}
                      disabled={!isDraft}
                    >
                      Añadir parada manual
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            )}
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(15,23,42,0.66)',
            px: { xs: 2.5, md: 3 },
            py: { xs: 2.5, md: 3 },
          }}
        >
          <Stack spacing={1.5}>
            <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
              Auditoría del parte
            </Typography>

            {(detail?.audit_events ?? []).length === 0 ? (
              <Typography sx={{ color: '#94a3b8' }}>
                Todavía no hay eventos de auditoría registrados para este parte.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {detail?.audit_events.map((event) => (
                  <Paper
                    key={event.id}
                    elevation={0}
                    sx={{
                      borderRadius: 2.5,
                      border: '1px solid rgba(148,163,184,0.14)',
                      bgcolor: 'rgba(2,6,23,0.28)',
                      px: 2,
                      py: 1.25,
                    }}
                  >
                    <Stack spacing={0.25}>
                      <Typography sx={{ color: '#e2e8f0', fontWeight: 800 }}>
                        {event.event_label}
                      </Typography>
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.88rem' }}>
                        {new Date(event.created_at).toLocaleString()}
                        {event.notes ? ` · ${event.notes}` : ''}
                      </Typography>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Stack>
        </Paper>
      </Stack>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      {screenMode === 'list' ? renderListScreen() : renderDetailScreen()}
      {renderCreateReportDialog()}
      {renderPlantNoticesDialog()}
      {renderLineDialog()}
      {renderMaterialUsagesDialog()}
      {renderDeleteLineDialog()}
      {renderFinishLineDialog()}
      {renderTimelineValidationDialog()}
      {renderIdlePeriodDialog()}
      {renderDeleteIdlePeriodDialog()}
      {renderQualityDialog()}
      {renderStopDialog()}
      {renderDeleteStopDialog()}
      {renderOperatorNumberDialog()}
      {renderReopenDialog()}

      <ProductionOperatorNumericKeyboard
        open={newReportOperatorKeyboardOpen}
        title="Nº operario"
        value={newReportForm.operator_number}
        onClose={() => setNewReportOperatorKeyboardOpen(false)}
        onConfirm={(value) => {
          setNewReportForm((current) => ({
            ...current,
            operator_number: value,
          }));
          setNewReportOperatorKeyboardOpen(false);
        }}
      />
    </Box>
  );
}

